import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button, TextField, Grid, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, Paper, TableFooter, TablePagination, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Typography, Modal, Chip, Tooltip, IconButton, Menu, MenuItem, FormControl, InputLabel, Select, CircularProgress, Autocomplete } from '@mui/material';
import { 
  HourglassEmpty as PendingIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  LocalHospital as ReceivedIcon,
  Science as AnalysisIcon,
  History as HistoryIcon,
  Visibility as ViewIcon,
  Cancel as CancelIcon,
  MoreVert as MoreVertIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PersonOff as PacienteAusenteIcon,
  Comment as CommentIcon,
  CommentOutlined as CommentOutlinedIcon,
  Error as MuestraNoConformeIcon,
  Store as RecepcionVentanillaIcon,
  Store,
  MeetingRoom as CubiculoIcon,
  CameraAlt as CameraAltIcon
} from '@mui/icons-material';
import BarcodeScannerComponent from 'react-qr-barcode-scanner';
import { useUser } from '../contexts/UserContext';
import { useSucursal } from '../contexts/SucursalContext';
import Swal from 'sweetalert2';
import { useAccesos } from '../contexts/AccesosContext';

// Componente para el menú desplegable de acciones
const AccionesDropdown = ({ 
  estadoActual, 
  estadosDisponibles, 
  onCambiarEstado, 
  getSiguienteEstado, 
  getEstadoIcon, 
  getEstadoButtonColor,
  isLoading = false,
  puedeReactivarMuestraNoConforme = true, // Nuevo prop para controlar si puede reactivar desde muestra no conforme
  enModalDetalle = false // Nuevo prop para identificar si está en modal de detalle
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEstadoClick = (estadoId) => {
    onCambiarEstado(estadoId);
    handleClose();
  };

  const estadoActualObj = estadosDisponibles.find(e => e.id === estadoActual);
  
  // Obtener opciones disponibles según el estado actual
  const getOpcionesDisponibles = () => {
    if (!estadosDisponibles || estadosDisponibles.length === 0) return [];
    
    const estadoActualObj = estadosDisponibles.find(e => e.id === estadoActual);
    if (!estadoActualObj) return [];
    
    console.log('🔍 DEBUG getOpcionesDisponibles:', {
      estadoActual,
      estadoActualObj,
      estado: estadoActualObj.estado,
      orden: estadoActualObj.orden
    });
    
    const ordenActual = estadoActualObj.orden;
    
    // Obtener el siguiente estado en la secuencia ordenada
    const siguienteEstado = estadosDisponibles
      .filter(e => e.orden === ordenActual + 1)
      .sort((a, b) => a.orden - b.orden)[0];
    
    const opciones = [];
    
    // Agregar el siguiente estado secuencial como opción primaria (si existe)
    // EXCEPCIÓN: No agregar siguiente estado desde Muestra No conforme, Anulada o Paciente Ausente
    // Recepción ventanilla SÍ permite siguiente estado (traslado)
    const esEstadoEspecial = estadoActualObj.estado === 'Muestra No conforme' || 
                           estadoActualObj.estado === 'Muestra No Conforme' ||
                           estadoActualObj.estado === 'Anulada' || 
                           estadoActualObj.estado === 'Paciente Ausente' ||
                           estadoActualObj.estado === 'Muestra Repetida' ||
                           estadoActualObj.estado === 'Análisis Reprocesado' ||
                           estadoActualObj.estado === 'Analisis Reprocesado';
                           
    console.log('🔍 DEBUG estados especiales:', {
      esEstadoEspecial,
      estadoActual: estadoActualObj.estado,
      siguienteEstado: siguienteEstado?.estado
    });
                           
    if (siguienteEstado && !esEstadoEspecial) {
      opciones.push({
        id: siguienteEstado.id,
        estado: siguienteEstado.estado,
        isPrimary: true
      });
    }
    
    // Agregar opciones especiales según el contexto
    const estadoAnulada = estadosDisponibles.find(e => e.estado === 'Anulada');
    const estadoPacienteAusente = estadosDisponibles.find(e => e.estado === 'Paciente Ausente');
    const estadoRecepcionVentanilla = estadosDisponibles.find(e => e.estado === 'Recepción ventanilla');
    const estadoMuestraNoConforme = estadosDisponibles.find(e => 
      e.estado === 'Muestra No Conforme' || 
      e.estado === 'Muestra No conforme' ||
      e.estado === 'MUESTRA NO CONFORME' ||
      e.estado === 'muestra no conforme' ||
      e.estado.toLowerCase().includes('muestra') && e.estado.toLowerCase().includes('conforme')
    );
    const estadoPendiente = estadosDisponibles.find(e => e.estado === 'Pendiente');
    const { tienePermisos } = useAccesos();
    // En modal de detalle, agregar opciones de retroceso al estado anterior
    if (enModalDetalle) {
      // Encontrar el estado anterior basado en el orden
      const estadoAnterior = estadosDisponibles
        .filter(e => e.orden === ordenActual - 1)
        .sort((a, b) => a.orden - b.orden)[0];
      
      // Si hay estado anterior y no es el mismo actual, agregarlo como opción
      if (estadoAnterior && estadoAnterior.id !== estadoActual && estadoAnterior.estado !== 'Pendiente') {
        opciones.push({
          id: estadoAnterior.id,
          estado: `Volver a ${estadoAnterior.estado}`,
          isPrimary: false
        });
      }
      
      // Siempre permitir volver a Pendiente desde modal de detalle (excepto si ya está en Pendiente)
      if (estadoPendiente && estadoActualObj.estado !== 'Pendiente') {
        opciones.push({
          id: estadoPendiente.id,
          estado: 'Volver a Pendiente',
          isPrimary: false
        });
      }
    }
    
    console.log('🔍 DEBUG estados especiales encontrados:', {
      estadoAnulada: estadoAnulada?.estado,
      estadoPacienteAusente: estadoPacienteAusente?.estado,
      estadoRecepcionVentanilla: estadoRecepcionVentanilla?.estado,
      estadoMuestraNoConforme: estadoMuestraNoConforme?.estado,
      estadoPendiente: estadoPendiente?.estado,
      todosLosEstados: estadosDisponibles.map(e => e.estado)
    });
    
    // Solo agregar "Anular" si no estamos en un estado final y existe el estado
    if (estadoAnulada && estadoActualObj.estado !== 'Anulada' && estadoActualObj.estado !== 'Procesada') {
      opciones.push({
        id: estadoAnulada.id,
        estado: estadoAnulada.estado,
        isPrimary: false
      });
    }
    
    // Solo agregar "Paciente Ausente" desde Pendiente
    if (estadoActualObj.estado === 'Pendiente' && estadoPacienteAusente) {
      opciones.push({
        id: estadoPacienteAusente.id,
        estado: estadoPacienteAusente.estado,
        isPrimary: false
      });
    }
    
    // Solo agregar "Recepción ventanilla" desde Pendiente
    if (estadoActualObj.estado === 'Pendiente' && estadoRecepcionVentanilla) {
      opciones.push({
        id: estadoRecepcionVentanilla.id,
        estado: estadoRecepcionVentanilla.estado,
        isPrimary: false
      });
    }
    
    // Agregar "Muestra No Conforme" desde TODOS los estados (excepto si ya está en ese estado)
    const esMuestraNoConforme = estadoActualObj.estado === 'Muestra No Conforme' || 
                               estadoActualObj.estado === 'Muestra No conforme' ||
                               estadoActualObj.estado === 'MUESTRA NO CONFORME' ||
                               estadoActualObj.estado === 'muestra no conforme' ||
                               (estadoActualObj.estado.toLowerCase().includes('muestra') && 
                                estadoActualObj.estado.toLowerCase().includes('conforme'));
    
    console.log('🔍 DEBUG evaluando Muestra No Conforme:', {
      estadoMuestraNoConforme: estadoMuestraNoConforme?.estado,
      estadoMuestraNoConformeId: estadoMuestraNoConforme?.id,
      estadoActual: estadoActualObj.estado,
      esMuestraNoConforme: esMuestraNoConforme,
      debeAgregar: estadoMuestraNoConforme && !esMuestraNoConforme
    });
    
    if (estadoMuestraNoConforme && !esMuestraNoConforme) {
      console.log('🔍 DEBUG agregando Muestra No Conforme a opciones');
      opciones.push({
        id: estadoMuestraNoConforme.id,
        estado: estadoMuestraNoConforme.estado,
        isPrimary: false
      });
    }
    
    // Agregar "Muestra Repetida" solo desde Verificada (orden >= 5) en adelante
    const estadoMuestraRepetida = estadosDisponibles.find(e => e.estado === 'Muestra Repetida');
    const esMuestraRepetida = estadoActualObj.estado === 'Muestra Repetida';
    const estadoVerificada = estadosDisponibles.find(e => e.estado === 'Verificada');
    const ordenVerificada = estadoVerificada ? estadoVerificada.orden : 5;
    if (estadoMuestraRepetida && !esMuestraRepetida && 
        ordenActual >= ordenVerificada &&
        !['Anulada', 'Procesada', 'Muestra No conforme', 'Muestra No Conforme', 'Análisis Reprocesado', 'Analisis Reprocesado'].includes(estadoActualObj.estado)) {
      opciones.push({
        id: estadoMuestraRepetida.id,
        estado: estadoMuestraRepetida.estado,
        isPrimary: false
      });
    }
    
    // Desde "Muestra Repetida", permitir volver a Pendiente (reiniciar proceso)
    if (esMuestraRepetida && estadoPendiente) {
      opciones.push({
        id: estadoPendiente.id,
        estado: 'Reiniciar Proceso',
        isPrimary: true
      });
    }
    
    // Desde "Análisis Reprocesado", permitir volver a reprocesar (auto-transición)
    const estadoAnalisisReprocesado = estadosDisponibles.find(e => 
      e.estado === 'Análisis Reprocesado' || e.estado === 'Analisis Reprocesado'
    );
    const esAnalisisReprocesado = estadoActualObj.estado === 'Análisis Reprocesado' || 
                                  estadoActualObj.estado === 'Analisis Reprocesado';
    if (esAnalisisReprocesado && estadoAnalisisReprocesado) {
      opciones.push({
        id: estadoAnalisisReprocesado.id,
        estado: 'Reprocesar nuevamente',
        isPrimary: true
      });
    }
    
    // Solo agregar "Reactivar" (volver a Pendiente) desde Anulada o Muestra No Conforme
    // Para "Muestra No Conforme", verificar que tenga motivo y acciones correctivas
    if ((estadoActualObj.estado === 'Anulada' || 
         estadoActualObj.estado === 'Muestra No Conforme' || 
         estadoActualObj.estado === 'Muestra No conforme') && estadoPendiente) {
      
      let puedeReactivar = true;
      
      // Para "Muestra No Conforme", usar el prop pasado desde el componente padre
      if (estadoActualObj.estado === 'Muestra No Conforme' || estadoActualObj.estado === 'Muestra No conforme') {
        puedeReactivar = puedeReactivarMuestraNoConforme;
        console.log('🔍 DEBUG Reactivar desde Muestra No Conforme:', {
          puedeReactivar,
          propValue: puedeReactivarMuestraNoConforme
        });
      }
      
      if (puedeReactivar) {
        console.log('🔍 DEBUG Reactivar:', {
          debeAgregarReactivar: true,
          estadoPendiente: estadoPendiente?.estado
        });
        opciones.push({
          id: estadoPendiente.id,
          estado: 'Reactivar',
          isPrimary: true
        });
      } else {
        console.log('🔍 DEBUG Reactivar bloqueado - falta motivo o acciones correctivas');
      }
    }
    
    // Desde Paciente Ausente, permitir volver a Pendiente para retomar la muestra
    if (estadoActualObj.estado === 'Paciente Ausente' && estadoPendiente) {
      opciones.push({
        id: estadoPendiente.id,
        estado: 'Retomar Muestra',
        isPrimary: true
      });
    }
    
    // Desde Recepción ventanilla, permitir continuar al siguiente estado y retomar muestra
    if (estadoActualObj.estado === 'Recepción ventanilla') {
      // Buscar el siguiente estado en la secuencia (orden + 1)
      const siguienteEstado = estadosDisponibles.find(e => e.orden === estadoActualObj.orden + 1);

      if (siguienteEstado) {
        opciones.push({
          id: siguienteEstado.id,
          estado: siguienteEstado.estado,
          isPrimary: true
        });
      }
      
      // Agregar opción de retomar muestra como secundaria
      if (estadoPendiente) {
        opciones.push({
          id: estadoPendiente.id,
          estado: 'Retomar Muestra',
          isPrimary: false
        });
      }
    }
    
    console.log('🔍 DEBUG opciones finales:', opciones);
    return opciones;
  };

  const opcionesDisponibles = getOpcionesDisponibles();
  const opcionPrimaria = opcionesDisponibles.find(op => op.isPrimary);
  const opcionesSecundarias = opcionesDisponibles.filter(op => !op.isPrimary);

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {/* Botón principal */}
      {opcionPrimaria ? (
        <Button
          size="small"
          variant="contained"
          color={getEstadoButtonColor(opcionPrimaria.id)}
          onClick={() => onCambiarEstado(opcionPrimaria.id)}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : getEstadoIcon(opcionPrimaria.id)}
          disabled={isLoading}
          sx={{
            minWidth: '140px',
            fontSize: '0.8rem',
            padding: '6px 12px',
            fontWeight: 'bold',
            boxShadow: 2,
            opacity: isLoading ? 0.7 : 1,
            '&:hover': {
              boxShadow: isLoading ? 2 : 4,
              transform: isLoading ? 'none' : 'translateY(-1px)'
            },
            '& .MuiButton-startIcon': {
              fontSize: '1rem'
            }
          }}
        >
          {isLoading ? 'Guardando...' : opcionPrimaria.estado}
        </Button>
      ) : (
        <Chip 
          label="Estado Final" 
          color="success" 
          variant="outlined"
          icon={<CompleteIcon />}
        />
      )}

      {/* Botón de menú desplegable para opciones secundarias */}
      {opcionesSecundarias.length > 0 && (
        <>
          <IconButton
            onClick={handleClick}
            size="small"
            disabled={isLoading}
            sx={{
              backgroundColor: '#f5f5f5',
              border: '1px solid #ddd',
              opacity: isLoading ? 0.5 : 1,
              '&:hover': {
                backgroundColor: isLoading ? '#f5f5f5' : '#e0e0e0'
              }
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
          
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {opcionesSecundarias.map((opcion) => (
              <MenuItem 
                key={opcion.id} 
                onClick={() => handleEstadoClick(opcion.id)}
                sx={{ 
                  fontSize: '0.8rem',
                  color: opcion.id === 6 ? '#dc3545' : 'inherit'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getEstadoIcon(opcion.id)}
                  {opcion.estado}
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
};

const TomaMuestra = ({ selectedOrden, selectedDetalles, open, onClose }) => {
  const server = process.env.REACT_APP_API_ROUTE;
  const key = process.env.REACT_APP_API_KEY;

  // Función para obtener icono y descripción según el estado
  const getEstadoIcon = (estadoId) => {
    switch (estadoId) {
      case 1:
        return <PendingIcon sx={{ fontSize: '0.8rem' }} />;
      case 2:
        return <StartIcon sx={{ fontSize: '0.8rem' }} />;
      case 3:
        return <ReceivedIcon sx={{ fontSize: '0.8rem' }} />;
      case 4:
        return <AnalysisIcon sx={{ fontSize: '0.8rem' }} />;
      case 5:
        return <CompleteIcon sx={{ fontSize: '0.8rem' }} />;
      case 6:
        return <MuestraNoConformeIcon sx={{ fontSize: '0.8rem', color: '#dc3545' }} />;
      case 7:
        return <AnalysisIcon sx={{ fontSize: '0.8rem' }} />;
      case 8:
        return <CancelIcon sx={{ fontSize: '0.8rem', color: 'red' }} />;
      case 9:
        return <PacienteAusenteIcon sx={{ fontSize: '0.8rem', color: 'orange' }} />;
      case 11:
        return <RecepcionVentanillaIcon sx={{ fontSize: '0.8rem', color: '#17a2b8' }} />;
      default:
        return <PendingIcon sx={{ fontSize: '0.8rem' }} />;
    }
  };

  const getEstadoDescription = (estadoId) => {
    switch (estadoId) {
      case 1:
        return 'Pendiente';
      case 2:
        return 'Toma Realizada';
      case 3:
        return 'Recibida';
      case 4:
        return 'En Análisis';
      case 5:
        return 'Inicio Toma Muestra';
      case 6:
        return 'Anulada';
      case 7:
        return 'Paciente Ausente';
      case 11:
        return 'Recepción ventanilla';
      default:
        return 'Pendiente';
    }
  };

  // Función para obtener el siguiente estado lógico basado en los estados disponibles
  const getSiguienteEstado = (estadoActual, estadosDisponibles) => {
    if (!estadosDisponibles || estadosDisponibles.length === 0) return null;
    
    const estadoActualObj = estadosDisponibles.find(e => e.id === estadoActual);
    const ordenActual = estadoActualObj?.orden || estadoActual;
    
    // Encontrar el siguiente estado en orden
    const estadosSiguientes = estadosDisponibles
      .filter(estado => (estado.orden || estado.id) > ordenActual)
      .sort((a, b) => (a.orden || a.id) - (b.orden || b.id));
    
    return estadosSiguientes.length > 0 ? estadosSiguientes[0] : null;
  };

  const [condiciones, setCondiciones] = useState([]);
  const [muestras, setMuestras] = useState([]);
  const [tablePage, setTablePage] = useState(0); // Estado para la página actual de la tabla
  const [rowsPerPage, setRowsPerPage] = useState(5); // Estado para el número de filas por página
  const [ordenSeleccionada, setOrdenSeleccionada] = useState({});
  const [codigoBarra, setCodigoBarra] = useState('');
  const [scanning, setScanning] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');
  const [ordenMedica, setOrdenMedica] = useState('');
  const [muestrasRealizadas, setMuestrasRealizadas] = useState([]);
  const [CondicionesCumple, setCondicionesCumple] = useState([]);
  const [condicionesOrden, setCondicionesOrden] = useState([]);
  const [muestrasOrden, setMuestrasOrden] = useState([]);
  const [muestrasSeleccionadas, setMuestrasSeleccionadas] = useState([]);
  const [imprimirEtiquetasOpen, setImprimirEtiquetasOpen] = useState(false);
  const [estadosMuestrasIndividuales, setEstadosMuestrasIndividuales] = useState(new Map());
  
  // Estados para modal de preview de etiquetas
  const [openEtiquetasPreview, setOpenEtiquetasPreview] = useState(false);
  const [etiquetasPdfUrl, setEtiquetasPdfUrl] = useState(null);
  
  // Estados para acciones masivas
  const [muestrasSeleccionadasMasivas, setMuestrasSeleccionadasMasivas] = useState([]);
  const [procesandoAccionMasiva, setProcesandoAccionMasiva] = useState(false);
  
  // Estados para las condiciones - similar al manejo de muestras
  const [condicionesSeleccionadasMasivas, setCondicionesSeleccionadasMasivas] = useState([]);
  const [procesandoGuardadoCondiciones, setProcesandoGuardadoCondiciones] = useState(false);
  const [observacionesCondiciones, setObservacionesCondiciones] = useState(new Map());
  
  // Estado para controlar el loading de cambios de estado individuales
  const [muestrasIndividualesCargando, setMuestrasIndividualesCargando] = useState(new Set());
  
  // Estados para modal de comentarios de cambio de estado
  const [openComentarioModal, setOpenComentarioModal] = useState(false);
  const [comentarioText, setComentarioText] = useState('');
  const [pendingCambioEstado, setPendingCambioEstado] = useState(null);
  const [muestraParaComentario, setMuestraParaComentario] = useState(null);
  
  // Estados para condiciones de muestra en modal de comentarios
  const [condicionesMuestra, setCondicionesMuestra] = useState([]);
  const [condicionMuestraSeleccionada, setCondicionMuestraSeleccionada] = useState('');
  const [cargandoCondicionesMuestra, setCargandoCondicionesMuestra] = useState(false);
  
  // Estados para condiciones de muestra guardadas (para edición)
  const [condicionesMuestraGuardadas, setCondicionesMuestraGuardadas] = useState(new Map());
  const [editandoCondicionMuestra, setEditandoCondicionMuestra] = useState(false);
  const [modalCondicionMuestraOpen, setModalCondicionMuestraOpen] = useState(false);
  const [condicionMuestraEditando, setCondicionMuestraEditando] = useState(null);
  const [condicionDetalleSeleccionada, setCondicionDetalleSeleccionada] = useState('');
  const [comentarioCondicionMuestra, setComentarioCondicionMuestra] = useState('');
  const [idHistoricoMuestra, setIdHistoricoMuestra] = useState(null);
  const [idMuestraOrdenActual, setIdMuestraOrdenActual] = useState(null);
  
  // Estados para cambio doble (TRASLADAR/VERIFICAR)
  const [usuarioTraslado, setUsuarioTraslado] = useState('');
  const [usuarioVerifica, setUsuarioVerifica] = useState('');
  const [fechaTraslado, setFechaTraslado] = useState('');
  const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
  const [cargandoUsuarios, setCargandoUsuarios] = useState(false);
  
  // Estado para observaciones agrupadas
  const [observacionesMuestras, setObservacionesMuestras] = useState(new Map());
  
  // Estados para Muestra Repetida
  const [motivosRepetida, setMotivosRepetida] = useState([]);
  const [motivoRepetidaSeleccionado, setMotivoRepetidaSeleccionado] = useState('');
  const [responsableRepetida, setResponsableRepetida] = useState('');
  const [cargandoMotivosRepetida, setCargandoMotivosRepetida] = useState(false);
  
  // Estado para controlar si las condiciones fueron guardadas (bloqueo para cerrar)
  const [condicionesGuardadas, setCondicionesGuardadas] = useState(false);
  
  // Estado para controlar observaciones expandidas
  const [observacionesExpandidas, setObservacionesExpandidas] = useState(new Set());
  
  // Ref para almacenar referencias a los textareas de observaciones
  const textareaRefs = useRef(new Map());
  
  // Estados para gestión de cubículo y sucursal
  const [cubiculoSeleccionado, setCubiculoSeleccionado] = useState(() => {
    return localStorage.getItem('cubiculoTomaMuestra') || '';
  });
  const [modalCubiculoOpen, setModalCubiculoOpen] = useState(false);
  const [mostrarAlertaCubiculo, setMostrarAlertaCubiculo] = useState(false);
  
  // Estados para tracking de acciones realizadas y guardado
  const [accionesRealizadas, setAccionesRealizadas] = useState({
    muestrasModificadas: 0,
    condicionesModificadas: 0,
    etiquetasImpresas: 0,
    cambiosEstado: [],
    fechaInicio: new Date()
  });
  const [guardandoFormulario, setGuardandoFormulario] = useState(false);
  
  const { user } = useUser();
  const { sucursalSeleccionada } = useSucursal();
  
  // Log para debug de datos de usuario y sucursal
  useEffect(() => {
    console.log('👤 DEBUG Usuario y Sucursal:', {
      user: user,
      sucursalSeleccionada: sucursalSeleccionada,
      idSucursalEnUser: user?.idSucursal,
      idSucursalEnSucursalSeleccionada: sucursalSeleccionada?.idSucursal,
      cubiculoSeleccionado: cubiculoSeleccionado
    });
  }, [user, sucursalSeleccionada, cubiculoSeleccionado]);
  
  // Efecto para mostrar modal de cubículo al abrir el componente si no hay cubículo seleccionado
  useEffect(() => {
    if (open && !cubiculoSeleccionado) {
      setMostrarAlertaCubiculo(true);
      setModalCubiculoOpen(true);
    }
  }, [open]);
  
  // Función para guardar cubículo seleccionado
  const handleGuardarCubiculo = (cubiculo) => {
    localStorage.setItem('cubiculoTomaMuestra', cubiculo);
    setCubiculoSeleccionado(cubiculo);
    setModalCubiculoOpen(false);
    setMostrarAlertaCubiculo(false);
    
    if (cubiculo) {
      Swal.fire({
        title: 'Cubículo Seleccionado',
        text: `Trabajando en: ${cubiculo}`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    }
  };
  
  // Función para abrir modal de gestión de cubículo
  const handleAbrirGestionCubiculo = () => {
    setModalCubiculoOpen(true);
  };
  
  // Función helper para verificar si una muestra individual está cargando
  const isMuestraIndividualCargando = (examen) => {
    const muestraKey = `${examen.id}-${examen.idMuestraXAnalisis}`;
    return muestrasIndividualesCargando.has(muestraKey);
  };
  
  // Función para formatear el usuario a 20 caracteres con espacios
  const formatUsuario = (usuario) => {
    if (!usuario) return 'ADMIN'.padEnd(20, ' ');
    return usuario.toString().substring(0, 20).padEnd(20, ' ');
  };
  
  // Función para manejar la expansión de observaciones
  const toggleObservacionExpandida = (index) => {
    const nuevasExpandidas = new Set(observacionesExpandidas);
    if (nuevasExpandidas.has(index)) {
      nuevasExpandidas.delete(index);
    } else {
      nuevasExpandidas.add(index);
    }
    setObservacionesExpandidas(nuevasExpandidas);
  };

  // Función para trackear acciones realizadas
  const trackearAccion = (tipo, detalle = null) => {
    setAccionesRealizadas(prev => {
      const nuevasAcciones = { ...prev };
      
      switch (tipo) {
        case 'muestra_modificada':
          nuevasAcciones.muestrasModificadas += (detalle?.cantidad || 1);
          break;
        case 'condicion_modificada':
          nuevasAcciones.condicionesModificadas += (detalle?.cantidad || 1);
          break;
        case 'etiquetas_impresas':
          nuevasAcciones.etiquetasImpresas += 1;
          break;
        case 'cambio_estado':
          const nuevoTimestamp = new Date();
          const ultimoCambio = nuevasAcciones.cambiosEstado[nuevasAcciones.cambiosEstado.length - 1];
          
          // Evitar duplicados de cambios de estado similares en un período corto
          const esDuplicado = ultimoCambio && 
                            ultimoCambio.idMuestraXAnalisis === detalle.idMuestraXAnalisis &&
                            ultimoCambio.estadoNuevoId === detalle.estadoNuevoId &&
                            (nuevoTimestamp - ultimoCambio.timestamp) < 2000;
          
          if (!esDuplicado) {
            nuevasAcciones.cambiosEstado.push({
              ...detalle,
              timestamp: nuevoTimestamp
            });
          }
          break;
        default:
          break;
      }
      
      return nuevasAcciones;
    });
  };

  // Función para generar resumen de acciones realizadas
  const generarResumenAcciones = () => {
    const tiempoTranscurrido = Math.round((new Date() - accionesRealizadas.fechaInicio) / 1000 / 60);
    const totalCambiosEstado = accionesRealizadas.cambiosEstado.length;
    
    let resumen = `📊 RESUMEN DE ACTIVIDAD\n\n`;
    resumen += `⏱️ Tiempo en pantalla: ${tiempoTranscurrido} minuto(s)\n`;
    
    // Solo agregar acciones que se realizaron (> 0)
    const acciones = [];
    
    if (accionesRealizadas.muestrasModificadas > 0) {
      acciones.push(`🔬 Muestras procesadas: ${accionesRealizadas.muestrasModificadas}`);
    }
    
    if (accionesRealizadas.condicionesModificadas > 0) {
      acciones.push(`📋 Condiciones actualizadas: ${accionesRealizadas.condicionesModificadas}`);
    }
    
    if (accionesRealizadas.etiquetasImpresas > 0) {
      acciones.push(`🏷️ Etiquetas impresas: ${accionesRealizadas.etiquetasImpresas} vez(es)`);
    }
    
    if (totalCambiosEstado > 0) {
      acciones.push(`🔄 Cambios de estado: ${totalCambiosEstado}`);
    }
    
    if (acciones.length > 0) {
      resumen += acciones.join('\n') + '\n\n';
      
      // Solo mostrar detalle si hay cambios de estado
      if (accionesRealizadas.cambiosEstado.length > 0) {
        resumen += `📝 Detalle de cambios de estado:\n`;
        accionesRealizadas.cambiosEstado.forEach((cambio, index) => {
          resumen += `${index + 1}. ${cambio.descripcion || 'Cambio de estado'} - ${cambio.timestamp.toLocaleTimeString()}\n`;
        });
      }
    } else {
      resumen += `ℹ️ No se realizaron acciones específicas durante esta sesión.\n`;
    }
    
    return resumen;
  };

  // Función para limpiar completamente el formulario
  const limpiarFormularioCompleto = () => {
    // Limpiar estados de muestras
    setMuestras([]);
    setMuestrasSeleccionadas([]);
    setMuestrasSeleccionadasMasivas([]);
    setMuestrasOrden([]);
    setMuestrasRealizadas([]);
    setEstadosMuestrasIndividuales(new Map());
    setMuestrasIndividualesCargando(new Set());
    
    // Limpiar estados de condiciones
    setCondiciones([]);
    setCondicionesOrden([]);
    setCondicionesCumple([]);
    setCondicionesSeleccionadasMasivas([]);
    setObservacionesCondiciones(new Map());
    setCondicionesMuestraGuardadas(new Map());
    
    // Limpiar observaciones
    setObservacionesMuestras(new Map());
    setObservacionesExpandidas(new Set());
    textareaRefs.current.clear();
    
    // Limpiar modales
    setImprimirEtiquetasOpen(false);
    setOpenComentarioModal(false);
    setModalCondicionMuestraOpen(false);
    setOpenEtiquetasPreview(false);
    
    // Limpiar otros estados
    setTablePage(0);
    setRowsPerPage(5);
    setCodigoBarra('');
    setScanning(false);
    setOrdenMedica('');
    setOrdenSeleccionada({});
    setProcesandoAccionMasiva(false);
    
    // Resetear tracking de acciones
    setAccionesRealizadas({
      muestrasModificadas: 0,
      condicionesModificadas: 0,
      etiquetasImpresas: 0,
      cambiosEstado: [],
      fechaInicio: new Date()
    });
    
    console.log('🧹 Formulario completamente limpiado');
  };

  // Función principal de guardado
  const handleGuardarFormulario = async () => {
    // Validar si hay condiciones sin guardar
    if (condiciones.length > 0 && !condicionesGuardadas) {
      Swal.fire({
        title: 'Condiciones sin guardar',
        html: 'Debe guardar las condiciones antes de continuar.<br><br><small>Presione <kbd>ESC</kbd> para salir sin guardar</small>',
        icon: 'warning',
        confirmButtonText: 'Entendido'
      });
      return;
    }
    
    setGuardandoFormulario(true);
    
    try {
      // Simular proceso de guardado (aunque ya esté guardado)
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      await Swal.fire({
        title: 'Toma de muestra guardada',
        icon: 'success',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#28a745',
        allowOutsideClick: false,
        preConfirm: () => {
          limpiarFormularioCompleto();
        }
      });
      
    } catch (error) {
      console.error('Error al guardar:', error);
      Swal.fire({
        title: 'Error',
        text: 'Error al procesar el guardado',
        icon: 'error',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setGuardandoFormulario(false);
    }
  };
  
  // Componente interno para el campo de texto - TOTALMENTE NO CONTROLADO
  const TextFieldObservacion = ({ index, valorInicial, placeholderTexto }) => {
    return (
      <textarea
        ref={(el) => {
          if (el) {
            textareaRefs.current.set(index, el);
            console.log(`📝 Textarea ref registrado para índice ${index}`);
          }
        }}
        placeholder={placeholderTexto || 'Ingrese observaciones...'}
        defaultValue={valorInicial || ''}
        autoFocus
        style={{
          width: '100%',
          minHeight: '120px',
          padding: '8px 12px',
          fontSize: '0.9rem',
          fontFamily: 'Roboto, sans-serif',
          border: '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: '4px',
          resize: 'vertical',
          outline: 'none',
          transition: 'border-color 0.2s',
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#1976d2';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = 'rgba(0, 0, 0, 0.23)';
        }}
      />
    );
  };
  
  // Componente para mostrar observaciones compactas
  const ObservacionesCompactas = ({ index, observacion, placeholder }) => {
    const estaExpandida = observacionesExpandidas.has(index);
    const tieneContenido = observacion && observacion.trim();
    
    // Verificar si la muestra está en estado "Muestra No conforme"
    const muestra = muestras[index];
    const esMuestraNoConforme = muestra && (muestra.idEstado === 9); // ID 9 = "Muestra No conforme"
    
    // Obtener condición de muestra guardada si existe
    const tieneCondicionMuestra = esMuestraNoConforme && condicionesMuestraGuardadas.size > 0;
    
    if (!estaExpandida) {
      // Vista compacta: botón con ícono diferente si es muestra no conforme
      return (
        <IconButton
          size="small"
          onClick={() => esMuestraNoConforme ? abrirModalCondicionMuestra(index) : toggleObservacionExpandida(index)}
          sx={{ 
            padding: '6px',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
            color: esMuestraNoConforme ? (tieneCondicionMuestra ? '#d32f2f' : '#ff9800') : (tieneContenido ? '#1976d2' : '#999')
          }}
          title={
            esMuestraNoConforme 
              ? (tieneCondicionMuestra ? 'Ver/editar condición de muestra no conforme' : 'Agregar condición de muestra no conforme')
              : (tieneContenido ? 'Ver/editar observaciones' : 'Agregar observaciones')
          }
        >
          {esMuestraNoConforme ? (
            <MuestraNoConformeIcon sx={{ fontSize: '1.2rem' }} />
          ) : tieneContenido ? (
            <CommentIcon sx={{ fontSize: '1.2rem' }} />
          ) : (
            <CommentOutlinedIcon sx={{ fontSize: '1.2rem' }} />
          )}
        </IconButton>
      );
    }

    // Modal expandido para observaciones normales (no muestra no conforme)
    if (!esMuestraNoConforme) {
      return (
        <>
          {/* Overlay backdrop */}
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1300
            }}
            onClick={() => toggleObservacionExpandida(index)}
          />
          {/* Modal content */}
          <Box sx={{ 
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '500px',
            maxHeight: '400px',
            backgroundColor: 'white',
            boxShadow: 24,
            borderRadius: 2,
            p: 3,
            zIndex: 1301
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Observaciones</Typography>
              <IconButton
                size="small"
                onClick={() => toggleObservacionExpandida(index)}
                sx={{ 
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                }}
              >
                <ExpandLessIcon />
              </IconButton>
            </Box>
            <TextFieldObservacion
              index={index}
              valorInicial={observacionesMuestras.get(index) || getObservacionMuestra(index) || ''}
              placeholderTexto="Ingrese observaciones..."
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => toggleObservacionExpandida(index)}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  console.log('🔘 Botón Guardar presionado para índice:', index);
                  
                  // Obtener el texto del textarea usando el ref
                  const textarea = textareaRefs.current.get(index);
                  const textoObservacion = textarea ? textarea.value.trim() : '';
                  console.log('🔘 Textarea encontrado:', !!textarea);
                  console.log('🔘 Texto capturado del textarea:', textoObservacion);
                  
                  // Cerrar modal INMEDIATAMENTE
                  const nuevasExpandidas = new Set(observacionesExpandidas);
                  nuevasExpandidas.delete(index);
                  setObservacionesExpandidas(nuevasExpandidas);
                  console.log('🔘 Modal cerrado');
                  
                  // Guardar en background
                  if (textoObservacion) {
                    guardarObservacionHistorico(index, textoObservacion)
                      .then(async () => {
                        console.log('✅ Guardado exitoso');
                        // Recargar las muestras para mostrar el comentario actualizado
                        await fetchMuestrasOrden(ordenSeleccionada.idOrdenMedica);
                        console.log('🔄 Muestras recargadas después de guardar observación');
                        Swal.fire({
                          icon: 'success',
                          title: 'Guardado',
                          text: 'Observación guardada correctamente',
                          timer: 1500,
                          showConfirmButton: false
                        });
                      })
                      .catch(error => {
                        console.error('❌ Error al guardar:', error);
                        Swal.fire({
                          icon: 'error',
                          title: 'Error',
                          text: 'No se pudo guardar la observación',
                          timer: 2000,
                          showConfirmButton: false
                        });
                      });
                  } else {
                    console.log('⚠️ No hay texto para guardar');
                  }
                }}
              >
                Guardar
              </Button>
            </Box>
          </Box>
        </>
      );
    }
    
    return null; // Para muestras no conformes, el modal se maneja por separado
  };
  
  // Función para abrir modal de condición de muestra
  const abrirModalCondicionMuestra = async (muestraIndex) => {
    setCondicionMuestraEditando(muestraIndex);
    
    // Obtener la información de la muestra
    const muestraInfo = muestras[muestraIndex];
    console.log('🔍 Abriendo modal para muestra:', muestraInfo);
    
    // Cargar condiciones disponibles
    await fetchCondicionesMuestra();
    
    // Dejar el campo de comentario vacío para que el usuario escriba libremente
    setComentarioCondicionMuestra('');
    setIdHistoricoMuestra(null);
    setIdMuestraOrdenActual(null);
    
    // Obtener condición no conforme usando la nueva API
    const condicionGuardada = await fetchCondicionNoConforme(ordenSeleccionada.idOrdenMedica);
    console.log('🔍 Condición guardada encontrada al abrir modal:', condicionGuardada);
    
    if (condicionGuardada && condicionGuardada.idCondicion) {
      // Pre-llenar la condición guardada
      console.log('🔍 Pre-llenando condición guardada:', condicionGuardada.idCondicion);
      setCondicionMuestraSeleccionada(condicionGuardada.idCondicion.toString());
    } else {
      // No hay condición guardada, limpiar selección
      console.log('🔍 No hay condición guardada, limpiando selección');
      setCondicionMuestraSeleccionada('');
    }
    
    setModalCondicionMuestraOpen(true);
  };
  
  // Función para cerrar modal de condición de muestra
  const cerrarModalCondicionMuestra = () => {
    setModalCondicionMuestraOpen(false);
    setCondicionMuestraEditando(null);
    setCondicionMuestraSeleccionada('');
    setComentarioCondicionMuestra('');
    setIdHistoricoMuestra(null);
    setIdMuestraOrdenActual(null);
  };
  
  // Función para obtener la condición de muestra guardada para mostrar
  const getCondicionMuestraGuardada = (muestraIndex) => {
    // Buscar si hay alguna condición guardada que coincida con esta muestra
    // Como las condiciones de muestra se guardan por orden (no por muestra específica),
    // retornamos la primera que encontremos
    for (const [idCondicion, condicionGuardada] of condicionesMuestraGuardadas) {
      return condicionGuardada;
    }
    return null;
  };
  
  // Función para actualizar comentario en el histórico de estados de muestra
  const actualizarComentarioHistorico = async (nuevoComentario) => {
    try {
      if (!idHistoricoMuestra) {
        console.error('No se encontró ID del histórico para actualizar');
        return false;
      }
      
      // Primero obtener el registro completo
      const getResponse = await fetch(`${server}/mg_historico_estados_muestra/${idHistoricoMuestra}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!getResponse.ok) {
        throw new Error('No se pudo obtener el registro del histórico');
      }
      
      const registroCompleto = await getResponse.json();
      console.log('Registro completo obtenido:', registroCompleto);
      
      // Actualizar solo el comentario y los campos de auditoría
      const registroActualizado = {
        ...registroCompleto,
        comentario: nuevoComentario,
        FechaModificacion: new Date().toISOString(),
        ModificadoPor: formatUsuario(user ? user.codigoUsuario : 'ADMIN')
      };
      
      const response = await fetch(`${server}/mg_historico_estados_muestra/${idHistoricoMuestra}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(registroActualizado)
      });
      
      if (response.ok) {
        console.log('Comentario del histórico actualizado exitosamente');
        return true;
      } else {
        const errorText = await response.text();
        console.error('Error al actualizar comentario del histórico:', response.status, errorText);
        throw new Error(`Error del servidor: ${response.status}`);
      }
    } catch (error) {
      console.error('Error al actualizar comentario del histórico:', error);
      throw error;
    }
  };
  
  // Función para actualizar condición de muestra
  const actualizarCondicionMuestra = async (idCondicion, nuevasObservaciones) => {
    try {
      const condicionExistente = condicionesMuestraGuardadas.get(idCondicion);
      
      if (condicionExistente) {
        // Actualizar condición existente
        const response = await fetch(`${server}/mg_condiciones_x_ordens/${condicionExistente.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${key}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...condicionExistente,
            observaciones: [nuevasObservaciones],
            FechaModificacion: new Date().toISOString(),
            ModificadoPor: formatUsuario(user ? user.codigoUsuario : 'ADMIN')
          })
        });

        if (response.ok) {
          console.log('Condición de muestra actualizada exitosamente');
          
          // Recargar condiciones guardadas
          await fetchCondicionesMuestraGuardadas(ordenSeleccionada.idOrdenMedica);
          
          return true;
        } else {
          throw new Error(`Error del servidor: ${response.status}`);
        }
      } else {
        // Crear nueva condición
        return await guardarCondicionMuestra(idCondicion, nuevasObservaciones);
      }
    } catch (error) {
      console.error('Error al actualizar condición de muestra:', error);
      throw error;
    }
  };
  
  const handleChangeTablePage = (event, newPage) => {
    setTablePage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setTablePage(0);
  };

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return '';

    const nacimiento = new Date(fechaNacimiento);
    const hoy = new Date();

    let años = hoy.getFullYear() - nacimiento.getFullYear();
    let meses = hoy.getMonth() - nacimiento.getMonth();
    let días = hoy.getDate() - nacimiento.getDate();

    // Ajustar días si hoy es antes del día de nacimiento
    if (días < 0) {
      meses--;
      const ultimoDiaMesAnterior = new Date(hoy.getFullYear(), hoy.getMonth(), 0).getDate();
      días += ultimoDiaMesAnterior;
    }

    // Ajustar meses si es antes del mes de nacimiento
    if (meses < 0) {
      años--;
      meses += 12;
    }

    return `${años} años, ${meses} meses y ${días} días`;
  };

  const formatearFechaISO = (fecha) => {
    if (!fecha) return '';
    return fecha.replace('T', ' ');
  };
  const formatFecha = (fechaString) => {
    if (!fechaString) return '';
    
    // Como las fechas en la DB son "timestamp without time zone", 
    // las tratamos como hora local, no UTC
    const fecha = new Date(fechaString);
    
    // Verificar si la fecha es válida
    if (isNaN(fecha.getTime())) return '';
    
    // Formatear como hora local (no UTC)
    return fecha.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };


  const handleCodigoBarraChange = async (codigo) => {
    const numericCodigo = String(codigo).replace(/\D/g, '');
    setCodigoBarra(numericCodigo);

    if (numericCodigo.length > 0) {
      try {
        const response = await fetch(`${server}/MgOrdenesMedicas/OrdenMedica/${numericCodigo}`);
        if (response.ok) {
          const data = await response.json();
          const idCliente = data.idCliente;
          const idOrdenMedica = data.idOrdenMedica;

          const orden = {
            cliente: data.nombreCompleto,
            identidad: data.numeroIdentidad,
            edad: calcularEdad(data.fechaNacimiento),
            sexo: data.descripcionSexo,
            direccion: data.direccionCompleta,
            telefono: data.telefono,
            correoElectronico: data.correoElectronico,
            fecha: data.fechaOrdenMedica || '',
            validez: data.validez || '',
            tiempo: data.tiempo || '',
            tipoPago: data.descripcionTipoPago || '',
            sucursalEntrega: data.sucursalEntrega || '',
            fechaHoraRecepcion: formatearFechaISO(data.fechaRecepcion),
            fechaHoraEntrega: formatearFechaISO(data.fechaEntrega),
            idOrdenMedica: data.idOrdenMedica,
          };

          setOrdenSeleccionada(orden);
          
          // Limpiar estados iniciales
          setMuestras([]);
          setMuestrasRealizadas([]);
          setCondiciones([]);
          setCondicionesCumple([]);
          setMuestrasSeleccionadas([]);
          
          // Asegurar que los estados están cargados
          if (estadosDisponibles.length === 0) {
            await fetchEstadosDisponibles();
          }
          
          // Obtener datos de la orden - PRIMERO las muestras
          const muestrasObtenidas = await fetchMuestrasOrden(idOrdenMedica);
          
          // LUEGO las condiciones (usando las muestras recién obtenidas)
          if (muestrasObtenidas && muestrasObtenidas.length > 0) {
            await fetchCondicionesOrden(idOrdenMedica, muestrasObtenidas);
          }
          
        } else {
          console.error('Error al obtener la orden');
        }
      } catch (error) {
        console.error('Error al obtener la orden:', error);
      }
    }
  };

  useEffect(() => {
    fetchEstadosDisponibles();
    
    // Agregar listener para tecla ESC
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && open && onClose) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscKey);
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open && selectedOrden) {
      setMuestrasSeleccionadas([]);
      
      // Limpiar estados para prevenir datos viejos
      setMuestras([]);
      setMuestrasRealizadas([]);
      setCondiciones([]);
      setCondicionesCumple([]);
      setCondicionesMuestraGuardadas(new Map());
      
      // Resetear estado de guardado de condiciones
      setCondicionesGuardadas(false);
      
      // Cargar datos actualizados
      handleCodigoBarraChange(selectedOrden.idOrdenMedicaMes);
      
      // Cargar condiciones de muestra guardadas
      if (selectedOrden.idOrdenMedica) {
        fetchCondicionesMuestraGuardadas(selectedOrden.idOrdenMedica);
      }
    }
  }, [open, selectedOrden]);

  // Efecto para mapear condiciones cuando tanto muestras como condiciones estén cargadas
  useEffect(() => {
    if (muestras && muestras.length > 0 && condiciones && condiciones.length > 0) {
      // Verificar si alguna condición no está mapeada
      const condicionesSinMapear = condiciones.filter(c => c.muestraRealEncontrada === false);
      if (condicionesSinMapear.length > 0) {
        const condicionesMapeadas = mapearCondicionesAMuestras(condiciones, muestras);
        setCondiciones(condicionesMapeadas);
        setCondicionesOrden(condicionesMapeadas);
      }
    }
  }, [muestras, condiciones]);

  // Función para agrupar muestras por descripción, módulo, cantidad Y ESTADO
  //Version Vieja de agruparMuestras:
  /*const agruparMuestras = (muestrasData) => {
    const muestrasAgrupadas = new Map();
    
    muestrasData.forEach((muestra, index) => {
      // Crear una clave única para agrupar incluyendo el estado
      const estadoId = muestra.idEstado || 1;
      const clave = `${muestra.descripcionMuestra}-${muestra.modulo}-${muestra.cantidad}-${estadoId}`;
      
      if (muestrasAgrupadas.has(clave)) {
        // Si ya existe, agregar el análisis a la lista
        const muestraExistente = muestrasAgrupadas.get(clave);
        muestraExistente.analisis.push(muestra.analisis);
        muestraExistente.examenes.push(muestra);
        
        // Combinar históricos de todas las muestras del grupo
        if (muestra.historico && muestra.historico.$values && muestra.historico.$values.length > 0) {
          muestraExistente.historicoCompleto.push(...muestra.historico.$values);
        }
      } else {
        // Extraer histórico inicial
        let historicoInicial = [];
        if (muestra.historico) {
          if (muestra.historico.$values && Array.isArray(muestra.historico.$values)) {
            historicoInicial = [...muestra.historico.$values];
          } else if (Array.isArray(muestra.historico)) {
            historicoInicial = [...muestra.historico];
          }
        }
        
        // Crear nueva entrada agrupada
        muestrasAgrupadas.set(clave, {
          idMuestraXAnalisis: muestra.idMuestraXAnalisis,
          descripcionMuestra: muestra.descripcionMuestra,
          cantidad: muestra.cantidad,
          modulo: muestra.modulo,
          analisis: [muestra.analisis],
          examenes: [muestra],
          ComparteCon: muestra.analisis, // Se actualizará después
          idEstado: estadoId,
          estadoDescripcion: muestra.estadoDescripcion || 'Pendiente',
          realizada: estadoId >= 2, // Estados 2+ se consideran realizados
          fechaHoraToma: muestra.inicioTomaMuestra ? formatFecha(muestra.inicioTomaMuestra) : null,
          usuarioToma: muestra.ingresadoPor ? muestra.ingresadoPor.trim() : null,
          esCobas303: muestra.esCobas303 || false,
          descripcionCantidad: muestra.cantidad,
          // Información para el detalle
          cantidadMuestras: 1, // Se actualizará al final
          // Preservar el histórico completo de todas las muestras del grupo
          historicoCompleto: historicoInicial
        });
      }
    });
    
    // Convertir el Map a array y formatear ComparteCon
    const resultado = Array.from(muestrasAgrupadas.values()).map(muestra => ({
      ...muestra,
      ComparteCon: muestra.analisis.join(', '),
      cantidadMuestras: muestra.examenes.length,
      // Ordenar histórico por fecha (más reciente primero)
      historicoCompleto: muestra.historicoCompleto.sort((a, b) => {
        const fechaA = new Date(a.fecha_cambio_estado || 0);
        const fechaB = new Date(b.fecha_cambio_estado || 0);
        return fechaB - fechaA;
      })
    }));
    
    return resultado;
  }; */

  // ✅ Versión optimizada (drop-in) - Agrupa por descripción, módulo, cantidad y estado
const agruparMuestras = (muestrasData) => {
  // Seguridad por si llega null/undefined
  if (!Array.isArray(muestrasData)) return [];

  const muestrasAgrupadas = new Map();

  for (const muestra of muestrasData) {
    const estadoId = muestra?.idEstado ?? 1;

    // Clave única de agrupación
    const clave = `${muestra?.descripcionMuestra ?? ""}|${muestra?.modulo ?? ""}|${muestra?.cantidad ?? ""}|${estadoId}`;

    let grupo = muestrasAgrupadas.get(clave);

    if (grupo) {
      // Agregar análisis y examen
      grupo.analisis.push(muestra.analisis);
      grupo.examenes.push(muestra);

      // Combinar historico si existe
      const h = muestra?.historico;
      const hVals = Array.isArray(h?.$values) ? h.$values : (Array.isArray(h) ? h : null);
      if (hVals && hVals.length) {
        grupo.historicoCompleto.push(...hVals);
      }
    } else {
      // Extraer historico inicial
      const h = muestra?.historico;
      const hVals = Array.isArray(h?.$values) ? h.$values : (Array.isArray(h) ? h : []);
      const historicoInicial = hVals.length ? [...hVals] : [];

      // Crear nuevo grupo (mismas props que versión original)
      grupo = {
        idMuestraXAnalisis: muestra.idMuestraXAnalisis,
        descripcionMuestra: muestra.descripcionMuestra,
        cantidad: muestra.cantidad,
        modulo: muestra.modulo,
        analisis: [muestra.analisis],
        examenes: [muestra],
        ComparteCon: muestra.analisis, // se recalcula al final
        idEstado: estadoId,
        estadoDescripcion: muestra.estadoDescripcion || "Pendiente",
        realizada: estadoId >= 2,
        fechaHoraToma: muestra.inicioTomaMuestra ? formatFecha(muestra.inicioTomaMuestra) : null,
        usuarioToma: muestra.ingresadoPor ? String(muestra.ingresadoPor).trim() : null,
        esCobas303: !!muestra.esCobas303,
        descripcionCantidad: muestra.cantidad,
        cantidadMuestras: 1, // se recalcula al final
        historicoCompleto: historicoInicial,
      };

      muestrasAgrupadas.set(clave, grupo);
    }
  }

  // Convertir Map a array y formatear campos derivados
  const resultado = Array.from(muestrasAgrupadas.values()).map((m) => {
    // Ordenar histórico por fecha (más reciente primero) de forma más eficiente
    const historicoOrdenado = Array.isArray(m.historicoCompleto) && m.historicoCompleto.length
      ? [...m.historicoCompleto].sort((a, b) => {
          const tA = Date.parse(a?.fecha_cambio_estado || "") || 0;
          const tB = Date.parse(b?.fecha_cambio_estado || "") || 0;
          return tB - tA;
        })
      : [];

    return {
      ...m,
      ComparteCon: Array.isArray(m.analisis) ? m.analisis.join(", ") : "",
      cantidadMuestras: Array.isArray(m.examenes) ? m.examenes.length : 0,
      historicoCompleto: historicoOrdenado,
    };
  });

  return resultado;
};

  const fetchMuestrasOrden = async (idOrdenMedica) => {
    console.log('🔍 [API GET] fetchMuestrasOrden - INICIO', { idOrden: idOrdenMedica });
    const t0 = performance.now();
    try {
      const response = await fetch(`${server}/mg_muestras_x_ordens/pororden/${idOrdenMedica}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        },
      });
      const t1 = performance.now();
      console.log('⏱️ [API GET] fetchMuestrasOrden - Network Time:', `${(t1 - t0).toFixed(0)}ms`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const muestrasData = await response.json();
      const t2 = performance.now();
      // Procesar y agrupar las muestras por tipo de muestra, módulo y cantidad
      const muestrasAgrupadas = agruparMuestras(muestrasData.$values || muestrasData);
      const t3 = performance.now();
      console.log('📊 fetchMuestrasOrden - Muestras agrupadas obtenidas:', muestrasAgrupadas.length);
      console.log('📊 fetchMuestrasOrden - Primer muestra de ejemplo:', muestrasAgrupadas[0]);
      
      console.log({
        network_server_ms: (t1 - t0).toFixed(0),
        json_parse_ms: (t2 - t1).toFixed(0),
        group_ms: (t3 - t2).toFixed(0),
        total_ms: (t3 - t0).toFixed(0),
      });

      setMuestrasOrden(muestrasAgrupadas);
      setMuestras(muestrasAgrupadas);
      
      // IMPORTANTE: Retornar las muestras para que puedan usarse inmediatamente
      return muestrasAgrupadas;
    } catch (error) {
      console.error('Error al obtener las muestras:', error);
      return [];
    }
  };

  // Función para mapear condiciones a muestras reales
  const mapearCondicionesAMuestras = (condiciones, muestrasReales) => {
    const condicionesMapeadas = condiciones.map(condicion => {
      // Buscar la muestra real que corresponde a esta condición
      const muestraReal = muestrasReales.find(muestra => {
        // Comparar análisis, limpiando espacios
        // condicion.analisis es un array de nombres de análisis
        const analisisCondicion = condicion.analisis && condicion.analisis[0] ? 
          (typeof condicion.analisis[0] === 'string' ? condicion.analisis[0].trim() : '') : '';
        
        // muestra.analisis puede ser string o array dependiendo si es agrupada o no
        let analisisMuestra = '';
        if (muestra.analisis) {
          if (Array.isArray(muestra.analisis)) {
            // Si es array (muestra agrupada), tomar el primer elemento
            analisisMuestra = muestra.analisis[0] ? muestra.analisis[0].trim() : '';
          } else {
            // Si es string (muestra individual), usarlo directamente
            analisisMuestra = muestra.analisis.trim();
          }
        }
        
        return analisisCondicion === analisisMuestra;
      });
      
      if (muestraReal) {
        return {
          ...condicion,
          idCondicionXMuestraXAnalisis: muestraReal.idMuestraXAnalisis,
          muestraRealEncontrada: true
        };
      } else {
        return {
          ...condicion,
          muestraRealEncontrada: false
        };
      }
    });
    
    return condicionesMapeadas;
  };

  // Función para agrupar condiciones por descripción
  const agruparCondiciones = (condicionesData, muestrasDisponibles = null) => {
    const condicionesAgrupadas = new Map();
    
    condicionesData.forEach(condicion => {
      
      // Crear una clave única para agrupar por descripción de condición
      const clave = condicion.descripcionCondicion;
      
      // Buscar el nombre del análisis en las muestras disponibles
      let nombreAnalisis = 'Análisis no encontrado';
      const idMuestraAnalisis = condicion.idMuestraXAnalisis || condicion.IdMuestraXAnalisis;
      
      if (muestrasDisponibles && idMuestraAnalisis) {
        const muestraEncontrada = muestrasDisponibles.find(m => m.idMuestraXAnalisis === idMuestraAnalisis);
        if (muestraEncontrada) {
          nombreAnalisis = muestraEncontrada.analisis;
          }
      }
      
      if (condicionesAgrupadas.has(clave)) {
        // Si ya existe, agregar el análisis a la lista
        const condicionExistente = condicionesAgrupadas.get(clave);
        if (!condicionExistente.analisis.includes(nombreAnalisis)) {
          condicionExistente.analisis.push(nombreAnalisis);
        }
        
        // Si esta condición está guardada, actualizar el estado de la agrupada
        if (condicion.esGuardada || condicion.idRegistroGuardado) {
          condicionExistente.esGuardada = true;
          condicionExistente.cumple = Boolean(condicion.cumple);
          condicionExistente.idRegistroGuardado = condicion.idRegistroGuardado || condicion.id || condicion.Id;
          
          // Actualizar observaciones si existen
          const obs = condicion.observaciones || condicion.Observaciones || '';
          if (obs) {
            condicionExistente.observacionesCondicion = Array.isArray(obs) ? obs[0] : obs;
          }
        }
      } else {
        // Determinar estado inicial basado en datos del backend
        let cumpleInicial = false;
        let observacionInicial = '';
        let idRegistroGuardado = null;
        let esGuardada = false;
        
        // Verificar si esta condición está guardada
        if (condicion.esGuardada || condicion.idRegistroGuardado || condicion.id || condicion.Id) {
          esGuardada = true;
          cumpleInicial = Boolean(condicion.cumple);
          observacionInicial = condicion.observaciones || condicion.Observaciones || '';
          idRegistroGuardado = condicion.idRegistroGuardado || condicion.id || condicion.Id || null;
          
          // Si observaciones es un array, tomar el primer elemento
          if (Array.isArray(observacionInicial) && observacionInicial.length > 0) {
            observacionInicial = observacionInicial[0];
          }
        }
        
        // Crear nueva entrada agrupada
        condicionesAgrupadas.set(clave, {
          idCondicionXMuestraXAnalisis: idMuestraAnalisis,
          idCondicion: condicion.IdCondicionCatalogo || condicion.idCondicionCatalogo || condicion.id_condicion || condicion.idCondicion || condicion.IdCondicion || null,
          descripcionCondicion: condicion.descripcionCondicion,
          analisis: [nombreAnalisis],
          cumple: cumpleInicial,
          observacionesCondicion: observacionInicial,
          ComparteCon: nombreAnalisis,
          esGuardada: esGuardada,
          idRegistroGuardado: idRegistroGuardado,
          id_muestra_x_analisis: condicion.id_muestra_x_analisis
        });
      }
    });
    
    // Convertir el Map a array y formatear ComparteCon
    const resultado = Array.from(condicionesAgrupadas.values()).map(condicion => ({
      ...condicion,
      ComparteCon: condicion.analisis.join(', ')
    }));
    
    return resultado;
  };

  // Función para crear una nueva muestra orden
  const crearNuevaMuestraOrden = async (idMuestraXAnalisis, estadoId, fechaHora) => {
    console.log('🔍 [API POST] crearNuevaMuestraOrden - INICIO', { idMuestraXAnalisis, estadoId });
    const t0 = performance.now();
    const dataToInsert = {
      id_orden: ordenSeleccionada.idOrdenMedica,
      id_muestra_x_analisis: idMuestraXAnalisis,
      id_estado: estadoId,
      inicio_toma_muestra: estadoId >= 2 ? fechaHora : null, // Solo si está en proceso o posterior
      fin_toma_muestra: estadoId >= 3 ? fechaHora : null, // Solo si está procesada o posterior
      ingresadoPor: formatUsuario(user ? user.codigoUsuario : 'ADMIN')
    };

    const response = await fetch(`${server}/mg_muestras_x_ordens`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': key,
      },
      body: JSON.stringify(dataToInsert),
    });

    const t1 = performance.now();
    console.log('⏱️ [API POST] crearNuevaMuestraOrden - Network Time:', `${(t1 - t0).toFixed(0)}ms`);
    
    if (!response.ok) {
      throw new Error(`Error al crear muestra orden: ${response.statusText}`);
    }

    return await response.json();
  };

  // Función para actualizar muestra orden existente
  const actualizarMuestraOrden = async (id, estadoId, fechaHora) => {
    console.log('🔍 [API PUT] actualizarMuestraOrden - INICIO', { id, estadoId });
    const t0 = performance.now();
    const dataToUpdate = {
      id: id,
      id_estado: estadoId,
      inicio_toma_muestra: estadoId >= 2 ? fechaHora : null,
      fin_toma_muestra: estadoId >= 3 ? fechaHora : null,
      modificadoPor: formatUsuario(user ? user.codigoUsuario : 'ADMIN')
    };

    const response = await fetch(`${server}/mg_muestras_x_ordens/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': key,
      },
      body: JSON.stringify(dataToUpdate),
    });

    const t1 = performance.now();
    console.log('⏱️ [API PUT] actualizarMuestraOrden - Network Time:', `${(t1 - t0).toFixed(0)}ms`);
    
    if (!response.ok) {
      throw new Error(`Error al actualizar muestra orden: ${response.statusText}`);
    }
  };

  // Función para crear registro en histórico de estados
  const crearHistoricoEstado = async (idMuestraOrden, idMuestraXAnalisis, estadoAnterior, estadoNuevo, comentario) => {
    try {
      // Si no tenemos el ID de muestra orden, lo buscamos
      if (!idMuestraOrden) {
        const muestraOrden = muestrasOrden.find(m => m.id_muestra_x_analisis === idMuestraXAnalisis);
        idMuestraOrden = muestraOrden?.id;
      }

      if (!idMuestraOrden) {
        console.warn('No se pudo obtener ID de muestra orden para histórico');
        return;
      }

      // Formatear nombre de sucursal (similar a usuario)
      const formatSucursal = (sucursal) => {
        if (!sucursal) return '';
        return sucursal.toString().substring(0, 50); // Limitar a 50 caracteres
      };

      const dataHistorico = {
        id_muestra_x_orden: idMuestraOrden,
        id_estado_anterior: estadoAnterior !== estadoNuevo ? estadoAnterior : null,
        id_estado_nuevo: estadoNuevo,
        comentario: comentario.trim(),
        fecha_cambio_estado: new Date().toISOString().slice(0, -1),
        cambiado_por: formatUsuario(user ? user.codigoUsuario : 'ADMIN'),
        ingresadoPor: formatUsuario(user ? user.codigoUsuario : 'ADMIN'),
        id_sucursal: user?.idSucursal || null,
        cubiculo: cubiculoSeleccionado || null
      };

      console.log('📝 Creando histórico con cubículo y sucursal:', {
        cubiculo: dataHistorico.cubiculo,
        idSucursal: dataHistorico.id_sucursal,
        usuario: dataHistorico.cambiado_por
      });

      const t0Historico = performance.now();
      const response = await fetch(`${server}/mg_historico_estados_muestra`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        },
        body: JSON.stringify(dataHistorico),
      });
      const t1Historico = performance.now();
      console.log('⏱️ [API POST] crearHistoricoEstado - Network Time:', `${(t1Historico - t0Historico).toFixed(0)}ms`);

      if (!response.ok) {
        console.error('Error al crear histórico:', await response.text());
      } else {
        console.log('Histórico creado exitosamente con cubículo:', dataHistorico.cubiculo);
        
        // Trackear el cambio de estado con nombres descriptivos
        const estadoAnteriorObj = estadosDisponibles.find(e => e.id === estadoAnterior) || { estado: 'Desconocido' };
        const estadoNuevoObj = estadosDisponibles.find(e => e.id === estadoNuevo) || { estado: 'Desconocido' };
        
        trackearAccion('cambio_estado', {
          descripcion: `${estadoAnteriorObj.estado} → ${estadoNuevoObj.estado}`,
          idMuestraXAnalisis: idMuestraXAnalisis,
          comentario: comentario.trim(),
          estadoAnteriorId: estadoAnterior,
          estadoNuevoId: estadoNuevo
        });
      }
    } catch (error) {
      console.error('Error al crear histórico de estado:', error);
    }
  };

  // Función para obtener color del estado
  const getEstadoColor = (estadoId) => {
    switch (estadoId) {
      case 1: return '#6c757d'; // Pendiente - Gris
      case 5: return '#ffc107'; // Inicio Toma Muestra - Amarillo  
      case 2: return '#28a745'; // Toma Realizada - Verde
      case 8: return '#17a2b8'; // Traslado - Azul
      case 3: return '#007bff'; // Verificada - Azul más oscuro
      case 9: return '#dc3545'; // Muestra No conforme - Rojo
      case 4: return '#20c997'; // Procesada - Teal
      case 6: return '#6610f2'; // Anulada - Morado
      case 7: return '#fd7e14'; // Paciente Ausente - Naranja
      case 11: return '#17a2b8'; // Recepción ventanilla - Azul claro
      default: return '#6c757d';
    }
  };

  // Función para obtener color del botón del estado
  const getEstadoButtonColor = (estadoId) => {
    switch (estadoId) {
      case 1: return 'inherit';   // Pendiente
      case 5: return 'warning';   // Inicio Toma Muestra
      case 2: return 'success';   // Toma Realizada
      case 8: return 'info';      // Traslado
      case 3: return 'primary';   // Verificada
      case 9: return 'error';     // Muestra No conforme
      case 4: return 'info';      // Procesada
      case 6: return 'secondary'; // Anulada
      case 7: return 'warning';   // Paciente Ausente
      case 11: return 'info';     // Recepción ventanilla
      default: return 'inherit';
    }
  };

  // Función para filtrar comentarios automáticos - MEJORADA
  const filtrarComentarioAutomatico = (comentario) => {
    if (!comentario || comentario === 'N/A' || comentario === '-' || comentario.trim() === '') return '';
    
    const patronesAutomaticos = [
      /cambio de estado de/i,
      /iniciando proceso/i,
      /estado actualizado automáticamente/i,
      /estado inicial creado automáticamente/i,
      /trasladado por:/i,
      /verificado por:/i,
      /observaciones adicionales si aplica/i,
      /cambio de estado automático/i,
      /estado cambiado por el sistema/i,
      /^\s*$/,  // Solo espacios en blanco
      /^N\/A$/i,
      /^-$/,
      /^sin comentarios$/i,
      /^cambio automático/i
    ];
    
    const esAutomatico = patronesAutomaticos.some(patron => patron.test(comentario));
    return esAutomatico ? '' : comentario.trim();
  };

  // Función para verificar si una fila del histórico tiene comentario relevante
  const tieneComentarioRelevante = (item) => {
    const comentarioFiltrado = filtrarComentarioAutomatico(item.comentario);
    return comentarioFiltrado && comentarioFiltrado.length > 0;
  };

  const fetchCondicionesOrden = async (idOrdenMedica, muestrasParaUsar = null) => {
    console.log('🔍 [API GET] fetchCondicionesOrden - INICIO', { idOrden: idOrdenMedica });
    const t0 = performance.now();
    try {
      console.log('📊 Obteniendo condiciones especiales agrupadas para orden:', idOrdenMedica);

      // =====================================================================
      // PASO 1: Obtener condiciones agrupadas desde el nuevo endpoint
      // =====================================================================
      const response = await fetch(
        `${server}/CdmCondicionesEspecialesXMuestraXAnalisis/pororden/${idOrdenMedica}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': key,
          },
        }
      );
      const t1 = performance.now();
      console.log('⏱️ [API GET] fetchCondicionesOrden - Network Time:', `${(t1 - t0).toFixed(0)}ms`);

      if (!response.ok) {
        throw new Error(`Error al obtener condiciones: ${response.status}`);
      }

      const data = await response.json();
      const condicionesBackend = data.$values || data || [];
      
      console.log('✅ Condiciones recibidas del backend:', condicionesBackend.length);

      if (condicionesBackend.length === 0) {
        console.log('ℹ️ No hay condiciones especiales para esta orden');
        setCondicionesOrden([]);
        setCondiciones([]);
        return;
      }

      // =====================================================================
      // PASO 2: Transformar formato backend → frontend
      // =====================================================================
      const condicionesTransformadas = condicionesBackend.map((condBackend, index) => {
        // Extraer arrays de IDs
        const idsMuestraXAnalisis = condBackend.idMuestraXAnalisis?.$values || [];
        const nombresAnalisis = condBackend.nombreTipoAnalisis?.$values || [];

        return {
          // Identificadores únicos
          idCondicion: condBackend.idCondicion,
          keyUnico: `condicion-${condBackend.idCondicion}-${index}`,
          
          // Descripción de la condición
          descripcionCondicion: condBackend.descripcionCondicion,
          
          // Arrays de IDs de muestras (para guardar en BD)
          idsMuestraXAnalisis: idsMuestraXAnalisis,
          
          // Nombres de los análisis (para mostrar)
          analisis: nombresAnalisis,
          ComparteCon: nombresAnalisis.join(', '),
          
          // Estado inicial (sin guardar)
          cumple: condBackend.cumple || false,
          observacionesCondicion: condBackend.observaciones || '',
          esGuardada: false,
          idRegistroGuardado: null
        };
      });

      console.log('🔄 Condiciones transformadas:', condicionesTransformadas.length);

      // =====================================================================
      // PASO 3: Obtener estados guardados previamente (si existen)
      // =====================================================================
      try {
        const t2 = performance.now();
        const responseGuardadas = await fetch(
          `${server}/mg_condiciones_x_ordens/pororden/${idOrdenMedica}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': key,
            },
          }
        );
        const t3 = performance.now();
        console.log('⏱️ [API GET] fetchCondicionesOrden - Guardadas Network Time:', `${(t3 - t2).toFixed(0)}ms`);

        if (responseGuardadas.ok) {
          const guardadasData = await responseGuardadas.json();
          const guardadas = guardadasData.$values || guardadasData || [];

          console.log('🔍 Estados guardados encontrados:', guardadas.length);

          // Agrupar estados guardados por id_condicion
          const estadosPorCondicion = new Map();
          guardadas.forEach(g => {
            const idCond = g.id_condicion || g.IdCondicion;
            if (!estadosPorCondicion.has(idCond)) {
              estadosPorCondicion.set(idCond, {
                cumple: g.cumple,
                observaciones: g.observaciones || '',
                ids: []
              });
            }
            estadosPorCondicion.get(idCond).ids.push(g.id || g.Id);
          });

          // Aplicar estados guardados a las condiciones transformadas
          condicionesTransformadas.forEach(condicion => {
            const estadoGuardado = estadosPorCondicion.get(condicion.idCondicion);
            if (estadoGuardado) {
              condicion.cumple = estadoGuardado.cumple || false;
              condicion.observacionesCondicion = estadoGuardado.observaciones;
              condicion.esGuardada = true;
              condicion.idsRegistrosGuardados = estadoGuardado.ids;
              
              console.log(`✅ Estado aplicado a condición ${condicion.idCondicion}:`, estadoGuardado.cumple);
            }
          });
        }
      } catch (error) {
        console.log('ℹ️ No hay estados guardados previamente (primera vez)');
      }

      // =====================================================================
      // PASO 4: Establecer estado en React
      // =====================================================================
      setCondicionesOrden(condicionesTransformadas);
      setCondiciones(condicionesTransformadas);

      // =====================================================================
      // PASO 5: Inicializar checkboxes (marcar las que cumplan)
      // =====================================================================
      const idsParaMarcar = condicionesTransformadas
        .filter(c => c.cumple === true)
        .map(c => c.idCondicion);
      
      console.log('✅ Condiciones a marcar como cumplidas:', idsParaMarcar.length);
      setCondicionesSeleccionadasMasivas(idsParaMarcar);

      // =====================================================================
      // PASO 6: Inicializar observaciones
      // =====================================================================
      const observacionesIniciales = new Map();
      condicionesTransformadas.forEach(c => {
        if (c.observacionesCondicion) {
          observacionesIniciales.set(c.idCondicion, c.observacionesCondicion);
        }
      });
      setObservacionesCondiciones(observacionesIniciales);

      console.log('✅ Condiciones especiales cargadas exitosamente');
      
      // Si todas las condiciones están guardadas, mantener el estado de guardado
      const todasGuardadas = condicionesTransformadas.length > 0 && 
                            condicionesTransformadas.every(c => c.esGuardada === true);
      if (todasGuardadas) {
        console.log('ℹ️ Todas las condiciones ya están guardadas, manteniendo condicionesGuardadas = true');
        setCondicionesGuardadas(true);
      }
      
      const tFinal = performance.now();
      console.log('⏱️ [API GET] fetchCondicionesOrden - TIEMPO TOTAL:', `${(tFinal - t0).toFixed(0)}ms`);

    } catch (error) {
      console.error('❌ Error al obtener condiciones especiales:', error);
      console.error('Stack:', error.stack);
      setCondicionesOrden([]);
      setCondiciones([]);
    }
  };


  const handleCondicionChange = (idCondicionXMuestraXAnalisis, checked) => {
    console.log('LOG: Cambiando estado de condición individual:', idCondicionXMuestraXAnalisis, 'a:', checked);
    
    setCondiciones((prev) =>
      prev.map((c) =>
        c.idCondicionXMuestraXAnalisis === idCondicionXMuestraXAnalisis
          ? { 
              ...c, 
              cumple: checked, 
              // Limpiar observaciones si se marca como cumplida
              observacionesCondicion: checked ? '' : c.observacionesCondicion 
            }
          : c
      )
    );
  };




  const [estadosDisponibles, setEstadosDisponibles] = useState([]);
  
  // Estados para modal de detalle de muestras individuales
  const [modalDetalleOpen, setModalDetalleOpen] = useState(false);
  const [muestrasDetalle, setMuestrasDetalle] = useState([]);
  const [grupoSeleccionado, setGrupoSeleccionado] = useState(null);
  const [muestrasDetalleSeleccionadas, setMuestrasDetalleSeleccionadas] = useState([]);
  
  // Estados para modal de histórico
  const [modalHistoricoOpen, setModalHistoricoOpen] = useState(false);
  const [historicoMuestra, setHistoricoMuestra] = useState([]);
  const [muestraHistoricoSeleccionada, setMuestraHistoricoSeleccionada] = useState(null);
  
  const fetchEstadosDisponibles = async () => {
    console.log('🔍 [API GET] fetchEstadosDisponibles - INICIO');
    const t0 = performance.now();
    try {
      const response = await fetch(`${server}/mg_estados_toma_muestras`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        },
      });
      const t1 = performance.now();
      console.log('⏱️ [API GET] fetchEstadosDisponibles - Network Time:', `${(t1 - t0).toFixed(0)}ms`);

      if (response.ok) {
        const estadosData = await response.json();
        console.log('Estados obtenidos:', estadosData);
        
        // Extraer el array de estados desde $values
        const estadosArray = estadosData.$values || estadosData;
        console.log('Array de estados:', estadosArray);
        
        if (Array.isArray(estadosArray)) {
          // Ordenar por campo 'orden'
          const estadosOrdenados = estadosArray.sort((a, b) => (a.orden || 0) - (b.orden || 0));
          console.log('Estados ordenados:', estadosOrdenados);
          setEstadosDisponibles(estadosOrdenados);
        } else {
          console.error('Los estados no son un array:', estadosArray);
        }
      } else {
        console.error('Error al obtener estados:', response.statusText);
      }
    } catch (error) {
      console.error('Error al obtener los estados:', error);
    }
  };

  // Función para obtener condiciones de muestra filtradas
  const fetchCondicionesMuestra = async () => {
    console.log('🔍 [API GET] fetchCondicionesMuestra - INICIO');
    const t0 = performance.now();
    setCargandoCondicionesMuestra(true);
    try {
      const response = await fetch(`${server}/CdmCondicionesEspeciales/demuestras`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json'
        }
      });
      const t1 = performance.now();
      console.log('⏱️ [API GET] fetchCondicionesMuestra - Network Time:', `${(t1 - t0).toFixed(0)}ms`);

      if (response.ok) {
        const data = await response.json();
        console.log('Condiciones de muestra obtenidas:', data);
        
        // Manejar la estructura de respuesta {$id: '1', $values: Array}
        let condicionesArray = [];
        
        if (data && data.$values && Array.isArray(data.$values)) {
          condicionesArray = data.$values;
        } else if (Array.isArray(data)) {
          condicionesArray = data;
        } else {
          console.warn('Respuesta de condiciones en formato inesperado:', data);
          setCondicionesMuestra([]);
          return;
        }
        
        // Procesar cada condición para asegurar que tenga los campos correctos
        const condicionesLimpias = condicionesArray.map(condicion => ({
          id: condicion.IdCondicion || condicion.idCondicion || condicion.id,
          descripcion: condicion.DescripcionCondicion || condicion.descripcionCondicion || condicion.descripcion || 'Sin descripción'
        }));
        
        console.log('Condiciones procesadas para combo:', condicionesLimpias);
        setCondicionesMuestra(condicionesLimpias);
      } else {
        console.error('Error al obtener condiciones de muestra:', response.statusText);
        setCondicionesMuestra([]);
      }
    } catch (error) {
      console.error('Error al obtener condiciones de muestra:', error);
      setCondicionesMuestra([]);
    } finally {
      setCargandoCondicionesMuestra(false);
    }
  };

  // Función para obtener motivos de muestra repetida
  const fetchMotivosRepetida = async () => {
    setCargandoMotivosRepetida(true);
    try {
      const response = await fetch(`${server}/CdmCondicionesEspeciales/derepetida`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        let condicionesArray = [];
        if (data && data.$values && Array.isArray(data.$values)) {
          condicionesArray = data.$values;
        } else if (Array.isArray(data)) {
          condicionesArray = data;
        }
        const condicionesLimpias = condicionesArray.map(condicion => ({
          id: condicion.IdCondicion || condicion.idCondicion || condicion.id,
          descripcion: condicion.DescripcionCondicion || condicion.descripcionCondicion || condicion.descripcion || 'Sin descripción'
        }));
        setMotivosRepetida(condicionesLimpias);
      } else {
        setMotivosRepetida([]);
      }
    } catch (error) {
      console.error('Error al obtener motivos de muestra repetida:', error);
      setMotivosRepetida([]);
    } finally {
      setCargandoMotivosRepetida(false);
    }
  };

  // Función para cargar usuarios disponibles
  const fetchUsuariosDisponibles = async () => {
    console.log('🔍 [API GET] fetchUsuariosDisponibles - INICIO');
    const t0 = performance.now();
    setCargandoUsuarios(true);
    try {
      const response = await fetch(`${server}/MgUsuarios`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        }
      });
      const t1 = performance.now();
      console.log('⏱️ [API GET] fetchUsuariosDisponibles - Network Time:', `${(t1 - t0).toFixed(0)}ms`);

      if (!response.ok) {
        throw new Error('Error al cargar usuarios');
      }

      const data = await response.json();
      console.log('👥 Usuarios cargados:', data);
      setUsuariosDisponibles(data.$values || []);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      setUsuariosDisponibles([]);
    } finally {
      setCargandoUsuarios(false);
    }
  };

  // Función para cargar condiciones de muestra guardadas
  const fetchCondicionesMuestraGuardadas = async (idOrden) => {
    console.log('🔍 [API GET] fetchCondicionesMuestraGuardadas - INICIO', { idOrden });
    const t0 = performance.now();
    try {
      const response = await fetch(`${server}/mg_condiciones_x_ordens/pororden/${idOrden}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json'
        }
      });
      const t1 = performance.now();
      console.log('⏱️ [API GET] fetchCondicionesMuestraGuardadas - Network Time:', `${(t1 - t0).toFixed(0)}ms`);

      if (response.ok) {
        const data = await response.json();
        console.log('Condiciones de muestra guardadas obtenidas:', data);
        
        // Manejar la estructura de respuesta correctamente
        let condicionesArray = [];
        if (Array.isArray(data)) {
          condicionesArray = data;
        } else if (data && Array.isArray(data.$values)) {
          condicionesArray = data.$values;
        }
        
        // Filtrar solo las condiciones con id_muestra_x_analisis null (condiciones de muestra)
        const condicionesMuestraFiltradas = condicionesArray.filter(c => c.id_muestra_x_analisis === null);
        
        // Crear un mapa para fácil acceso
        const condicionesMap = new Map();
        condicionesMuestraFiltradas.forEach(condicion => {
          condicionesMap.set(condicion.id_condicion, condicion);
        });
        
        setCondicionesMuestraGuardadas(condicionesMap);
        console.log('Condiciones de muestra guardadas procesadas:', condicionesMap);
      } else {
        console.error('Error al obtener condiciones guardadas:', response.statusText);
      }
    } catch (error) {
      console.error('Error al obtener condiciones de muestra guardadas:', error);
    }
  };

  // Función para obtener comentarios del histórico de estados de muestra
  const fetchComentarioHistoricoMuestra = async (muestraInfo) => {
    console.log('🔍 [API GET] fetchComentarioHistoricoMuestra - INICIO');
    const t0 = performance.now();
    try {
      console.log('🔍 📋 INICIANDO fetchComentarioHistoricoMuestra con:', muestraInfo);
      
      // Buscar el registro de muestra orden para esta muestra específica
      const muestraEncontrada = muestras.find(m => 
        m.descripcionMuestra === muestraInfo.descripcionMuestra &&
        m.modulo === muestraInfo.modulo
      );
      
      console.log('🔍 📋 Muestra encontrada:', muestraEncontrada);
      
      if (!muestraEncontrada || !muestraEncontrada.examenes || muestraEncontrada.examenes.length === 0) {
        console.log('❌ No se encontró muestra o exámenes para obtener histórico');
        return null;
      }
      
      // Usar el primer examen para obtener el histórico (todos los exámenes del grupo deberían tener el mismo histórico)
      const primerExamen = muestraEncontrada.examenes[0];
      
      console.log('🔍 📋 Primer examen para histórico:', primerExamen);
      console.log('🔍 📋 Campos disponibles:', Object.keys(primerExamen));
      
      // Intentar diferentes nombres de campo para id_muestra_x_orden
      const idMuestraXOrden = primerExamen.id_muestra_x_orden || 
                             primerExamen.idMuestraXOrden ||
                             primerExamen.IdMuestraXOrden ||
                             primerExamen.idMuestraOrden ||
                             primerExamen.id;
      
      console.log('🔍 📋 ID Muestra X Orden encontrado:', idMuestraXOrden);
      
      if (!idMuestraXOrden) {
        console.log('❌ No se encontró id_muestra_x_orden para obtener histórico');
        console.log('❌ Campos disponibles en primerExamen:', Object.keys(primerExamen));
        return null;
      }
      
      // Obtener histórico de estados para esta muestra orden usando el endpoint correcto
      const url = `${server}/mg_historico_estados_muestra/pormuestraxorden/${idMuestraXOrden}`;
      console.log('🔍 📋 Llamando a URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json'
        }
      });
      const t1 = performance.now();
      console.log('⏱️ [API GET] fetchComentarioHistoricoMuestra - Network Time:', `${(t1 - t0).toFixed(0)}ms`);
      
      console.log('🔍 📋 Response status:', response.status);
      console.log('🔍 📋 Response ok:', response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 📋 Histórico COMPLETO obtenido:', data);
        
        // La API devuelve { values: { $values: historico } } según la respuesta del log
        let historico = data;
        
        // Intentar extraer el array de diferentes formas según la estructura
        if (data.values && data.values.$values) {
          historico = data.values.$values;
          console.log('🔍 📋 Usando data.values.$values');
        } else if (data.Values && data.Values.$values) {
          historico = data.Values.$values;
          console.log('🔍 📋 Usando data.Values.$values');
        } else if (data.$values) {
          historico = data.$values;
          console.log('🔍 📋 Usando data.$values');
        } else if (data.values) {
          historico = data.values;
          console.log('🔍 📋 Usando data.values');
        } else if (data.Values) {
          historico = data.Values;
          console.log('🔍 📋 Usando data.Values');
        }
        
        console.log('🔍 📋 Histórico extraído final:', historico);
        console.log('🔍 📋 Es array:', Array.isArray(historico));
        console.log('🔍 📋 Longitud:', historico?.length);
        
        // Primero buscar el último comentario del estado "Muestra No conforme" (ID 9)
        const registrosMuestraNoConforme = Array.isArray(historico) ? 
          historico.filter(h => h.id_estado_nuevo === 9) :
          [];
        
        console.log('🔍 📋 Registros de Muestra No Conforme encontrados:', registrosMuestraNoConforme.length);
        console.log('🔍 📋 Registros:', registrosMuestraNoConforme);
        
        if (registrosMuestraNoConforme.length > 0) {
          // Obtener el más reciente
          const ultimoRegistro = registrosMuestraNoConforme.sort((a, b) => 
            new Date(b.fecha_cambio_estado) - new Date(a.fecha_cambio_estado)
          )[0];
          
          console.log('🔍 📋 ✅ Último registro encontrado:', ultimoRegistro);
          console.log('🔍 📋 ✅ Comentario:', ultimoRegistro.comentario);
          console.log('🔍 📋 ✅ Longitud comentario:', ultimoRegistro.comentario?.length);
          
          return {
            comentario: ultimoRegistro.comentario || '',
            idHistorico: ultimoRegistro.id,
            idMuestraOrden: idMuestraXOrden
          };
        } else {
          console.log('⚠️ No se encontraron registros de Muestra No Conforme, usando último registro disponible');
          
          // Si no hay registros de muestra no conforme, usar el último registro disponible
          if (Array.isArray(historico) && historico.length > 0) {
            const ultimoRegistro = historico.sort((a, b) => 
              new Date(b.fecha_cambio_estado) - new Date(a.fecha_cambio_estado)
            )[0];
            
            console.log('🔍 📋 ✅ Último registro general encontrado:', ultimoRegistro);
            
            return {
              comentario: ultimoRegistro.comentario || '',
              idHistorico: ultimoRegistro.id,
              idMuestraOrden: idMuestraXOrden
            };
          }
          
          console.log('❌ No se encontraron registros en el histórico');
        }
      } else {
        console.error('❌ Error al obtener histórico:', response.status, response.statusText);
      }
      
      return null;
    } catch (error) {
      console.error('❌ Error al obtener comentario del histórico:', error);
      return null;
    }
  };

  // Función para obtener la condición no conforme específica por orden usando la nueva API
  const fetchCondicionNoConforme = async (idOrden) => {
    console.log('🔍 [API GET] fetchCondicionNoConforme - INICIO', { idOrden });
    const t0 = performance.now();
    try {
      const response = await fetch(`${server}/mg_condiciones_x_ordens/noconformepororden/${idOrden}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json'
        }
      });
      const t1 = performance.now();
      console.log('⏱️ [API GET] fetchCondicionNoConforme - Network Time:', `${(t1 - t0).toFixed(0)}ms`);

      if (response.ok) {
        const condicion = await response.json();
        console.log('Condición no conforme obtenida:', condicion);
        return condicion;
      } else if (response.status === 404) {
        console.log('No hay condición no conforme guardada para esta orden');
        return null;
      } else {
        console.error('Error al obtener condición no conforme:', response.statusText);
        return null;
      }
    } catch (error) {
      console.error('Error al obtener condición no conforme:', error);
      return null;
    }
  };

  // Función para cambiar el estado de una muestra individual
  const handleCambiarEstadoIndividual = async (examen, nuevoEstadoId) => {
    try {
      // Verificar si esta muestra ya está siendo procesada
      const muestraKey = `${examen.id}-${examen.idMuestraXAnalisis}`;
      if (muestrasIndividualesCargando.has(muestraKey)) {
        console.log('Esta muestra ya está siendo procesada, ignorando duplicado');
        return;
      }

      // Marcar la muestra como en proceso
      setMuestrasIndividualesCargando(prev => new Set([...prev, muestraKey]));

      // Obtener el estado actual real de la muestra
      const estadoActualId = examen.idEstado || 1;
      const estadoNuevo = estadosDisponibles.find(e => e.id === nuevoEstadoId);
      const estadoAnterior = estadosDisponibles.find(e => e.id === estadoActualId);
      
      if (!estadoNuevo || !estadoAnterior) {
        console.error('Estado no encontrado:', { nuevoEstadoId, estadoActualId, estadosDisponibles });
        throw new Error('Estado no válido');
      }
      
      // Detectar si es un cambio doble (TRASLADAR/VERIFICAR) - ANTES de validar secuencia
      const estadoTomaRealizadaOrden = estadosDisponibles.find(e => e.estado === 'Toma Realizada')?.orden;
      const estadoVerificadaOrden = estadosDisponibles.find(e => e.estado === 'Verificada')?.orden;
      const esCambioDoble = estadoAnterior.orden === estadoTomaRealizadaOrden && estadoNuevo.orden === estadoVerificadaOrden;
      
      // Validar que el cambio de estado respete la secuencia ordenada
      const ordenAnterior = estadoAnterior.orden;
      const ordenNuevo = estadoNuevo.orden;
      
      // Permitir cambios secuenciales o a estados especiales
      const esSecuencial = ordenNuevo === ordenAnterior + 1;
      const esEstadoEspecial = ['Anulada', 'Paciente Ausente', 'Muestra No Conforme', 'Muestra No conforme', 'Recepción ventanilla', 'Muestra Repetida', 'Análisis Reprocesado', 'Analisis Reprocesado'].includes(estadoNuevo.estado);
      const esReactivacion = (
        estadoAnterior.estado === 'Anulada' || 
        estadoAnterior.estado === 'Muestra No Conforme' || 
        estadoAnterior.estado === 'Muestra No conforme'
      ) && estadoNuevo.estado === 'Pendiente';
      const esDesdePacienteAusente = estadoAnterior.estado === 'Paciente Ausente';
      const esDesdeRecepcionVentanilla = estadoAnterior.estado === 'Recepción ventanilla';
      const esDesdeMuestraRepetida = estadoAnterior.estado === 'Muestra Repetida' && estadoNuevo.estado === 'Pendiente';
      const esAutoTransicionReprocesado = (estadoAnterior.estado === 'Análisis Reprocesado' || estadoAnterior.estado === 'Analisis Reprocesado') && 
                                           (estadoNuevo.estado === 'Análisis Reprocesado' || estadoNuevo.estado === 'Analisis Reprocesado');
      
      // En modal de detalle, permitir retroceso al estado anterior o a Pendiente
      const esRetrocesoEnModalDetalle = modalDetalleOpen && (
        ordenNuevo === ordenAnterior - 1 || // Retroceso al estado anterior
        estadoNuevo.estado === 'Pendiente'  // Retroceso a Pendiente desde cualquier estado
      );
      
      // Validación específica: desde "Muestra No conforme" solo se puede ir a "Pendiente"
      const desdeMuestraNoConforme = estadoAnterior.estado === 'Muestra No Conforme' || estadoAnterior.estado === 'Muestra No conforme';
      if (desdeMuestraNoConforme && estadoNuevo.estado !== 'Pendiente') {
        throw new Error(`Desde "Muestra No conforme" solo se puede reactivar a "Pendiente". No se puede cambiar directamente a "${estadoNuevo.estado}".`);
      }
      
      // Permitir cambio doble como caso especial
      if (!esSecuencial && !esEstadoEspecial && !esReactivacion && !esDesdePacienteAusente && !esDesdeRecepcionVentanilla && !esRetrocesoEnModalDetalle && !esCambioDoble && !esDesdeMuestraRepetida && !esAutoTransicionReprocesado) {
        throw new Error(`No se puede cambiar de "${estadoAnterior.estado}" (orden ${ordenAnterior}) a "${estadoNuevo.estado}" (orden ${ordenNuevo}). Debe seguir la secuencia ordenada.`);
      }
      
      console.log('Cambiando estado individual:', {
        estadoAnterior: estadoAnterior.estado,
        estadoNuevo: estadoNuevo.estado,
        ordenAnterior,
        ordenNuevo,
        examen: examen.analisis,
        esCambioDoble
      });
      
      // Siempre permitir comentario personalizable cuando el destino sea "Muestra No Conforme"
      const esMuestraNoConforme = estadoNuevo.estado === 'Muestra No Conforme' || estadoNuevo.estado === 'Muestra No conforme';
      
      console.log('🔍 Debug modal comentario:', {
        estadoAnterior: estadoAnterior.estado,
        estadoNuevo: estadoNuevo.estado,
        esMuestraNoConforme,
        esCambioDoble,
        siemprePermiteComentario: esMuestraNoConforme || esCambioDoble
      });

      if (esCambioDoble) {
        // Abrir modal para capturar información del traslado
        console.log('🔄 Abriendo modal para cambio doble (TRASLADAR/VERIFICAR)');
        setPendingCambioEstado({ 
          examen, 
          nuevoEstadoId, 
          estadoNuevo, 
          estadoAnterior,
          isIndividual: true,
          esCambioDoble: true,
          estadoIntermedio: estadosDisponibles.find(e => e.estado === 'Traslado')?.id
        });
        setMuestraParaComentario(examen);
        setComentarioText('');
        setUsuarioTraslado('');
        setUsuarioVerifica('');
        setFechaTraslado('');
        
        // Cargar usuarios disponibles
        await fetchUsuariosDisponibles();
        
        setOpenComentarioModal(true);
      } else if (esMuestraNoConforme) {
        // Para cualquier estado → Muestra No Conforme: siempre permitir comentario personalizado
        setPendingCambioEstado({
          examen,
          nuevoEstadoId,
          estadoNuevo,
          estadoAnterior,
          isIndividual: true
        });
        
        // Campo vacío para escritura libre sin texto predeterminado
        setComentarioText('');
        setMuestraParaComentario({ 
          descripcionMuestra: examen.analisis,
          analisis: examen.analisis
        });
        
        // Cargar condiciones de muestra para el combo box (igual que en masivas)
        await fetchCondicionesMuestra();
        
        setOpenComentarioModal(true);
      } else if (estadoNuevo.estado === 'Muestra Repetida') {
        // Para Muestra Repetida: requiere motivo y responsable
        setPendingCambioEstado({
          examen,
          nuevoEstadoId,
          estadoNuevo,
          estadoAnterior,
          isIndividual: true,
          esMuestraRepetida: true
        });
        
        setComentarioText('');
        setMotivoRepetidaSeleccionado('');
        setResponsableRepetida('');
        setMuestraParaComentario({ 
          descripcionMuestra: examen.analisis,
          analisis: examen.analisis
        });
        
        // Cargar motivos de muestra repetida y usuarios disponibles
        await Promise.all([fetchMotivosRepetida(), fetchUsuariosDisponibles()]);
        
        setOpenComentarioModal(true);
      } else {
        // Para todos los demás casos: ejecutar directamente sin confirmación ni comentarios personalizables
        await procesarCambioEstadoIndividualSinConfirmacion(examen, nuevoEstadoId, estadoNuevo, estadoAnterior);
        
        // Si estamos en el modal de detalle, actualizarlo después del cambio
        if (modalDetalleOpen && grupoSeleccionado) {
          setTimeout(() => {
            actualizarModalDetalleConNuevosDatos();
          }, 500);
        }
      }

    } catch (error) {
      console.error('Error al preparar cambio de estado individual:', error);
      Swal.fire('Error', error.message, 'error');
    } finally {
      // Siempre limpiar el loading state, incluso si hay error
      const muestraKey = `${examen.id}-${examen.idMuestraXAnalisis}`;
      setMuestrasIndividualesCargando(prev => {
        const newSet = new Set(prev);
        newSet.delete(muestraKey);
        return newSet;
      });
    }
  };

  // Función para obtener la fecha del último cambio al estado actual desde el histórico
  const obtenerFechaEstadoActual = async (idMuestraXAnalisis, idEstadoActual) => {
    console.log('🔍 [API GET] obtenerFechaEstadoActual - INICIO', { idMuestraXAnalisis });
    const t0 = performance.now();
    try {
      const response = await fetch(`${server}/mg_muestras_x_ordens/historico/${idMuestraXAnalisis}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        },
      });
      const t1 = performance.now();
      console.log('⏱️ [API GET] obtenerFechaEstadoActual - Network Time:', `${(t1 - t0).toFixed(0)}ms`);

      if (response.ok) {
        const historico = await response.json();
        const historicoArray = historico.$values || historico || [];
        
        // Buscar el registro más reciente del estado actual
        const registroEstadoActual = historicoArray
          .filter(h => h.id_estado_nuevo === idEstadoActual)
          .sort((a, b) => new Date(b.fecha_cambio_estado) - new Date(a.fecha_cambio_estado))[0];
        
        return registroEstadoActual?.fecha_cambio_estado || null;
      }
      return null;
    } catch (error) {
      console.error('Error obteniendo fecha del estado:', error);
      return null;
    }
  };

  // Función para calcular tiempo transcurrido y validar si excede el límite permitido
  const calcularTiempoTranscurrido = (fechaInicio, fechaFin) => {
    if (!fechaInicio || !fechaFin) return null;
    
    const fechaInicioDate = new Date(fechaInicio);
    const fechaFinDate = new Date(fechaFin);
    
    // Calcular diferencia en minutos
    const diferenciaMs = fechaFinDate - fechaInicioDate;
    const diferenciaMinutos = Math.floor(diferenciaMs / (1000 * 60));
    const diferenciaHoras = Math.floor(diferenciaMinutos / 60);
    const minutosRestantes = diferenciaMinutos % 60;
    
    return {
      minutos: diferenciaMinutos,
      horas: diferenciaHoras,
      minutosRestantes: minutosRestantes,
      excedeLimite: diferenciaMinutos > 120, // Más de 2 horas
      textoFormateado: diferenciaHoras > 0 
        ? `${diferenciaHoras}h ${minutosRestantes}m` 
        : `${diferenciaMinutos}m`,
      fechaInicio: fechaInicioDate.toLocaleString('es-ES'),
      fechaFin: fechaFinDate.toLocaleString('es-ES')
    };
  };

  // Función para procesar cambio doble de estado (TRASLADAR/VERIFICAR)
  const procesarCambioEstadoDoble = async (comentarioVerificada, usuarioTraslado, usuarioVerifica, fechaTraslado) => {
    try {
      const { examen, estadoAnterior, esMasiva, muestrasAfectadas, muestrasIndices } = pendingCambioEstado;
      const usuarioTrasladoFormateado = formatUsuario(usuarioTraslado);
      const usuarioVerificaFormateado = formatUsuario(usuarioVerifica);
      const idSucursalFinal = sucursalSeleccionada?.idSucursal || null;
      
      console.log('🔄 [CAMBIO DOBLE] Iniciando proceso TRASLADAR/VERIFICAR', {
        esMasiva,
        cantidadMuestras: esMasiva ? muestrasAfectadas?.length : 1,
        examen: esMasiva ? 'Múltiples' : examen?.analisis,
        estadoAnterior: estadoAnterior.estado,
        usuarioTraslado,
        usuarioVerifica,
        fechaTraslado
      });

      // Obtener IDs de estados por nombre
      const estadoTomaRealizada = estadosDisponibles.find(e => e.estado === 'Toma Realizada');
      const estadoTraslado = estadosDisponibles.find(e => e.estado === 'Traslado');
      const estadoVerificada = estadosDisponibles.find(e => e.estado === 'Verificada');

      if (esMasiva) {
        // Procesar cambio doble masivo
        let exitosas = 0;
        let fallidas = 0;
        const errores = [];
        const muestrasPasadasTiempo = [];

        for (let i = 0; i < muestrasAfectadas.length; i++) {
          const muestra = muestrasAfectadas[i];
          
          try {
            // Determinar si es un examen individual o un grupo
            const examenes = Array.isArray(muestra.examenes) ? muestra.examenes : [muestra];
            
            // Validar tiempo transcurrido para cada muestra
            for (const examenItem of examenes) {
              // Obtener la fecha del estado actual desde el histórico
              const fechaEstadoActual = await obtenerFechaEstadoActual(
                examenItem.idMuestraXAnalisis, 
                estadoTomaRealizada.id
              );
              
              if (fechaEstadoActual) {
                const tiempoInfo = calcularTiempoTranscurrido(fechaEstadoActual, fechaTraslado);
                if (tiempoInfo) {
                  console.log(`⏱️ Muestra ${examenItem.analisis}:`, {
                    fechaTomaRealizada: tiempoInfo.fechaInicio,
                    fechaVerificacion: tiempoInfo.fechaFin,
                    tiempoTranscurrido: tiempoInfo.textoFormateado,
                    excedeLimite: tiempoInfo.excedeLimite
                  });
                  
                  if (tiempoInfo.excedeLimite) {
                    muestrasPasadasTiempo.push({
                      muestra: examenItem.analisis || muestra.descripcionMuestra,
                      tiempo: tiempoInfo.textoFormateado,
                      fechaEstadoAnterior: tiempoInfo.fechaInicio,
                      fechaVerificacion: tiempoInfo.fechaFin
                    });
                  }
                }
              }
            }
            
            // Procesar cada análisis individual
            for (const examenItem of examenes) {
              // PASO 1: Toma Realizada → Traslado
              const requestTraslado = {
                IdOrden: ordenSeleccionada.idOrdenMedica,
                IdMuestraXAnalisis: examenItem.idMuestraXAnalisis,
                EstadoAnterior: estadoTomaRealizada.id,
                NuevoEstado: estadoTraslado.id,
                UsuarioId: usuarioTrasladoFormateado,
                Comentario: `Trasladado por: ${usuarioTraslado}`,
                IdSucursal: idSucursalFinal,
                Cubiculo: cubiculoSeleccionado || null,
                FechaCambioEstado: fechaTraslado
              };

              const tTraslado = performance.now();
              const responseTraslado = await fetch(`${server}/mg_muestras_x_ordens/cambiarestado`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'x-api-key': key,
                },
                body: JSON.stringify(requestTraslado)
              });
              const tTrasladoFin = performance.now();
              console.log('⏱️ [API POST] cambioEstadoTraslado - Network Time:', `${(tTrasladoFin - tTraslado).toFixed(0)}ms`);

              if (!responseTraslado.ok) {
                const errorText = await responseTraslado.text();
                throw new Error(`Error al registrar traslado: ${errorText}`);
              }

              // PASO 2: Traslado → Verificada
              const requestVerificada = {
                IdOrden: ordenSeleccionada.idOrdenMedica,
                IdMuestraXAnalisis: examenItem.idMuestraXAnalisis,
                EstadoAnterior: estadoTraslado.id,
                NuevoEstado: estadoVerificada.id,
                UsuarioId: usuarioVerificaFormateado,
                Comentario: `Verificado por: ${usuarioVerifica}`,
                IdSucursal: idSucursalFinal,
                Cubiculo: cubiculoSeleccionado || null
              };

              const tVerificada = performance.now();
              const responseVerificada = await fetch(`${server}/mg_muestras_x_ordens/cambiarestado`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'x-api-key': key,
                },
                body: JSON.stringify(requestVerificada)
              });
              const tVerificadaFin = performance.now();
              console.log('⏱️ [API POST] cambioEstadoVerificada - Network Time:', `${(tVerificadaFin - tVerificada).toFixed(0)}ms`);

              if (!responseVerificada.ok) {
                const errorText = await responseVerificada.text();
                throw new Error(`Error al registrar verificada: ${errorText}`);
              }
            }
            
            exitosas++;
          } catch (error) {
            fallidas++;
            errores.push(`${muestra.descripcionMuestra}: ${error.message}`);
            console.error(`Error en muestra ${muestra.descripcionMuestra}:`, error);
          }
        }

        // Mostrar resultado
        if (exitosas > 0) {
          let alertaHTML = `
            <div style="text-align: left; padding: 10px;">
              <p><strong>✅ Exitosas: ${exitosas}</strong></p>
              ${fallidas > 0 ? `<p><strong>❌ Fallidas: ${fallidas}</strong></p>` : ''}
              <p>📋 Proceso completado:</p>
              <p>• Toma Realizada → Traslado (${usuarioTraslado})</p>
              <p>• Traslado → Verificada (${usuarioVerifica})</p>
          `;
          
          // Agregar advertencia de muestras pasadas de tiempo si existen
          if (muestrasPasadasTiempo.length > 0) {
            alertaHTML += `
              <hr style="margin: 15px 0;">
              <div style="background-color: #fff3cd; padding: 10px; border-radius: 5px; border-left: 4px solid #ffc107;">
                <p style="color: #856404; margin: 0; font-weight: bold;">⚠️ ADVERTENCIA: Muestras fuera del tiempo permitido</p>
                <p style="color: #856404; margin: 5px 0 10px 0; font-size: 0.9em;">Las siguientes muestras exceden el tiempo recomendado (>2 horas):</p>
                <ul style="margin: 0; padding-left: 20px; color: #856404;">
            `;
            
            muestrasPasadasTiempo.forEach(item => {
              alertaHTML += `
                <li style="margin-bottom: 10px;">
                  <strong>${item.muestra}:</strong><br/>
                  <span style="font-size: 0.85em;">
                    • Toma Realizada: ${item.fechaEstadoAnterior}<br/>
                    • Verificación: ${item.fechaVerificacion}<br/>
                    • <strong>Tiempo transcurrido: ${item.tiempo}</strong>
                  </span>
                </li>
              `;
            });
            
            alertaHTML += `
                </ul>
              </div>
            `;
          }
          
          alertaHTML += '</div>';
          
          Swal.fire({
            title: 'Verificación completada',
            html: alertaHTML,
            icon: muestrasPasadasTiempo.length > 0 ? 'warning' : 'success',
            confirmButtonText: 'Entendido',
            timer: 2200,
            timerProgressBar: true,
            showConfirmButton: false,
            customClass: {
              popup: 'swal-wide'
            }
          });
        }

        // Refrescar muestras
        await fetchMuestrasOrden(ordenSeleccionada.idOrdenMedica);
        
        // Trackear acción
        if (exitosas > 0) {
          trackearAccion('cambio_estado', {
            descripcion: `TRASLADAR/VERIFICAR masivo (${exitosas} muestras)`,
            cantidadMuestras: exitosas
          });
        }
        
        setProcesandoAccionMasiva(false);
      } else {
        // Procesar cambio doble individual
        
        // Obtener la fecha del estado Toma Realizada desde el histórico
        const fechaEstadoActual = await obtenerFechaEstadoActual(
          examen.idMuestraXAnalisis, 
          estadoTomaRealizada.id
        );
        
        // Validar tiempo transcurrido
        let tiempoInfo = null;
        if (fechaEstadoActual) {
          tiempoInfo = calcularTiempoTranscurrido(fechaEstadoActual, fechaTraslado);
          
          if (tiempoInfo) {
            console.log(`⏱️ Tiempo transcurrido para ${examen.analisis}:`, {
              fechaTomaRealizada: tiempoInfo.fechaInicio,
              fechaVerificacion: tiempoInfo.fechaFin,
              tiempoTranscurrido: tiempoInfo.textoFormateado,
              excedeLimite: tiempoInfo.excedeLimite
            });
          }
        }

      // PASO 1: Cambio de Toma Realizada → Traslado
      const requestTraslado = {
        IdOrden: ordenSeleccionada.idOrdenMedica,
        IdMuestraXAnalisis: examen.idMuestraXAnalisis,
        EstadoAnterior: estadoTomaRealizada.id,
        NuevoEstado: estadoTraslado.id,
        UsuarioId: usuarioTrasladoFormateado,
        Comentario: `Trasladado por: ${usuarioTraslado}`,
        IdSucursal: idSucursalFinal,
        Cubiculo: cubiculoSeleccionado || null,
        FechaCambioEstado: fechaTraslado
      };

      console.log('🔄 [PASO 1] Registrando Traslado:', requestTraslado);

      const tTrasladoInd = performance.now();
      const responseTraslado = await fetch(`${server}/mg_muestras_x_ordens/cambiarestado`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        },
        body: JSON.stringify(requestTraslado)
      });
      const tTrasladoIndFin = performance.now();
      console.log('⏱️ [API POST] cambioEstadoTraslado (Individual) - Network Time:', `${(tTrasladoIndFin - tTrasladoInd).toFixed(0)}ms`);

      if (!responseTraslado.ok) {
        const errorText = await responseTraslado.text();
        throw new Error(`Error al registrar traslado: ${errorText}`);
      }

      console.log('✅ [PASO 1] Traslado registrado exitosamente');

      // PASO 2: Cambio de Traslado → Verificada
      const requestVerificada = {
        IdOrden: ordenSeleccionada.idOrdenMedica,
        IdMuestraXAnalisis: examen.idMuestraXAnalisis,
        EstadoAnterior: estadoTraslado.id,
        NuevoEstado: estadoVerificada.id,
        UsuarioId: usuarioVerificaFormateado,
        Comentario: `Verificado por: ${usuarioVerifica}`,
        IdSucursal: idSucursalFinal,
        Cubiculo: cubiculoSeleccionado || null
      };

      console.log('🔄 [PASO 2] Registrando Verificada:', requestVerificada);

      const tVerificadaInd = performance.now();
      const responseVerificada = await fetch(`${server}/mg_muestras_x_ordens/cambiarestado`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        },
        body: JSON.stringify(requestVerificada)
      });
      const tVerificadaIndFin = performance.now();
      console.log('⏱️ [API POST] cambioEstadoVerificada (Individual) - Network Time:', `${(tVerificadaIndFin - tVerificadaInd).toFixed(0)}ms`);

      if (!responseVerificada.ok) {
        const errorText = await responseVerificada.text();
        throw new Error(`Error al registrar verificada: ${errorText}`);
      }

      console.log('✅ [PASO 2] Verificada registrada exitosamente');

      // Mostrar mensaje de éxito con advertencia de tiempo si excede límite
      let mensajeHTML = `
        <div style="text-align: left; padding: 10px;">
          <p><strong>✅ Cambio doble realizado exitosamente</strong></p>
          <p>📋 Análisis: ${examen.analisis}</p>
          <p>• Toma Realizada → Traslado (${usuarioTraslado})</p>
          <p>• Traslado → Verificada (${usuarioVerifica})</p>
      `;
      
      if (tiempoInfo) {
        if (tiempoInfo.excedeLimite) {
          mensajeHTML += `
            <hr style="margin: 15px 0;">
            <div style="background-color: #fff3cd; padding: 10px; border-radius: 5px; border-left: 4px solid #ffc107;">
              <p style="color: #856404; margin: 0; font-weight: bold;">⚠️ ADVERTENCIA: Muestra fuera del tiempo permitido</p>
              <p style="color: #856404; margin: 5px 0; font-size: 0.9em;">
                • Toma Realizada: ${tiempoInfo.fechaInicio}<br/>
                • Verificación: ${tiempoInfo.fechaFin}<br/>
                • <strong>Tiempo transcurrido: ${tiempoInfo.textoFormateado}</strong> (límite: 2 horas)
              </p>
            </div>
          `;
        } else {
          mensajeHTML += `
            <hr style="margin: 15px 0;">
            <div style="background-color: #d4edda; padding: 10px; border-radius: 5px; border-left: 4px solid #28a745;">
              <p style="color: #155724; margin: 0; font-weight: bold;">✅ Tiempo dentro del límite permitido</p>
              <p style="color: #155724; margin: 5px 0 0 0; font-size: 0.9em;">
                • Toma Realizada: ${tiempoInfo.fechaInicio}<br/>
                • Verificación: ${tiempoInfo.fechaFin}<br/>
                • <strong>Tiempo transcurrido: ${tiempoInfo.textoFormateado}</strong>
              </p>
            </div>
          `;
        }
      }
      
      mensajeHTML += '</div>';
      
      Swal.fire({
        title: 'Verificación Completada',
        html: mensajeHTML,
        icon: tiempoInfo && tiempoInfo.excedeLimite ? 'warning' : 'success',
        confirmButtonText: 'Entendido'
      });

      // Refrescar las muestras
      const muestrasActualizadas = await fetchMuestrasOrden(ordenSeleccionada.idOrdenMedica);
      
      // Actualizar modal de detalle si está abierto
      if (modalDetalleOpen && grupoSeleccionado && muestrasActualizadas) {
        const muestraActualizada = muestrasActualizadas.find(m => {
          return m.descripcionMuestra === grupoSeleccionado.descripcionMuestra &&
                 m.modulo === grupoSeleccionado.modulo &&
                 m.cantidad === grupoSeleccionado.cantidad;
        });
        
        if (muestraActualizada) {
          setMuestrasDetalle(muestraActualizada.examenes);
        }
      }
      }
      
    } catch (error) {
      console.error('❌ Error en cambio doble:', error);
      Swal.fire({
        title: 'Error',
        text: `No se pudo completar el cambio doble: ${error.message}`,
        icon: 'error'
      });
    } finally {
      if (pendingCambioEstado?.esMasiva) {
        setProcesandoAccionMasiva(false);
      } else if (pendingCambioEstado?.examen) {
        const { examen } = pendingCambioEstado;
        const muestraKey = `${examen.id}-${examen.idMuestraXAnalisis}`;
        setMuestrasIndividualesCargando(prev => {
          const newSet = new Set(prev);
          newSet.delete(muestraKey);
          return newSet;
        });
      }
    }
  };

  // Función para procesar cambio de estado individual con comentario
  const procesarCambioEstadoIndividualConComentario = async (comentario) => {
    try {
      const { examen, nuevoEstadoId, estadoNuevo, estadoAnterior } = pendingCambioEstado;
      
      const fechaHoraFormateada = new Date().toLocaleString('es-ES');
      const usuarioFormateado = formatUsuario(user ? user.codigoUsuario : 'ADMIN');
      const idSucursalFinal = sucursalSeleccionada?.idSucursal || null;
      
      const requestData = {
        IdOrden: ordenSeleccionada.idOrdenMedica,
        IdMuestraXAnalisis: examen.idMuestraXAnalisis,
        EstadoAnterior: estadoAnterior?.id,
        NuevoEstado: nuevoEstadoId,
        UsuarioId: usuarioFormateado,
        Comentario: comentario.trim(),
        IdSucursal: idSucursalFinal,
        Cubiculo: cubiculoSeleccionado || null
      };

      console.log('🔄 [INDIVIDUAL CON COMENTARIO] Enviando cambio de estado:', {
        ...requestData,
        cubiculoActual: cubiculoSeleccionado,
        idSucursalDesdeSucursalSeleccionada: sucursalSeleccionada?.idSucursal,
        idSucursalFinal: idSucursalFinal,
        nombreUsuario: user?.codigoUsuario
      });

      const response = await fetch(`${server}/mg_muestras_x_ordens/cambiarestado`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error en la respuesta del servidor:', errorText);
        throw new Error(`Error al cambiar estado: ${errorText}`);
      }

      const responseData = await response.json();
      console.log('Respuesta del cambio de estado:', responseData);

      // Mostrar mensaje de éxito
      Swal.fire({
        title: 'Estado cambiado correctamente',
        text: `${examen.analisis}: ${estadoAnterior?.estado} → ${estadoNuevo?.estado}`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });

      // Refrescar las muestras después del cambio y obtener los datos actualizados
      const muestrasActualizadas = await fetchMuestrasOrden(ordenSeleccionada.idOrdenMedica);
      
      // Forzar actualización inmediata del modal de detalle si está abierto
      if (modalDetalleOpen && grupoSeleccionado && muestrasActualizadas) {
        console.log('🔄 Forzando actualización INMEDIATA del modal después del cambio CON comentario');
        
        // Buscar la muestra actualizada directamente en los datos obtenidos
        const muestraActualizada = muestrasActualizadas.find(m => {
          const coincideDescripcion = m.descripcionMuestra === grupoSeleccionado.descripcionMuestra;
          const coincideModulo = m.modulo === grupoSeleccionado.modulo;
          const coincideCantidad = m.cantidad === grupoSeleccionado.cantidad;
          return coincideDescripcion && coincideModulo && coincideCantidad;
        });
        
        if (muestraActualizada) {
          console.log('✅ Actualizando modal INMEDIATAMENTE con datos frescos CON comentario');
          console.log('✅ Exámenes actualizados:', muestraActualizada.examenes);
          
          // Verificar que el examen específico haya cambiado de estado
          const examenActualizado = muestraActualizada.examenes.find(e => e.idMuestraXAnalisis === examen.idMuestraXAnalisis);
          if (examenActualizado) {
            console.log('✅ Estado del examen después del cambio:', examenActualizado.idEstado);
          }
          
          setMuestrasDetalle(muestraActualizada.examenes);
        } else {
          console.log('❌ No se encontró muestra actualizada en datos frescos CON comentario');
        }
      }
      
    } catch (error) {
      console.error('Error al cambiar estado individual:', error);
      Swal.fire({
        title: 'Error',
        text: `No se pudo cambiar el estado: ${error.message}`,
        icon: 'error'
      });
    } finally {
      // Limpiar el loading state al terminar el procesamiento con comentario
      const { examen } = pendingCambioEstado;
      const muestraKey = `${examen.id}-${examen.idMuestraXAnalisis}`;
      setMuestrasIndividualesCargando(prev => {
        const newSet = new Set(prev);
        newSet.delete(muestraKey);
        return newSet;
      });
    }
  };

  // Función para procesar cambio de estado individual sin confirmación (con comentarios predeterminados)
  const procesarCambioEstadoIndividualSinConfirmacion = async (examen, nuevoEstadoId, estadoNuevo, estadoAnterior) => {
    try {
      const usuarioFormateado = formatUsuario(user ? user.codigoUsuario : 'ADMIN');
      const idSucursalFinal = sucursalSeleccionada?.idSucursal || null;
      
      const requestData = {
        IdOrden: ordenSeleccionada.idOrdenMedica,
        IdMuestraXAnalisis: examen.idMuestraXAnalisis,
        EstadoAnterior: estadoAnterior?.id,
        NuevoEstado: nuevoEstadoId,
        UsuarioId: usuarioFormateado,
        Comentario: '',
        IdSucursal: idSucursalFinal,
        Cubiculo: cubiculoSeleccionado || null
      };

      console.log('🔄 [INDIVIDUAL SIN CONFIRMACIÓN] Enviando cambio de estado:', {
        ...requestData,
        cubiculoActual: cubiculoSeleccionado,
        idSucursalDesdeSucursalSeleccionada: sucursalSeleccionada?.idSucursal,
        idSucursalFinal: idSucursalFinal,
        nombreUsuario: user?.codigoUsuario
      });

      const response = await fetch(`${server}/mg_muestras_x_ordens/cambiarestado`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error en la respuesta del servidor:', errorText);
        throw new Error(`Error al cambiar estado: ${errorText}`);
      }

      const responseData = await response.json();
      console.log('Respuesta del cambio de estado:', responseData);

      // Mostrar mensaje de éxito
      Swal.fire({
        title: 'Estado cambiado correctamente',
        text: `${examen.analisis}: ${estadoAnterior?.estado} → ${estadoNuevo?.estado}`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });

      // Refrescar las muestras después del cambio y obtener los datos actualizados
      const muestrasActualizadas = await fetchMuestrasOrden(ordenSeleccionada.idOrdenMedica);
      
      // Forzar actualización inmediata del modal de detalle si está abierto
      if (modalDetalleOpen && grupoSeleccionado && muestrasActualizadas) {
        console.log('🔄 Forzando actualización INMEDIATA del modal después del cambio SIN comentario');
        
        // Buscar la muestra actualizada directamente en los datos obtenidos
        const muestraActualizada = muestrasActualizadas.find(m => {
          const coincideDescripcion = m.descripcionMuestra === grupoSeleccionado.descripcionMuestra;
          const coincideModulo = m.modulo === grupoSeleccionado.modulo;
          const coincideCantidad = m.cantidad === grupoSeleccionado.cantidad;
          return coincideDescripcion && coincideModulo && coincideCantidad;
        });
        
        if (muestraActualizada) {
          console.log('✅ Actualizando modal INMEDIATAMENTE con datos frescos SIN comentario');
          console.log('✅ Exámenes actualizados:', muestraActualizada.examenes);
          
          // Verificar que el examen específico haya cambiado de estado
          const examenActualizado = muestraActualizada.examenes.find(e => e.idMuestraXAnalisis === examen.idMuestraXAnalisis);
          if (examenActualizado) {
            console.log('✅ Estado del examen después del cambio:', examenActualizado.idEstado);
          }
          
          setMuestrasDetalle(muestraActualizada.examenes);
        } else {
          console.log('❌ No se encontró muestra actualizada en datos frescos SIN comentario');
        }
      }
      
    } catch (error) {
      console.error('Error al cambiar estado individual:', error);
      Swal.fire({
        title: 'Error',
        text: `No se pudo cambiar el estado: ${error.message}`,
        icon: 'error'
      });
    } finally {
      // Limpiar el loading state al terminar el procesamiento sin confirmación
      const muestraKey = `${examen.id}-${examen.idMuestraXAnalisis}`;
      setMuestrasIndividualesCargando(prev => {
        const newSet = new Set(prev);
        newSet.delete(muestraKey);
        return newSet;
      });
    }
  };

  // Funciones para manejar el modal de comentarios
  const handleCloseComentarioModal = () => {
    setOpenComentarioModal(false);
    setComentarioText('');
    setPendingCambioEstado(null);
    setMuestraParaComentario(null);
    setCondicionMuestraSeleccionada('');
    setCargandoCondicionesMuestra(false);
    setUsuarioTraslado('');
    setUsuarioVerifica('');
    setFechaTraslado('');
  };

  const handleConfirmComentario = async () => {
    // Validar campos requeridos para cambio doble
    if (pendingCambioEstado?.esCambioDoble) {
      if (!usuarioTraslado.trim()) {
        Swal.fire('Error', 'Debe seleccionar el usuario que traslada', 'error');
        return;
      }
      if (!usuarioVerifica.trim()) {
        Swal.fire('Error', 'Debe seleccionar el usuario que verifica', 'error');
        return;
      }
      if (!fechaTraslado.trim()) {
        Swal.fire('Error', 'Debe ingresar la fecha del traslado', 'error');
        return;
      }
      // No validar comentario - usar comentario por defecto
    } else if (pendingCambioEstado?.esMuestraRepetida) {
      // Validar campos de Muestra Repetida
      if (!motivoRepetidaSeleccionado) {
        Swal.fire('Error', 'Debe seleccionar un motivo de muestra repetida', 'error');
        return;
      }
      if (!responsableRepetida.trim()) {
        Swal.fire('Error', 'Debe seleccionar un responsable', 'error');
        return;
      }
      if (!comentarioText.trim() || comentarioText.trim().length < 5) {
        Swal.fire('Error', 'El comentario debe tener al menos 5 caracteres', 'error');
        return;
      }
    } else {
      // Solo validar comentario si NO es cambio doble
      if (!comentarioText.trim() || comentarioText.trim().length < 5) {
        Swal.fire('Error', 'El comentario debe tener al menos 5 caracteres', 'error');
        return;
      }
    }

    // Verificar si se requiere condición de muestra (cambio a "Muestra No Conforme")
    const esCambioAMuestraNoConforme = pendingCambioEstado?.nuevoEstadoId === 9; // ID para "Muestra No conforme"
    const esMasiva = pendingCambioEstado?.esMasiva;
    const esIndividual = pendingCambioEstado?.isIndividual;
    
    if (esCambioAMuestraNoConforme && (esMasiva || esIndividual) && !condicionMuestraSeleccionada) {
      Swal.fire('Error', 'Debe seleccionar una condición de muestra', 'error');
      return;
    }

    // Cerrar modal
    setOpenComentarioModal(false);
    
    try {
      // Si es cambio doble, usar función especial
      if (pendingCambioEstado?.esCambioDoble) {
        // Generar comentario por defecto
        const comentarioPorDefecto = 'Muestra verificada en recepción';
        await procesarCambioEstadoDoble(comentarioPorDefecto, usuarioTraslado, usuarioVerifica, fechaTraslado);
        return;
      }
      
      // Si es Muestra Repetida, procesar con lógica especial (cambio + reset a Pendiente)
      if (pendingCambioEstado?.esMuestraRepetida) {
        const motivoSeleccionado = motivosRepetida.find(m => m.id === parseInt(motivoRepetidaSeleccionado));
        const motivoTexto = motivoSeleccionado ? motivoSeleccionado.descripcion : 'Motivo no especificado';
        const comentarioRepetida = `MOTIVO MUESTRA REPETIDA: ${motivoTexto}\nResponsable: ${responsableRepetida}\n${comentarioText.trim()}`;
        const estadoPendiente = estadosDisponibles.find(e => e.estado === 'Pendiente');
        const usuarioFormateado = formatUsuario(user ? user.codigoUsuario : 'ADMIN');
        const idSucursalFinal = sucursalSeleccionada?.idSucursal || null;

        if (pendingCambioEstado.esMasiva) {
          // FLUJO MASIVO: iterar sobre todas las muestras afectadas
          const { muestrasAfectadas, nuevoEstadoId, muestrasIndices } = pendingCambioEstado;
          let exitosas = 0;
          let fallidas = 0;

          for (let i = 0; i < muestrasAfectadas.length; i++) {
            const muestra = muestrasAfectadas[i];
            try {
              // Obtener examenes de esta muestra
              const examenes = muestra.examenes || [muestra];
              for (const examen of examenes) {
                // Paso 1: Cambiar a "Muestra Repetida"
                const requestRepetida = {
                  IdOrden: ordenSeleccionada.idOrdenMedica,
                  IdMuestraXAnalisis: examen.idMuestraXAnalisis,
                  EstadoAnterior: examen.idEstadoTomaMuestra,
                  NuevoEstado: nuevoEstadoId,
                  UsuarioId: usuarioFormateado,
                  Comentario: comentarioRepetida,
                  IdSucursal: idSucursalFinal,
                  Cubiculo: cubiculoSeleccionado || null
                };

                const resp1 = await fetch(`${server}/mg_muestras_x_ordens/cambiarestado`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'x-api-key': key },
                  body: JSON.stringify(requestRepetida)
                });

                if (resp1.ok && estadoPendiente) {
                  // Paso 2: Reiniciar a Pendiente
                  const requestPendiente = {
                    IdOrden: ordenSeleccionada.idOrdenMedica,
                    IdMuestraXAnalisis: examen.idMuestraXAnalisis,
                    EstadoAnterior: nuevoEstadoId,
                    NuevoEstado: estadoPendiente.id,
                    UsuarioId: usuarioFormateado,
                    Comentario: 'Reinicio automático desde Muestra Repetida',
                    IdSucursal: idSucursalFinal,
                    Cubiculo: cubiculoSeleccionado || null
                  };

                  await fetch(`${server}/mg_muestras_x_ordens/cambiarestado`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'x-api-key': key },
                    body: JSON.stringify(requestPendiente)
                  });
                }
                exitosas++;
              }
            } catch (error) {
              console.error('Error procesando muestra repetida masiva:', error);
              fallidas++;
            }
          }

          await fetchMuestrasOrden(ordenSeleccionada.idOrdenMedica);
          Swal.fire({
            title: 'Muestra Repetida masiva completada',
            text: `${exitosas} análisis procesados${fallidas > 0 ? `, ${fallidas} fallidos` : ''}`,
            icon: fallidas === 0 ? 'success' : 'warning',
            timer: 3000
          });
        } else {
          // FLUJO INDIVIDUAL: usar examen individual
          await procesarCambioEstadoIndividualConComentario(comentarioRepetida);
          
          if (estadoPendiente && pendingCambioEstado.examen) {
            const { examen } = pendingCambioEstado;
            const requestData = {
              IdOrden: ordenSeleccionada.idOrdenMedica,
              IdMuestraXAnalisis: examen.idMuestraXAnalisis,
              EstadoAnterior: pendingCambioEstado.nuevoEstadoId,
              NuevoEstado: estadoPendiente.id,
              UsuarioId: usuarioFormateado,
              Comentario: 'Reinicio automático desde Muestra Repetida',
              IdSucursal: idSucursalFinal,
              Cubiculo: cubiculoSeleccionado || null
            };

            const response = await fetch(`${server}/mg_muestras_x_ordens/cambiarestado`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'x-api-key': key },
              body: JSON.stringify(requestData)
            });

            if (!response.ok) {
              console.error('Error al reiniciar a Pendiente:', await response.text());
            }
            
            await fetchMuestrasOrden(ordenSeleccionada.idOrdenMedica);
          }
        }
        
        // Limpiar estados de Muestra Repetida
        setMotivoRepetidaSeleccionado('');
        setResponsableRepetida('');
        setComentarioText('');
        setPendingCambioEstado(null);
        setMuestraParaComentario(null);
        return;
      }
      
      // Construir comentario final para acciones de "Muestra No Conforme" (masivas o individuales)
      let comentarioFinal = comentarioText.trim();
      
      if (esCambioAMuestraNoConforme && (esMasiva || esIndividual) && condicionMuestraSeleccionada) {
        // Encontrar la descripción de la condición seleccionada
        const condicionSeleccionada = condicionesMuestra.find(c => c.id === parseInt(condicionMuestraSeleccionada));
        const motivoNoConformidad = condicionSeleccionada ? condicionSeleccionada.descripcion : 'Condición no especificada';
        
        // Concatenar la información de la condición al comentario
        comentarioFinal = `MOTIVO NO CONFORMIDAD: ${motivoNoConformidad}\n\n${comentarioText.trim()}`;
        
        console.log('Comentario final construido:', comentarioFinal);
      }

      // Guardar condición de muestra si es necesario (tanto para masivas como individuales)
      if (esCambioAMuestraNoConforme && (esMasiva || esIndividual) && condicionMuestraSeleccionada) {
        await guardarCondicionMuestra(condicionMuestraSeleccionada, comentarioFinal);
      }

      // Procesar según si es individual, grupal o masiva
      if (pendingCambioEstado.esMasiva) {
        await procesarAccionMasivaConComentario(comentarioFinal);
      } else if (pendingCambioEstado.isIndividual) {
        await procesarCambioEstadoIndividualConComentario(comentarioFinal);
      } else {
        await procesarCambioEstadoConComentario(comentarioFinal);
      }
    } catch (error) {
      console.error('Error al procesar cambio con comentario:', error);
      Swal.fire('Error', 'No se pudo procesar el cambio de estado', 'error');
    } finally {
      // Limpiar estados
      setComentarioText('');
      setPendingCambioEstado(null);
      setMuestraParaComentario(null);
      setCondicionMuestraSeleccionada('');
      setMotivoRepetidaSeleccionado('');
      setResponsableRepetida('');
    }
  };

  // Función para abrir modal de detalle
  const handleAbrirDetalleModal = (muestraGrupo) => {
    setGrupoSeleccionado(muestraGrupo);
    setMuestrasDetalle(muestraGrupo.examenes);
    setMuestrasDetalleSeleccionadas([]); // Limpiar selección
    setModalDetalleOpen(true);
  };

  // Función para cerrar modal de detalle  
  const handleCerrarDetalleModal = () => {
    setModalDetalleOpen(false);
    setGrupoSeleccionado(null);
    setMuestrasDetalle([]);
  };
  
  // Función para actualizar el modal de detalle cuando cambian las muestras
  const actualizarModalDetalleConNuevosDatos = () => {
    if (modalDetalleOpen && grupoSeleccionado) {
      console.log('🔄 Intentando actualizar modal de detalle...');
      console.log('🔄 Grupo seleccionado:', grupoSeleccionado);
      console.log('🔄 Total muestras disponibles:', muestras.length);
      
      // Buscar la muestra actualizada usando una búsqueda más robusta
      const muestraActualizada = muestras.find(m => {
        const coincideDescripcion = m.descripcionMuestra === grupoSeleccionado.descripcionMuestra;
        const coincideModulo = m.modulo === grupoSeleccionado.modulo;
        const coincideCantidad = m.cantidad === grupoSeleccionado.cantidad;
        
        console.log(`🔍 Comparando muestra:`, {
          descripcion: `${m.descripcionMuestra} === ${grupoSeleccionado.descripcionMuestra} = ${coincideDescripcion}`,
          modulo: `${m.modulo} === ${grupoSeleccionado.modulo} = ${coincideModulo}`,
          cantidad: `${m.cantidad} === ${grupoSeleccionado.cantidad} = ${coincideCantidad}`
        });
        
        return coincideDescripcion && coincideModulo && coincideCantidad;
      });
      
      if (muestraActualizada) {
        console.log('✅ Muestra actualizada encontrada:', muestraActualizada);
        console.log('✅ Nuevos exámenes:', muestraActualizada.examenes);
        setMuestrasDetalle(muestraActualizada.examenes);
        console.log('✅ Modal de detalle actualizado automáticamente con nuevos estados');
      } else {
        console.log('❌ No se encontró muestra actualizada para el modal');
        console.log('❌ Muestras disponibles:', muestras.map(m => ({
          descripcion: m.descripcionMuestra,
          modulo: m.modulo,
          cantidad: m.cantidad
        })));
      }
    } else {
      console.log('🔄 Modal no está abierto o no hay grupo seleccionado');
    }
  };

  // Efecto para actualizar el modal de detalle cuando cambien las muestras
  useEffect(() => {
    console.log('🔄 useEffect disparado - actualizando modal de detalle');
    console.log('🔄 Dependencias:', { 
      muestrasLength: muestras.length, 
      modalDetalleOpen, 
      grupoSeleccionado: grupoSeleccionado?.descripcionMuestra 
    });
    actualizarModalDetalleConNuevosDatos();
  }, [muestras, modalDetalleOpen, grupoSeleccionado]);

  // Función para abrir modal de histórico
  const handleAbrirHistorico = async (muestra) => {
    try {
      // Intentar obtener el histórico de diferentes fuentes
      let historicoFinal = [];
      
      // Opción 1: usar historicoCompleto si existe (objeto agrupado)
      if (muestra.historicoCompleto && muestra.historicoCompleto.length > 0) {
        historicoFinal = muestra.historicoCompleto;
      }
      // Opción 2: usar histórico directo del objeto individual
      else if (muestra.historico) {
        // Manejar diferentes estructuras
        if (muestra.historico.$values && Array.isArray(muestra.historico.$values)) {
          historicoFinal = muestra.historico.$values;
        } else if (Array.isArray(muestra.historico)) {
          historicoFinal = muestra.historico;
        }
        
        // Ordenar por fecha (más reciente primero)
        if (historicoFinal.length > 0) {
          historicoFinal = historicoFinal.sort((a, b) => {
            const fechaA = new Date(a.fecha_cambio_estado || 0);
            const fechaB = new Date(b.fecha_cambio_estado || 0);
            return fechaB - fechaA;
          });
        }
      }
      // Opción 3: extraer de cada examen individual (objeto agrupado con examenes)
      else if (muestra.examenes && Array.isArray(muestra.examenes) && muestra.examenes.length > 0) {
        muestra.examenes.forEach((examen) => {
          if (examen.historico) {
            // Manejar diferentes estructuras
            let historicoExamen = [];
            
            if (examen.historico.$values && Array.isArray(examen.historico.$values)) {
              historicoExamen = examen.historico.$values;
            } else if (Array.isArray(examen.historico)) {
              historicoExamen = examen.historico;
            }
            
            if (historicoExamen.length > 0) {
              historicoFinal.push(...historicoExamen);
            }
          }
        });
        
        // Ordenar por fecha (más reciente primero)
        historicoFinal = historicoFinal.sort((a, b) => {
          const fechaA = new Date(a.fecha_cambio_estado || 0);
          const fechaB = new Date(b.fecha_cambio_estado || 0);
          return fechaB - fechaA;
        });
      }
      
      setGrupoSeleccionado({
        ...muestra,
        historico: historicoFinal
      });
      
      // Debug: Verificar que los comentarios están llegando
      console.log('Histórico cargado para modal:', historicoFinal);
      if (historicoFinal.length > 0) {
        console.log('Comentarios en histórico:', historicoFinal.map(h => ({
          fecha: h.fecha_cambio_estado,
          comentario: h.comentario,
          estado_anterior: h.estadoAnterior,
          estado_nuevo: h.estadoNuevo
        })));
      }
      
      setModalHistoricoOpen(true);
    } catch (error) {
      console.error('Error al procesar histórico:', error);
      
      // Mostrar mensaje de error al usuario
      Swal.fire({
        title: 'Error',
        text: 'No se pudo cargar el histórico de la muestra',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
      
      setGrupoSeleccionado({
        ...muestra,
        historico: []
      });
      setModalHistoricoOpen(true);
    }
  };

  // Función para cambiar el estado de una muestra usando la nueva API
  const handleCambiarEstadoMuestra = async (muestraIndex, nuevoEstadoId) => {
    try {
      const muestra = muestras[muestraIndex];
      const estadoActualId = muestra.idEstado || 1; // Estado por defecto: Pendiente
      const estadoNuevo = estadosDisponibles.find(e => e.id === nuevoEstadoId);
      const estadoAnterior = estadosDisponibles.find(e => e.id === estadoActualId);
      
      if (!estadoNuevo || !estadoAnterior) {
        console.error('Estado no encontrado:', { nuevoEstadoId, estadoActualId, estadosDisponibles });
        throw new Error('Estado no válido');
      }
      
      // Validar que el cambio de estado respete la secuencia ordenada
      const ordenAnterior = estadoAnterior.orden;
      const ordenNuevo = estadoNuevo.orden;
      
      // Permitir cambios secuenciales o a estados especiales
      const esSecuencial = ordenNuevo === ordenAnterior + 1;
      const esEstadoEspecial = ['Anulada', 'Paciente Ausente', 'Muestra No Conforme', 'Muestra No conforme', 'Recepción ventanilla'].includes(estadoNuevo.estado);
      const esReactivacion = (
        estadoAnterior.estado === 'Anulada' || 
        estadoAnterior.estado === 'Muestra No Conforme' || 
        estadoAnterior.estado === 'Muestra No conforme'
      ) && estadoNuevo.estado === 'Pendiente';
      const esDesdePacienteAusente = estadoAnterior.estado === 'Paciente Ausente';
      const esDesdeRecepcionVentanilla = estadoAnterior.estado === 'Recepción ventanilla';
      
      // Validación específica: desde "Muestra No conforme" solo se puede ir a "Pendiente"
      const desdeMuestraNoConforme = estadoAnterior.estado === 'Muestra No Conforme' || estadoAnterior.estado === 'Muestra No conforme';
      if (desdeMuestraNoConforme && estadoNuevo.estado !== 'Pendiente') {
        throw new Error(`Desde "Muestra No conforme" solo se puede reactivar a "Pendiente". No se puede cambiar directamente a "${estadoNuevo.estado}".`);
      }
      
      if (!esSecuencial && !esEstadoEspecial && !esReactivacion && !esDesdePacienteAusente && !esDesdeRecepcionVentanilla) {
        throw new Error(`No se puede cambiar de "${estadoAnterior.estado}" (orden ${ordenAnterior}) a "${estadoNuevo.estado}" (orden ${ordenNuevo}). Debe seguir la secuencia ordenada.`);
      }
      
      console.log('Cambiando estado masivo:', {
        estadoAnterior: estadoAnterior.estado,
        estadoNuevo: estadoNuevo.estado,
        ordenAnterior,
        ordenNuevo,
        muestra: muestra.descripcionMuestra
      });
      
      // Siempre permitir comentario personalizable cuando el destino sea "Muestra No Conforme"
      const esMuestraNoConforme = estadoNuevo.estado === 'Muestra No Conforme' || estadoNuevo.estado === 'Muestra No conforme';
      
      console.log('🔍 Debug modal masivo:', {
        estadoAnterior: estadoAnterior.estado,
        estadoNuevo: estadoNuevo.estado,
        esMuestraNoConforme,
        siemprePermiteComentario: esMuestraNoConforme
      });

      if (esMuestraNoConforme) {
        // Para cualquier estado → Muestra No Conforme: siempre permitir comentario personalizado
        setPendingCambioEstado({
          muestraIndex,
          nuevoEstadoId,
          estadoNuevo,
          estadoAnterior,
          muestra
        });
        
        // Campo vacío para escritura libre sin texto predeterminado
        setComentarioText('');
        setMuestraParaComentario(muestra);
        
        // Cargar condiciones de muestra para el combo box
        await fetchCondicionesMuestra();
        
        setOpenComentarioModal(true);
      } else {
        // Para todos los demás casos: ejecutar directamente sin confirmación ni comentarios personalizables
        await procesarCambioEstadoSinConfirmacion(muestraIndex, nuevoEstadoId, estadoNuevo, estadoAnterior, muestra);
      }

    } catch (error) {
      console.error('Error al preparar cambio de estado:', error);
      Swal.fire('Error', error.message, 'error');
    }
  };

  // Función para procesar el cambio de estado con comentario
  const procesarCambioEstadoConComentario = async (comentario) => {
    try {
      const { muestraIndex, nuevoEstadoId, estadoNuevo, estadoAnterior, muestra } = pendingCambioEstado;
      
      const fechaHoraFormateada = new Date().toLocaleString('es-ES');

      // Determinar si es un examen individual o un grupo
      const examenes = Array.isArray(muestra.examenes) ? muestra.examenes : [muestra];
      
      // Procesar cada análisis individual usando la nueva API
      for (const examen of examenes) {
        const usuarioFormateado = formatUsuario(user ? user.codigoUsuario : 'ADMIN');
        const idSucursalFinal = sucursalSeleccionada?.idSucursal || null;
        
        const requestData = {
          IdOrden: ordenSeleccionada.idOrdenMedica,
          IdMuestraXAnalisis: examen.idMuestraXAnalisis,
          EstadoAnterior: estadoAnterior?.id,
          NuevoEstado: nuevoEstadoId,
          UsuarioId: usuarioFormateado,
          Comentario: comentario.trim(), // Agregar el comentario a la API
          IdSucursal: idSucursalFinal,
          Cubiculo: cubiculoSeleccionado || null
        };

        console.log('🔄 [MASIVO CON COMENTARIO] Enviando cambio de estado:', {
          ...requestData,
          cubiculoActual: cubiculoSeleccionado,
          idSucursalDesdeSucursalSeleccionada: sucursalSeleccionada?.idSucursal,
          idSucursalFinal: idSucursalFinal,
          nombreUsuario: user?.codigoUsuario,
          comentarioLength: comentario.trim().length
        });

        const response = await fetch(`${server}/mg_muestras_x_ordens/cambiarestado`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': key,
          },
          body: JSON.stringify(requestData),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Error al cambiar estado: ${errorText}`);
        }

        const responseData = await response.json();
        console.log('Respuesta del cambio de estado:', responseData);
      }

      // Actualizar el estado local de la muestra
      setMuestras(prevMuestras => {
        const nuevasMuestras = [...prevMuestras];
        nuevasMuestras[muestraIndex] = {
          ...muestra,
          idEstado: nuevoEstadoId,
          estadoDescripcion: estadoNuevo?.estado || '',
          realizada: nuevoEstadoId >= 2,
          usuarioToma: formatUsuario(user ? user.codigoUsuario : 'ADMIN').trim(),
          fechaHoraToma: fechaHoraFormateada
        };
        return nuevasMuestras;
      });

      // Recargar datos para reflejar cambios
      await fetchMuestrasOrden(ordenSeleccionada.idOrdenMedica);

      Swal.fire({
        title: 'Estado actualizado correctamente',
        text: `${estadoAnterior?.estado} → ${estadoNuevo?.estado}`,
        icon: 'success',
        timer: 2000
      });

    } catch (error) {
      console.error('Error al cambiar estado:', error);
      Swal.fire('Error al cambiar el estado', error.message, 'error');
    }
  };

  // Función para procesar cambio de estado sin confirmación (con comentarios predeterminados)
  const procesarCambioEstadoSinConfirmacion = async (muestraIndex, nuevoEstadoId, estadoNuevo, estadoAnterior, muestra) => {
    try {
      const fechaHoraFormateada = new Date().toLocaleString('es-ES');

      // Determinar si es un examen individual o un grupo
      const examenes = Array.isArray(muestra.examenes) ? muestra.examenes : [muestra];
      
      // Procesar cada análisis individual usando la nueva API
      for (const examen of examenes) {
        const usuarioFormateado = formatUsuario(user ? user.codigoUsuario : 'ADMIN');
        const idSucursalFinal = sucursalSeleccionada?.idSucursal || null;
        
        const requestData = {
          IdOrden: ordenSeleccionada.idOrdenMedica,
          IdMuestraXAnalisis: examen.idMuestraXAnalisis,
          EstadoAnterior: estadoAnterior?.id,
          NuevoEstado: nuevoEstadoId,
          UsuarioId: usuarioFormateado,
          Comentario: '',
          IdSucursal: idSucursalFinal,
          Cubiculo: cubiculoSeleccionado || null
        };

        console.log('🔄 [MASIVO SIN CONFIRMACIÓN] Enviando cambio de estado:', {
          ...requestData,
          cubiculoActual: cubiculoSeleccionado,
          idSucursalDesdeSucursalSeleccionada: sucursalSeleccionada?.idSucursal,
          idSucursalFinal: idSucursalFinal,
          nombreUsuario: user?.codigoUsuario
        });

        const response = await fetch(`${server}/mg_muestras_x_ordens/cambiarestado`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': key,
          },
          body: JSON.stringify(requestData),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Error al cambiar estado: ${errorText}`);
        }

        const responseData = await response.json();
        console.log('Respuesta del cambio de estado:', responseData);
      }

      // Actualizar el estado local de la muestra
      setMuestras(prevMuestras => {
        const nuevasMuestras = [...prevMuestras];
        nuevasMuestras[muestraIndex] = {
          ...muestra,
          idEstado: nuevoEstadoId,
          estadoDescripcion: estadoNuevo?.estado || '',
          realizada: nuevoEstadoId >= 2,
          usuarioToma: formatUsuario(user ? user.codigoUsuario : 'ADMIN').trim(),
          fechaHoraToma: fechaHoraFormateada
        };
        return nuevasMuestras;
      });

      // Recargar datos para reflejar cambios
      await fetchMuestrasOrden(ordenSeleccionada.idOrdenMedica);

      Swal.fire({
        title: 'Estado actualizado correctamente',
        text: `${estadoAnterior?.estado} → ${estadoNuevo?.estado}`,
        icon: 'success',
        timer: 2000
      });

    } catch (error) {
      console.error('Error al cambiar estado:', error);
      Swal.fire('Error al cambiar el estado', error.message, 'error');
    }
  };


  const fetchDetallesOrden = async (idOrdenTrabajo) => {
    try {
      const response = await fetch(`${server}/MgOrdenesMedicasDetalles/idOrdenTrabajo/${idOrdenTrabajo}`);
      if (response.ok) {
        const data = await response.json();
        const detalles = data.$values || data;
        setAnalisisAgregados(detalles);
        console.log(detalles);
        // Obtener las muestras para los detalles
        await fetchMuestrasParaDetalles(detalles);
      } else {
        console.error('Error al obtener los detalles de la orden');
      }
    } catch (error) {
      console.error('Error al obtener los detalles de la orden:', error);
    }
  };
  


  const fetchMuestrasPorDetalle = async (idDetalle) => {
    try {
      // Supongamos que la API es /api/CdmTiposDeMuestras/idDetalle/{id}
      const response = await fetch(`${server}/CdmTiposDeMuestras/idDetalle/${idDetalle}`);
      if (response.ok) {
        const data = await response.json();
        return data.$values || data;
      } else {
        console.error(`Error al obtener las muestras para el detalle ${idDetalle}`);
        return [];
      }
    } catch (error) {
      console.error(`Error al obtener las muestras para el detalle ${idDetalle}:`, error);
      return [];
    }
  };

  const fetchMuestrasParaDetalles = async (detalles) => {
    const muestrasTotales = [];

    for (const detalle of detalles) {
      const muestrasDetalle = await fetchMuestrasPorDetalle(detalle.idDetalle);
      // Agregar el campo esCobas303 a cada muestra
      const muestrasConCobas = muestrasDetalle.map(muestra => ({
        ...muestra,
        esCobas303: false, // Por defecto es false
        ComparteCon: detalle.descripcionTipoAnalisis || muestra.ComparteCon || ''
      }));
      muestrasTotales.push(...muestrasConCobas);
    }

    setMuestras(muestrasTotales);
  };

  const handleGuardar = async () => {
    try {
      console.log('Iniciando proceso de guardado...');
      
      // Validar que todas las condiciones no cumplidas tengan observaciones
      const condicionesNoCumplidasSinObservacion = condiciones.filter(condicion => 
        !condicion.cumple && (!condicion.observacionesCondicion || !condicion.observacionesCondicion.trim())
      );
      
      if (condicionesNoCumplidasSinObservacion.length > 0) {
        const condicionesStr = condicionesNoCumplidasSinObservacion
          .map(c => c.descripcionCondicion)
          .join('\n• ');
        
        Swal.fire({
          title: 'Observaciones requeridas',
          html: `Las siguientes condiciones no cumplidas requieren observaciones:<br><br><strong>• ${condicionesStr}</strong>`,
          icon: 'warning',
          confirmButtonText: 'Entendido'
        });
        return;
      }

      // Validar que hay condiciones para guardar
      if (condiciones.length === 0) {
        Swal.fire('No hay condiciones para guardar', '', 'info');
        return;
      }

      console.log('Validaciones pasadas, procediendo con guardado...');
      
      // Guardar observaciones de muestras si hay alguna
      await guardarObservacionesMuestras();
      
      // Si todas las validaciones pasan, proceder con el guardado de condiciones
      await continuarGuardadoCondiciones();
      
      // Marcar condiciones como guardadas para permitir cerrar
      setCondicionesGuardadas(true);
    } catch (error) {
      console.error('Error al guardar los datos:', error);
      Swal.fire('Error', 'Ocurrió un error al guardar los datos: ' + error.message, 'error');
    }
  };

  // Función para guardar observaciones de muestras
  const guardarObservacionesMuestras = async () => {
    try {
      if (observacionesMuestras.size === 0) {
        console.log('No hay observaciones de muestras para guardar');
        return;
      }

      console.log('Guardando observaciones de muestras...');
      
      for (const [muestraIndex, observacion] of observacionesMuestras.entries()) {
        if (observacion.trim()) {
          const muestra = muestras[muestraIndex];
          console.log(`Guardando observación para muestra ${muestra.descripcionMuestra}: ${observacion}`);
          
          // Aquí podrías implementar la lógica para guardar las observaciones en la base de datos
          // Por ejemplo, creando un endpoint para guardar observaciones de muestras
          // const response = await fetch(`${server}/mg_muestras_observaciones`, {
          //   method: 'POST',
          //   headers: {
          //     'Content-Type': 'application/json',
          //     'x-api-key': key,
          //   },
          //   body: JSON.stringify({
          //     idOrden: ordenSeleccionada.idOrdenMedica,
          //     idMuestraXAnalisis: muestra.idMuestraXAnalisis,
          //     observacion: observacion.trim(),
          //     ingresadoPor: formatUsuario(user ? user.codigoUsuario : 'ADMIN')
          //   })
          // });
        }
      }
      
      console.log('Observaciones de muestras guardadas exitosamente');
    } catch (error) {
      console.error('Error al guardar observaciones de muestras:', error);
      throw error;
    }
  };



  // Función para obtener el id_condicion desde mg_muestra_x_analisis
  const obtenerIdCondicion = async (idMuestraXAnalisis) => {
    try {
      const response = await fetch(`${server}/mg_muestra_x_analisis/${idMuestraXAnalisis}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`Datos de muestra_x_analisis para ID ${idMuestraXAnalisis}:`, data);
        return data.id_condicion || data.IdCondicion || null;
      }
      return null;
    } catch (error) {
      console.error(`Error al obtener id_condicion para ${idMuestraXAnalisis}:`, error);
      return null;
    }
  };

  // Función para validar que los IDs de muestra x análisis existen
  const validarIdsMuestraAnalisis = async (idsToValidate) => {
    try {
      console.log('🔍 [validarIdsMuestraAnalisis] === INICIO VALIDACIÓN ===');
      console.log('🔍 [validarIdsMuestraAnalisis] IDs a validar:', idsToValidate);
      console.log('🔍 [validarIdsMuestraAnalisis] URL base:', `${server}/mg_muestra_x_analisis/`);
      
      const validationPromises = idsToValidate.map(async (id) => {
        try {
          const url = `${server}/mg_muestra_x_analisis/${id}`;
          console.log(`🌐 [validarIdsMuestraAnalisis] Validando ID ${id} en: ${url}`);
          
          const response = await fetch(url, {
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': key,
            }
          });
          
          console.log(`📡 [validarIdsMuestraAnalisis] Response para ID ${id}: Status ${response.status}`);
          
          if (response.ok) {
            const data = await response.json();
            console.log(`✅ [validarIdsMuestraAnalisis] ID ${id} existe:`, data);
            return { id, exists: true, data };
          } else if (response.status === 404) {
            console.log(`❌ [validarIdsMuestraAnalisis] ID ${id} NO existe (404)`);
            return { id, exists: false };
          } else {
            console.error(`💥 [validarIdsMuestraAnalisis] Error al validar ID ${id}:`, response.status);
            return { id, exists: false, error: `HTTP ${response.status}` };
          }
        } catch (error) {
          console.error(`🔥 [validarIdsMuestraAnalisis] Error de red al validar ID ${id}:`, error);
          return { id, exists: false, error: error.message };
        }
      });
      
      const validationResults = await Promise.all(validationPromises);
      console.log('📊 [validarIdsMuestraAnalisis] Resultados completos:', validationResults);
      
      const invalidIds = validationResults.filter(result => !result.exists);
      
      if (invalidIds.length > 0) {
        console.warn('⚠️ [validarIdsMuestraAnalisis] IDs inválidos encontrados:', invalidIds);
        return { valid: false, invalidIds };
      }
      
      console.log('Todos los IDs son válidos');
      return { valid: true, invalidIds: [] };
    } catch (error) {
      console.error('Error durante la validación de IDs:', error);
      return { valid: false, error: error.message };
    }
  };

  // Función separada para continuar el guardado después de las observaciones
  const continuarGuardadoCondiciones = async () => {
    try {
      console.log('Iniciando guardado de condiciones...');
      console.log('Condiciones totales:', condiciones.length);
      console.log('Orden seleccionada ID:', ordenSeleccionada.idOrdenMedica);
      
      if (condiciones.length === 0) {
        console.log('No hay condiciones para procesar');
        Swal.fire('No hay condiciones para guardar.', '', 'info');
        return;
      }

      // Extraer todos los IDs únicos de id_muestra_x_analisis
      const uniqueIds = [...new Set(condiciones.map(c => c.idCondicionXMuestraXAnalisis))];
      console.log('IDs únicos a validar:', uniqueIds);
      
      // Validar que todos los IDs existan antes de proceder
      const validationResult = await validarIdsMuestraAnalisis(uniqueIds);
      
      if (!validationResult.valid) {
        if (validationResult.invalidIds && validationResult.invalidIds.length > 0) {
          const invalidIdsList = validationResult.invalidIds.map(item => item.id).join(', ');
          const errorMessage = `Los siguientes IDs de muestra x análisis no existen en la base de datos: ${invalidIdsList}. Esto puede indicar que faltan registros en la tabla mg_muestra_x_analisis.`;
          
          console.error(errorMessage);
          Swal.fire({
            title: 'Error de validación',
            text: errorMessage,
            icon: 'error',
            confirmButtonText: 'OK'
          });
          return;
        } else {
          console.error('Error durante la validación:', validationResult.error);
          Swal.fire({
            title: 'Error de validación',
            text: 'Error al validar los datos. Por favor intente nuevamente.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
          return;
        }
      }
      
      // Filtrar solo las condiciones con IDs válidos
      const validCondiciones = condiciones.filter(condicion => 
        !validationResult.invalidIds.some(invalid => invalid.id === condicion.idCondicionXMuestraXAnalisis)
      );
      
      console.log(`Procesando ${validCondiciones.length} condiciones válidas de ${condiciones.length} totales`);

      const condicionesPromises = [];
      
      // Procesar TODAS las condiciones válidas para tener un registro completo
      for (const condicion of validCondiciones) {
        const valorObservaciones = condicion.observacionesCondicion ? condicion.observacionesCondicion.trim() : '';
        const cumpleValue = Boolean(condicion.cumple);

        console.log(`Procesando condición: ${condicion.descripcionCondicion}`);
        console.log(`- idCondicionXMuestraXAnalisis: ${condicion.idCondicionXMuestraXAnalisis}`);
        console.log(`- cumple: ${cumpleValue}`);
        console.log(`- observación: "${valorObservaciones}"`);
        console.log(`- datos completos de condición:`, condicion);

        // Si no tenemos id_condicion, intentar obtenerlo
        let idCondicion = condicion.idCondicion;
        if (!idCondicion) {
          console.log(`ID Condición no disponible, obteniendo desde mg_muestra_x_analisis...`);
          idCondicion = await obtenerIdCondicion(condicion.idCondicionXMuestraXAnalisis);
          console.log(`ID Condición obtenido: ${idCondicion}`);
        }

        try {
          // Primero verificar si ya existe un registro para esta condición específica
          const checkResponse = await fetch(`${server}/mg_condiciones_x_ordens/pororden/${ordenSeleccionada.idOrdenMedica}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': key,
            },
          });

          let condicionExistente = null;
          if (checkResponse.ok) {
            const responseData = await checkResponse.json();
            console.log('Respuesta de condiciones existentes:', responseData);
            
            // La respuesta puede ser un array directo o tener la estructura {$values: [...]}
            const existingCondiciones = Array.isArray(responseData) ? responseData : 
                                        (responseData && responseData.$values ? responseData.$values : []);
            
            console.log('Condiciones existentes encontradas:', existingCondiciones.length);
            
            // Buscar por el IdCondicionXMuestraXAnalisis
            condicionExistente = existingCondiciones.find(c => 
              c.IdCondicionXMuestraXAnalisis === condicion.idCondicionXMuestraXAnalisis
            );
            
            if (condicionExistente) {
              console.log(`Encontrada condición existente con ID: ${condicionExistente.Id}`);
            }
          } else {
            console.error('Error al verificar condiciones existentes:', checkResponse.status, checkResponse.statusText);
          }

          // Datos para POST (nueva condición)
          const dataForPOST = {
            id_orden: ordenSeleccionada.idOrdenMedica,
            id_muestra_x_analisis: condicion.idCondicionXMuestraXAnalisis,
            id_condicion: idCondicion, // Usar el id_condicion obtenido
            cumple: cumpleValue,
            observaciones: valorObservaciones ? [valorObservaciones] : [],
            FechaIngreso: new Date().toISOString(),
            IngresadoPor: formatUsuario(user ? user.codigoUsuario : 'ADMIN')
          };

          // Datos para PUT (actualizar condición existente)
          const dataForPUT = {
            id: condicionExistente?.Id || 0,
            id_orden: ordenSeleccionada.idOrdenMedica,
            id_muestra_x_analisis: condicion.idCondicionXMuestraXAnalisis,
            id_condicion: idCondicion, // Usar el id_condicion obtenido
            cumple: cumpleValue,
            observaciones: valorObservaciones ? [valorObservaciones] : [],
            FechaModificacion: new Date().toISOString(),
            ModificadoPor: formatUsuario(user ? user.codigoUsuario : 'ADMIN')
          };

          console.log('Datos preparados para POST:', dataForPOST);
          console.log('Datos preparados para PUT:', dataForPUT);
          console.log(`ID validado previamente: ${condicion.idCondicionXMuestraXAnalisis}`);
          console.log(`ID_CONDICION incluido: ${idCondicion || 'NULL'}`);

          let promise;

          if (condicionExistente && condicionExistente.Id) {
            // ACTUALIZAR condición existente con PUT
            console.log(`Ejecutando PUT para condición existente ID: ${condicionExistente.Id}`);
            
            promise = fetch(`${server}/mg_condiciones_x_ordens/${condicionExistente.Id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'x-api-key': key,
              },
              body: JSON.stringify(dataForPUT),
            }).then(async response => {
              const responseText = await response.text();
              
              console.log(`PUT Response status: ${response.status}`);
              console.log(`PUT Response text: "${responseText}"`);
              
              if (!response.ok) {
                console.error(`Error en PUT para condición ${condicion.descripcionCondicion}:`, response.status, response.statusText);
                console.error('Detalles del error:', responseText);
                
                // Análisis específico para errores de clave foránea
                if (responseText.includes('foreign key constraint') && responseText.includes('id_muestra_x_analisis_fkey')) {
                  console.error(`ERROR DE CLAVE FORÁNEA: El ID ${condicion.idCondicionXMuestraXAnalisis} no existe en mg_muestra_x_analisis`);
                  console.error(`Condición afectada: ${condicion.descripcionCondicion}`);
                  throw new Error(`CLAVE FORÁNEA: ID ${condicion.idCondicionXMuestraXAnalisis} no existe en la base de datos`);
                }
                
                throw new Error(`PUT Error ${response.status}: ${responseText}`);
              }
              
              console.log(`Condición ${condicion.descripcionCondicion} actualizada exitosamente (PUT)`);
              return { success: true, method: 'PUT', id: condicionExistente.Id };
            });
          } else {
            // CREAR nueva condición con POST
            console.log(`Ejecutando POST para nueva condición: ${condicion.descripcionCondicion}`);
            
            promise = fetch(`${server}/mg_condiciones_x_ordens`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'x-api-key': key,
              },
              body: JSON.stringify(dataForPOST),
            }).then(async response => {
              const responseText = await response.text();
              
              console.log(`POST Response status: ${response.status}`);
              console.log(`POST Response text: "${responseText}"`);
              
              if (!response.ok) {
                console.error(`Error en POST para condición ${condicion.descripcionCondicion}:`, response.status, response.statusText);
                console.error('Detalles del error:', responseText);
                
                // Análisis específico para errores de clave foránea
                if (responseText.includes('foreign key constraint') && responseText.includes('id_muestra_x_analisis_fkey')) {
                  console.error(`ERROR DE CLAVE FORÁNEA: El ID ${condicion.idCondicionXMuestraXAnalisis} no existe en mg_muestra_x_analisis`);
                  console.error(`Condición afectada: ${condicion.descripcionCondicion}`);
                  throw new Error(`CLAVE FORÁNEA: ID ${condicion.idCondicionXMuestraXAnalisis} no existe en la base de datos`);
                }
                
                throw new Error(`POST Error ${response.status}: ${responseText}`);
              }
              
              let result = {};
              if (responseText) {
                try {
                  result = JSON.parse(responseText);
                } catch (parseError) {
                  console.log('Respuesta no JSON válida, pero operación exitosa');
                  result = { success: true };
                }
              }
              
              console.log(`Condición ${condicion.descripcionCondicion} creada exitosamente (POST)`);
              return { success: true, method: 'POST', data: result };
            });
          }

          condicionesPromises.push(promise);
        } catch (error) {
          console.error(`Error al preparar condición ${condicion.descripcionCondicion}:`, error);
          // Agregar una promesa que falle para esta condición específica
          condicionesPromises.push(Promise.reject(new Error(`Error en condición ${condicion.descripcionCondicion}: ${error.message}`)));
        }
      }

      // Esperar a que todas las promesas se resuelvan
      console.log(`Esperando ${condicionesPromises.length} operaciones...`);
      
      if (condicionesPromises.length > 0) {
        const results = await Promise.allSettled(condicionesPromises);
        
        const successful = results.filter(r => r.status === 'fulfilled');
        const failed = results.filter(r => r.status === 'rejected');
        
        console.log(`Operaciones exitosas: ${successful.length}`);
        console.log(`Operaciones fallidas: ${failed.length}`);
        
        if (failed.length > 0) {
          console.error('Errores en algunas condiciones:');
          failed.forEach((failure, index) => {
            console.error(`Error ${index + 1}:`, failure.reason);
          });
          
          const totalOriginal = condiciones.length;
          const totalProcesadas = validCondiciones.length;
          const skipped = totalOriginal - totalProcesadas;
          
          let message = `Se procesaron ${totalProcesadas} de ${totalOriginal} condiciones. `;
          message += `${successful.length} se guardaron correctamente, ${failed.length} fallaron.`;
          if (skipped > 0) {
            message += ` ${skipped} condiciones fueron omitidas por IDs inválidos.`;
          }
          
          if (successful.length > 0) {
            Swal.fire({
              title: 'Guardado parcial',
              text: message,
              icon: 'warning'
            });
          } else {
            Swal.fire({
              title: 'Error al guardar',
              text: 'No se pudo guardar ninguna condición. Ver consola para detalles.',
              icon: 'error'
            });
          }
        } else {
          console.log('Todas las condiciones procesadas exitosamente');
          const totalOriginal = condiciones.length;
          const totalProcesadas = validCondiciones.length;
          const skipped = totalOriginal - totalProcesadas;
          
          if (skipped > 0) {
            Swal.fire({
              title: 'Condiciones guardadas',
              text: `Se guardaron ${successful.length} condiciones correctamente. ${skipped} condiciones fueron omitidas por IDs inválidos.`,
              icon: 'success'
            });
          } else {
            Swal.fire('Condiciones guardadas exitosamente.', '', 'success');
          }
        }
      } else {
        console.log('No se crearon promesas para procesar');
        Swal.fire('No hay condiciones para procesar.', '', 'info');
      }
      
      // Recargar los datos para mostrar los cambios
      console.log('Recargando datos de condiciones...');
      await fetchCondicionesOrden(ordenSeleccionada.idOrdenMedica);
      
    } catch (error) {
      console.error('Error crítico al guardar los datos:', error);
      console.error('Stack trace:', error.stack);
      Swal.fire({
        title: 'Error crítico',
        text: `Error al procesar las condiciones: ${error.message}`,
        icon: 'error'
      });
    }
  };

  // Función para guardar condición de muestra cuando se cambia a "Muestra No Conforme"
  const guardarCondicionMuestra = async (idCondicion, comentario) => {
    try {
        const usuarioFormateado = formatUsuario(user ? user.codigoUsuario : 'ADMIN');
      const fechaActual = new Date().toISOString();
      
      const condicionMuestraData = {
        id_orden: ordenSeleccionada.idOrdenMedica,
        id_muestra_x_analisis: null, // Nulo para condiciones de muestra como solicitado
        id_condicion: idCondicion,
        cumple: false, // Siempre false para "Muestra No Conforme"
        observaciones: [comentario],
        FechaIngreso: fechaActual,
        IngresadoPor: usuarioFormateado
      };

      console.log('Guardando condición de muestra:', condicionMuestraData);

      const response = await fetch(`${server}/mg_condiciones_x_ordens`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(condicionMuestraData)
      });

      if (response.ok) {
        const resultado = await response.json();
        console.log('Condición de muestra guardada exitosamente:', resultado);
        return true;
      } else {
        const errorText = await response.text();
        console.error('Error al guardar condición de muestra:', response.status, errorText);
        throw new Error(`Error del servidor: ${response.status}`);
      }
    } catch (error) {
      console.error('Error al guardar condición de muestra:', error);
      throw error;
    }
  };


  const handleSearch = async () => {
    handleCodigoBarraChange(ordenMedica);
  };

  const limpiarFormulario = () => {
    setCondiciones([]);
    setMuestras([]);
    setTablePage(0);
    setRowsPerPage(5);
    setOrdenSeleccionada({});
    setCodigoBarra('');
    setScanning(false);
    setOrdenMedica('');
    setMuestrasRealizadas([]);
    setCondicionesCumple([]);
    setCondicionesOrden([]);
    setMuestrasOrden([]);
    setMuestrasSeleccionadas([]);
    setImprimirEtiquetasOpen(false);
    setMuestrasSeleccionadasMasivas([]);
    setProcesandoAccionMasiva(false);
  };

  const handleMuestraSeleccionChange = (idMuestraXAnalisis, checked) => {
    if (checked) {
      setMuestrasSeleccionadas(prev => [...prev, idMuestraXAnalisis]);
    } else {
      setMuestrasSeleccionadas(prev => prev.filter(id => id !== idMuestraXAnalisis));
    }
  };

  const handleAbrirImprimirEtiquetas = () => {
    // Detectar si es sucursal principal (ID = 1) 
    const esSucursalPrincipal = sucursalSeleccionada?.idSucursal === 2;
    
    // Actualizar las muestras para incluir el campo esCobas303
    const muestrasActualizadas = muestras.map(muestra => {
      // Pre-seleccionar COBAS si es sucursal principal Y módulo es bioquímica clínica
      const moduloTexto = (muestra.modulo || '').toLowerCase();
      const esBioquimicaClinica = moduloTexto.includes('bioquimica') || 
                                  moduloTexto.includes('bioquímica') ||
                                  (moduloTexto.includes('bio') && moduloTexto.includes('clinica'));
      
      const preSeleccionarCobas = esSucursalPrincipal && esBioquimicaClinica;
      
      return {
        ...muestra,
        esCobas303: preSeleccionarCobas || muestra.esCobas303 || false
      };
    });
    
    setMuestras(muestrasActualizadas);
    setImprimirEtiquetasOpen(true);
  };

  const handleCerrarImprimirEtiquetas = () => {
    setImprimirEtiquetasOpen(false);
    setMuestrasSeleccionadas([]);
  };

  const handleSeleccionarTodasMuestras = (checked) => {
    if (checked) {
      const todasIds = muestras.map(muestra => muestra.idMuestraXAnalisis);
      setMuestrasSeleccionadas(todasIds);
    } else {
      setMuestrasSeleccionadas([]);
    }
  };

  const handleImprimirEtiquetas = async () => {
    if (muestrasSeleccionadas.length === 0) {
      Swal.fire({
        title: 'Seleccione muestras',
        text: 'Debe seleccionar al menos una muestra para imprimir etiquetas',
        icon: 'warning'
      });
      return;
    }

    try {
      const idOrden = ordenSeleccionada.idOrdenMedica;
      
      // Crear el array de objetos con la estructura esperada por el backend
      const muestrasSeleccionadasRequest = muestrasSeleccionadas.map(idMuestra => {
        const muestra = muestras.find(m => m.idMuestraXAnalisis === idMuestra);
        return {
          IdMuestraXAnalisis: idMuestra,
          EsCobas303: muestra?.esCobas303 || false
        };
      });
      
      const response = await fetch(`${server}/MgOrdenesMedicas/ImprimirEtiquetasSeleccionadas/${idOrden}?anchoMM=60&altoMM=30`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(muestrasSeleccionadasRequest)
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Mostrar el PDF en una ventana emergente (modal)
        setEtiquetasPdfUrl(url);
        setOpenEtiquetasPreview(true);
        setImprimirEtiquetasOpen(false);
        
        // Trackear acción de impresión de etiquetas
        trackearAccion('etiquetas_impresas');
        
        Swal.fire({
          title: 'Éxito',
          text: 'Las etiquetas se han generado correctamente',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        throw new Error('Error al generar etiquetas');
      }
    } catch (error) {
      console.error('Error al imprimir etiquetas:', error);
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al generar las etiquetas',
        icon: 'error'
      });
    }
  };

  const handleSeleccionarTodasCondiciones = (checked) => {
    console.log('LOG: Seleccionando todas las condiciones:', checked);
    
    if (checked) {
      // Seleccionar todas las condiciones usando idCondicion (no índices)
      const todosLosIds = condiciones.map(c => c.idCondicion);
      setCondicionesSeleccionadasMasivas(todosLosIds);
      
      // Establecer todas las condiciones como cumplidas (true por defecto)
      setCondiciones((prevCondiciones) => 
        prevCondiciones.map(condicion => ({
          ...condicion,
          cumple: true // Por defecto true al seleccionar
        }))
      );
    } else {
      // Deseleccionar todas
      setCondicionesSeleccionadasMasivas([]);
      
      // NO cambiar el estado de cumple al deseleccionar
      // Mantener el estado actual de cada condición
    }
  };

  // Funciones para manejo individual de condiciones (nuevo patrón)
  const handleCondicionSeleccionChange = (idCondicion, checked) => {
    if (checked) {
      // Agregar a seleccionadas
      setCondicionesSeleccionadasMasivas(prev => [...prev, idCondicion]);
      
      // Establecer cumple = true por defecto al seleccionar
      setCondiciones(prev =>
        prev.map(c =>
          c.idCondicion === idCondicion
            ? { ...c, cumple: true }
            : c
        )
      );
    } else {
      // Quitar de seleccionadas
      setCondicionesSeleccionadasMasivas(prev => prev.filter(id => id !== idCondicion));
      
      // NO cambiar el estado de cumple al deseleccionar
      // Mantener el valor actual por si vuelven a seleccionar
    }
  };

  const handleCondicionCumpleChange = (idCondicion, cumple) => {
    console.log('LOG: Cambiando cumplimiento de condición:', idCondicion, 'a:', cumple);
    
    // Marcar como no guardadas al hacer cambios
    setCondicionesGuardadas(false);
    
    setCondiciones(prev => 
      prev.map((condicion) => 
        condicion.idCondicion === idCondicion 
          ? { 
              ...condicion, 
              cumple: cumple,
              // Limpiar observaciones si se marca como cumplida
              observacionesCondicion: cumple ? '' : condicion.observacionesCondicion 
            }
          : condicion
      )
    );
    
    // Si se marca como cumplida, limpiar también del Map de observaciones
    if (cumple) {
      setObservacionesCondiciones(prev => {
        const nuevas = new Map(prev);
        nuevas.delete(idCondicion);
        return nuevas;
      });
    }
  };

  const handleObservacionCondicionChange = (idCondicion, observacion) => {
    console.log('LOG: Cambiando observación de condición:', idCondicion, 'observación:', observacion);
    
    // Marcar como no guardadas al hacer cambios
    setCondicionesGuardadas(false);
    
    // Guardar en el estado local de observaciones (ahora usa idCondicion como key)
    setObservacionesCondiciones(prev => {
      const nuevasObservaciones = new Map(prev);
      nuevasObservaciones.set(idCondicion, observacion);
      return nuevasObservaciones;
    });

    // También actualizar en el estado principal
    setCondiciones(prev => 
      prev.map((condicion) => 
        condicion.idCondicion === idCondicion 
          ? { ...condicion, observacionesCondicion: observacion }
          : condicion
      )
    );
  };

  // Función para obtener observación de condición
  const getObservacionCondicion = (idCondicion) => {
    // Primero verificar si hay observación manual en el Map
    const observacionManual = observacionesCondiciones.get(idCondicion);
    if (observacionManual) {
      return observacionManual;
    }
    
    // Obtener de la condición actual
    const condicion = condiciones.find(c => c.idCondicion === idCondicion);
    return condicion?.observacionesCondicion || '';
  };

  // Función para guardar condiciones masivamente
  const handleGuardarCondicionesMasivo = async () => {
    console.log('💾 [NUEVO] === INICIO GUARDADO CONDICIONES ESPECIALES ===');
    console.log('💾 Usuario:', user?.codigoUsuario);
    console.log('💾 ID Orden:', ordenSeleccionada?.idOrdenMedica);
    console.log('💾 IDs Condiciones seleccionadas:', condicionesSeleccionadasMasivas);
    
    if (condicionesSeleccionadasMasivas.length === 0) {
      Swal.fire('Advertencia', 'Debe seleccionar al menos una condición para guardar.', 'warning');
      return;
    }

    // Obtener las condiciones seleccionadas (por idCondicion)
    const condicionesAGuardar = condiciones.filter(c => 
      condicionesSeleccionadasMasivas.includes(c.idCondicion)
    );

    console.log('💾 Condiciones a guardar:', condicionesAGuardar.length);
    
    // Validar que las condiciones no cumplidas tengan observaciones
    const condicionesSinObservacion = condicionesAGuardar.filter(c => {
      const observacion = observacionesCondiciones.get(c.idCondicion);
      return !c.cumple && (!observacion || observacion.trim().length < 10);
    });

    if (condicionesSinObservacion.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Observaciones requeridas',
        html: `Las condiciones no cumplidas requieren observaciones de al menos 10 caracteres.<br><br>
               <strong>Condiciones sin observación válida:</strong><br>
               ${condicionesSinObservacion.map(c => `• ${c.descripcionCondicion?.substring(0, 60)}...`).join('<br>')}`,
        confirmButtonText: 'Entendido'
      });
      return;
    }

    // Guardar directamente sin confirmación
    setProcesandoGuardadoCondiciones(true);

    try {
      const usuarioFormateado = formatUsuario(user?.codigoUsuario || 'ADMIN');
      
      // =====================================================================
      // PASO 1: Obtener registros existentes en la BD
      // =====================================================================
      console.log('🔍 Verificando registros existentes...');
      const checkResponse = await fetch(
        `${server}/mg_condiciones_x_ordens/pororden/${ordenSeleccionada.idOrdenMedica}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': key,
          },
        }
      );

      let registrosExistentes = [];
      if (checkResponse.ok) {
        const data = await checkResponse.json();
        registrosExistentes = data.$values || data || [];
        console.log('✅ Registros existentes:', registrosExistentes.length);
      }

      // =====================================================================
      // PASO 2: Preparar payloads (un registro por cada idMuestraXAnalisis)
      // =====================================================================
      const payloads = [];
      
      for (const condicion of condicionesAGuardar) {
        const observacion = observacionesCondiciones.get(condicion.idCondicion) || '';
        
        // Recorrer TODOS los idMuestraXAnalisis de esta condición
        for (const idMuestra of condicion.idsMuestraXAnalisis) {
          // Buscar si ya existe un registro para esta combinación
          const registroExistente = registrosExistentes.find(r =>
            (r.id_condicion === condicion.idCondicion) &&
            (r.id_muestra_x_analisis === idMuestra)
          );

          if (registroExistente) {
            // UPDATE
            payloads.push({
              operacion: 'PUT',
              url: `${server}/mg_condiciones_x_ordens/${registroExistente.id}`,
              body: {
                id: registroExistente.id,
                id_orden: ordenSeleccionada.idOrdenMedica,
                id_muestra_x_analisis: idMuestra,
                id_condicion: condicion.idCondicion,
                cumple: condicion.cumple,
                observaciones: observacion ? [observacion] : [],
                FechaModificacion: new Date().toISOString(),
                ModificadoPor: usuarioFormateado
              }
            });
          } else {
            // CREATE
            payloads.push({
              operacion: 'POST',
              url: `${server}/mg_condiciones_x_ordens`,
              body: {
                id_orden: ordenSeleccionada.idOrdenMedica,
                id_muestra_x_analisis: idMuestra,
                id_condicion: condicion.idCondicion,
                cumple: condicion.cumple,
                observaciones: observacion ? [observacion] : [],
                FechaIngreso: new Date().toISOString(),
                IngresadoPor: usuarioFormateado
              }
            });
          }
        }
      }

      console.log(`📦 Total de payloads a enviar: ${payloads.length}`);

      // =====================================================================
      // PASO 3: Ejecutar las peticiones
      // =====================================================================
      const resultados = [];
      
      for (const payload of payloads) {
        try {
          const response = await fetch(payload.url, {
            method: payload.operacion,
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': key,
            },
            body: JSON.stringify(payload.body)
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error(`❌ Error ${payload.operacion}:`, errorText);
            resultados.push({ success: false, error: errorText });
          } else {
            resultados.push({ success: true });
          }
        } catch (error) {
          console.error(`❌ Error de red ${payload.operacion}:`, error);
          resultados.push({ success: false, error: error.message });
        }
      }

      // =====================================================================
      // PASO 4: Mostrar resultados
      // =====================================================================
      const exitosos = resultados.filter(r => r.success).length;
      const fallidos = resultados.filter(r => !r.success).length;

      console.log(`✅ Exitosos: ${exitosos} | ❌ Fallidos: ${fallidos}`);

      if (exitosos > 0) {
        // Trackear las condiciones modificadas
        trackearAccion('condicion_modificada', { cantidad: exitosos });
        
        Swal.fire({
          title: 'Guardado exitoso',
          text: fallidos > 0 
            ? `${exitosos} registros guardados, ${fallidos} fallaron.`
            : `${exitosos} registros guardados correctamente.`,
          icon: fallidos > 0 ? 'warning' : 'success',
          timer: 3000
        });

        // Limpiar y recargar
        setCondicionesSeleccionadasMasivas([]);
        setObservacionesCondiciones(new Map());
        
        if (ordenSeleccionada.idOrdenMedica) {
          await fetchCondicionesOrden(ordenSeleccionada.idOrdenMedica);
        }
        
        // Marcar condiciones como guardadas DESPUÉS de recargar
        console.log('✅ Condiciones guardadas exitosamente, marcando condicionesGuardadas = true');
        setCondicionesGuardadas(true);
      } else {
        Swal.fire({
          title: 'Error',
          text: 'No se pudo guardar ningún registro.',
          icon: 'error'
        });
      }

    } catch (error) {
      console.error('❌ Error crítico:', error);
      Swal.fire({
        title: 'Error Crítico',
        text: `Error inesperado: ${error.message}`,
        icon: 'error'
      });
    } finally {
      setProcesandoGuardadoCondiciones(false);
    }
  };

  // Funciones para acciones masivas
  const handleSeleccionMasivaChange = (muestraIndex, checked) => {
    if (checked) {
      setMuestrasSeleccionadasMasivas(prev => [...prev, muestraIndex]);
    } else {
      setMuestrasSeleccionadasMasivas(prev => prev.filter(index => index !== muestraIndex));
    }
  };

  const handleSeleccionarTodasMasiva = (checked) => {
    if (checked) {
      const todosLosIndices = muestras.map((_, index) => index);
      setMuestrasSeleccionadasMasivas(todosLosIndices);
    } else {
      setMuestrasSeleccionadasMasivas([]);
    }
  };

  // Función para manejar observaciones de muestras
  const handleObservacionMuestraChange = (muestraIndex, observacion) => {
    setObservacionesMuestras(prevObservaciones => {
      const nuevasObservaciones = new Map(prevObservaciones);
      // Siempre establecer el valor, incluso si está vacío, para mantener la sincronización del cursor
      nuevasObservaciones.set(muestraIndex, observacion);
      return nuevasObservaciones;
    });
  };

  // Función para guardar observación en el histórico de muestra
  const guardarObservacionHistorico = async (muestraIndex, textoObservacion) => {
    try {
      if (!textoObservacion || !textoObservacion.trim()) {
        console.log('❌ No hay observación para guardar');
        return false;
      }

      const muestra = muestras[muestraIndex];
      if (!muestra) {
        console.error('❌ No se encontró la muestra');
        return false;
      }

      console.log('💾 Guardando observación para muestra:', muestra.descripcionMuestra);
      console.log('💾 Observación:', textoObservacion);

      // Obtener el histórico de esta muestra
      const historicoInfo = await fetchComentarioHistoricoMuestra(muestra);
      
      if (!historicoInfo || !historicoInfo.idHistorico) {
        console.error('❌ No se pudo obtener el histórico de la muestra');
        throw new Error('No se encontró el histórico de la muestra');
      }

      console.log('💾 ID del histórico a actualizar:', historicoInfo.idHistorico);

      // Obtener el registro completo del histórico
      const getResponse = await fetch(`${server}/mg_historico_estados_muestra/${historicoInfo.idHistorico}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json'
        }
      });

      if (!getResponse.ok) {
        throw new Error('No se pudo obtener el registro del histórico');
      }

      const registroCompleto = await getResponse.json();
      console.log('💾 Registro completo obtenido:', registroCompleto);

      // Actualizar el comentario y campos de auditoría
      const registroActualizado = {
        ...registroCompleto,
        comentario: textoObservacion.trim(),
        FechaModificacion: new Date().toISOString(),
        ModificadoPor: formatUsuario(user ? user.codigoUsuario : 'ADMIN')
      };

      console.log('📝 Actualizando histórico con:', registroActualizado);

      const updateResponse = await fetch(`${server}/mg_historico_estados_muestra/${historicoInfo.idHistorico}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(registroActualizado)
      });

      if (updateResponse.ok) {
        console.log('✅ Observación guardada exitosamente en el histórico');
        return true;
      } else {
        const errorText = await updateResponse.text();
        console.error('❌ Error al actualizar histórico:', updateResponse.status, errorText);
        throw new Error(`Error del servidor: ${updateResponse.status}`);
      }
    } catch (error) {
      console.error('💥 Error al guardar observación en histórico:', error);
      throw error;
    }
  };

  // Función para obtener observación de una muestra
  const getObservacionMuestra = (muestraIndex) => {
    // Primero verificar si hay observación manual
    const observacionManual = observacionesMuestras.get(muestraIndex);
    if (observacionManual) {
      return observacionManual;
    }
    
    // Si no hay observación manual, obtener el último comentario del histórico
    const muestra = muestras[muestraIndex];
    if (muestra && muestra.historicoCompleto && muestra.historicoCompleto.length > 0) {
      // Obtener el comentario más reciente que no esté vacío
      const ultimoComentario = muestra.historicoCompleto.find(h => h.comentario && h.comentario.trim());
      if (ultimoComentario) {
        return ultimoComentario.comentario.trim();
      }
    }
    
    // Si no hay histórico completo, verificar en examenes individuales
    if (muestra && muestra.examenes && Array.isArray(muestra.examenes) && muestra.examenes.length > 0) {
      for (const examen of muestra.examenes) {
        if (examen.historico) {
          let historicoExamen = [];
          
          if (examen.historico.$values && Array.isArray(examen.historico.$values)) {
            historicoExamen = examen.historico.$values;
          } else if (Array.isArray(examen.historico)) {
            historicoExamen = examen.historico;
          }
          
          if (historicoExamen.length > 0) {
            // Ordenar por fecha y obtener el más reciente con comentario
            const historicoOrdenado = historicoExamen.sort((a, b) => {
              const fechaA = new Date(a.fecha_cambio_estado || 0);
              const fechaB = new Date(b.fecha_cambio_estado || 0);
              return fechaB - fechaA;
            });
            
            const ultimoComentario = historicoOrdenado.find(h => h.comentario && h.comentario.trim());
            if (ultimoComentario) {
              return ultimoComentario.comentario.trim();
            }
          }
        }
      }
    }
    
    return '';
  };

  // Función para obtener la fecha del último comentario del histórico
  const getFechaUltimoComentario = (muestraIndex) => {
    const muestra = muestras[muestraIndex];
    if (!muestra) return '';
    
    // Verificar histórico completo primero
    if (muestra.historicoCompleto && muestra.historicoCompleto.length > 0) {
      const ultimoComentario = muestra.historicoCompleto.find(h => h.comentario && h.comentario.trim());
      if (ultimoComentario && ultimoComentario.fecha_cambio_estado) {
        return new Date(ultimoComentario.fecha_cambio_estado).toLocaleString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    }
    
    // Si no hay histórico completo, verificar en examenes individuales
    if (muestra.examenes && Array.isArray(muestra.examenes) && muestra.examenes.length > 0) {
      for (const examen of muestra.examenes) {
        if (examen.historico) {
          let historicoExamen = [];
          
          if (examen.historico.$values && Array.isArray(examen.historico.$values)) {
            historicoExamen = examen.historico.$values;
          } else if (Array.isArray(examen.historico)) {
            historicoExamen = examen.historico;
          }
          
          if (historicoExamen.length > 0) {
            // Ordenar por fecha y obtener el más reciente con comentario
            const historicoOrdenado = historicoExamen.sort((a, b) => {
              const fechaA = new Date(a.fecha_cambio_estado || 0);
              const fechaB = new Date(b.fecha_cambio_estado || 0);
              return fechaB - fechaA;
            });
            
            const ultimoComentario = historicoOrdenado.find(h => h.comentario && h.comentario.trim());
            if (ultimoComentario && ultimoComentario.fecha_cambio_estado) {
              return new Date(ultimoComentario.fecha_cambio_estado).toLocaleString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });
            }
          }
        }
      }
    }
    
    return '';
  };

  // Función para validar que todas las muestras seleccionadas tienen el mismo estado
  const validarMuestrasSeleccionadasMismoEstado = () => {
    // Detectar contexto: modal de detalle o tabla principal
    const seleccionadas = modalDetalleOpen ? muestrasDetalleSeleccionadas : muestrasSeleccionadasMasivas;
    const fuenteMuestras = modalDetalleOpen ? muestrasDetalle : muestras;
    
    if (seleccionadas.length === 0) {
      return { valido: false, mensaje: 'Debe seleccionar al menos una muestra' };
    }

    const estadosDiferentes = [...new Set(seleccionadas.map(index => fuenteMuestras[index]?.idEstado || fuenteMuestras[index]?.estadoActual || 1))];
    
    if (estadosDiferentes.length > 1) {
      return { 
        valido: false, 
        mensaje: 'Todas las muestras seleccionadas deben tener el mismo estado actual' 
      };
    }

    return { valido: true, estadoComun: estadosDiferentes[0] };
  };

  // Función para obtener el estado común y el siguiente estado de las muestras seleccionadas
  const obtenerEstadoSiguienteMasivo = () => {
    const validacion = validarMuestrasSeleccionadasMismoEstado();
    
    if (!validacion.valido) {
      return null;
    }

    const estadoComun = validacion.estadoComun;
    
    // Para estado Pendiente (orden=1), obtener el siguiente estado en la secuencia
    if (estadoComun === 1) {
      // Buscar el estado actual (Pendiente)
      const estadoActualObj = estadosDisponibles.find(e => e.id === estadoComun);
      if (!estadoActualObj) {
        return {
          estadoActual: estadoComun,
          opcionesDisponibles: [],
          cantidadMuestras: muestrasSeleccionadasMasivas.length
        };
      }
      
      // Buscar el siguiente estado en la secuencia (orden + 1)
      const siguienteEstado = estadosDisponibles.find(e => e.orden === estadoActualObj.orden + 1);
      
      // Buscar el estado "Paciente Ausente"
      const estadoPacienteAusente = estadosDisponibles.find(e => 
        e.estado === 'Paciente Ausente' || 
        e.estado.toLowerCase().includes('paciente') && e.estado.toLowerCase().includes('ausente')
      );
      
      // Buscar el estado "Recepción ventanilla"
      const estadoRecepcionVentanilla = estadosDisponibles.find(e => 
        e.estado === 'Recepción ventanilla' || 
        e.estado.toLowerCase().includes('recepción') && e.estado.toLowerCase().includes('ventanilla')
      );
      
      const opciones = [];
      
      if (siguienteEstado) {
        opciones.push({ 
          id: siguienteEstado.id, 
          estado: siguienteEstado.estado, 
          isPrimary: true 
        });
      }
      
      if (estadoPacienteAusente) {
        opciones.push({ 
          id: estadoPacienteAusente.id, 
          estado: estadoPacienteAusente.estado, 
          isPrimary: false 
        });
      }
      
      if (estadoRecepcionVentanilla) {
        opciones.push({ 
          id: estadoRecepcionVentanilla.id, 
          estado: estadoRecepcionVentanilla.estado, 
          isPrimary: false 
        });
      }
      
      return {
        estadoActual: estadoComun,
        opcionesDisponibles: opciones,
        cantidadMuestras: muestrasSeleccionadasMasivas.length
      };
    }
    
    // Para estado Paciente Ausente, permitir volver a Pendiente
    const estadoPacienteAusente = estadosDisponibles.find(e => e.estado === 'Paciente Ausente');
    if (estadoPacienteAusente && estadoComun === estadoPacienteAusente.id) {
      // Buscar el estado Pendiente
      const estadoPendiente = estadosDisponibles.find(e => e.estado === 'Pendiente');
      
      const opciones = [];
      
      if (estadoPendiente) {
        opciones.push({ 
          id: estadoPendiente.id, 
          estado: 'Retomar Muestra', 
          isPrimary: true 
        });
      }
      
      return {
        estadoActual: estadoComun,
        opcionesDisponibles: opciones,
        cantidadMuestras: muestrasSeleccionadasMasivas.length
      };
    }
    
    // Para estado Recepción ventanilla, permitir continuar al siguiente estado y volver a Pendiente
    const estadoRecepcionVentanilla = estadosDisponibles.find(e => e.estado === 'Recepción ventanilla');
    if (estadoRecepcionVentanilla && estadoComun === estadoRecepcionVentanilla.id) {
      const estadoTraslado = estadosDisponibles.find(e => e.estado === 'Traslado');
      const estadoPendiente = estadosDisponibles.find(e => e.estado === 'Pendiente');
      
      const opciones = [];
      
      // Mantener flujo secuencial mostrando solo Traslado
      if (estadoTraslado) {
        opciones.push({ 
          id: estadoTraslado.id, 
          estado: estadoTraslado.estado, 
          isPrimary: true 
        });
      }
      
      // Agregar opción de retomar muestra como secundaria
      if (estadoPendiente) {
        opciones.push({ 
          id: estadoPendiente.id, 
          estado: 'Retomar Muestra', 
          isPrimary: false 
        });
      }
      
      return {
        estadoActual: estadoComun,
        opcionesDisponibles: opciones,
        cantidadMuestras: muestrasSeleccionadasMasivas.length
      };
    }
    
    // Para estado "Muestra No conforme", ofrecer opciones de reactivación
    const estadoMuestraNoConforme = estadosDisponibles.find(e => 
      e.estado === 'Muestra No conforme' || e.estado === 'Muestra No Conforme'
    );
    if (estadoMuestraNoConforme && estadoComun === estadoMuestraNoConforme.id) {
      const estadoPendiente = estadosDisponibles.find(e => e.estado === 'Pendiente');
      const estadoAnulada = estadosDisponibles.find(e => e.estado === 'Anulada');
      
      const opciones = [];
      
      if (estadoPendiente) {
        opciones.push({ 
          id: estadoPendiente.id, 
          estado: 'Reactivar', 
          isPrimary: true 
        });
      }
      
      if (estadoAnulada) {
        opciones.push({ 
          id: estadoAnulada.id, 
          estado: 'Anular', 
          isPrimary: false 
        });
      }
      
      return {
        estadoActual: estadoComun,
        opcionesDisponibles: opciones,
        cantidadMuestras: muestrasSeleccionadasMasivas.length
      };
    }
    
    // Para estado "Anulada", permitir reactivación
    const estadoAnulada = estadosDisponibles.find(e => e.estado === 'Anulada');
    if (estadoAnulada && estadoComun === estadoAnulada.id) {
      const estadoPendiente = estadosDisponibles.find(e => e.estado === 'Pendiente');
      
      const opciones = [];
      
      if (estadoPendiente) {
        opciones.push({ 
          id: estadoPendiente.id, 
          estado: 'Reactivar', 
          isPrimary: true 
        });
      }
      
      return {
        estadoActual: estadoComun,
        opcionesDisponibles: opciones,
        cantidadMuestras: muestrasSeleccionadasMasivas.length
      };
    }
    
    // CASO ESPECIAL: Estado "Toma Realizada" mostrando solo Traslado
    const estadoTomaRealizada = estadosDisponibles.find(e => e.estado === 'Toma Realizada');
    if (estadoTomaRealizada && estadoComun === estadoTomaRealizada.id) {
      const estadoTraslado = estadosDisponibles.find(e => e.estado === 'Traslado');
      
      const opciones = [];
      
      if (estadoTraslado) {
        opciones.push({ 
          id: estadoTraslado.id, 
          estado: estadoTraslado.estado, 
          isPrimary: true 
        });
      }
      
      return {
        estadoActual: estadoComun,
        opcionesDisponibles: opciones,
        cantidadMuestras: muestrasSeleccionadasMasivas.length
      };
    }
    
    // CASO ESPECIAL: Estado "Traslado" -> "Verificada"
    const estadoTraslado = estadosDisponibles.find(e => e.estado === 'Traslado');
    if (estadoTraslado && estadoComun === estadoTraslado.id) {
      const estadoVerificada = estadosDisponibles.find(e => e.estado === 'Verificada');
      
      const opciones = [];
      
      if (estadoVerificada) {
        opciones.push({ 
          id: estadoVerificada.id, 
          estado: estadoVerificada.estado, 
          isPrimary: true 
        });
      }
      
      return {
        estadoActual: estadoComun,
        opcionesDisponibles: opciones,
        cantidadMuestras: muestrasSeleccionadasMasivas.length
      };
    }
    
    // Para estado "Muestra Repetida", permitir volver a Pendiente
    const estadoMuestraRepetida = estadosDisponibles.find(e => e.estado === 'Muestra Repetida');
    if (estadoMuestraRepetida && estadoComun === estadoMuestraRepetida.id) {
      const estadoPendiente = estadosDisponibles.find(e => e.estado === 'Pendiente');
      const opciones = [];
      if (estadoPendiente) {
        opciones.push({ id: estadoPendiente.id, estado: 'Reactivar', isPrimary: true });
      }
      return {
        estadoActual: estadoComun,
        opcionesDisponibles: opciones,
        cantidadMuestras: muestrasSeleccionadasMasivas.length
      };
    }
    
    // Para estado "Análisis Reprocesado", permitir auto-transición (reprocesar de nuevo)
    const estadoReprocesado = estadosDisponibles.find(e => e.estado === 'Análisis Reprocesado' || e.estado === 'Analisis Reprocesado');
    if (estadoReprocesado && estadoComun === estadoReprocesado.id) {
      const opciones = [];
      opciones.push({ id: estadoReprocesado.id, estado: 'Reprocesar nuevamente', isPrimary: true });
      return {
        estadoActual: estadoComun,
        opcionesDisponibles: opciones,
        cantidadMuestras: muestrasSeleccionadasMasivas.length
      };
    }
    
    // Para otros estados, usar la lógica original
    const estadoActualObjGenerico = estadosDisponibles.find(e => e.id === estadoComun);
    const siguienteEstado = getSiguienteEstado(estadoComun, estadosDisponibles);
    
    // Convertir estadoSiguiente a formato de opciones
    const opciones = [];
    if (siguienteEstado) {
      opciones.push({
        id: siguienteEstado.id,
        estado: siguienteEstado.estado,
        isPrimary: true
      });
    }
    
    // Agregar "Muestra Repetida" solo desde Verificada (orden >= 5) en adelante
    const estadoVerificadaMasiva = estadosDisponibles.find(e => e.estado === 'Verificada');
    const ordenVerificadaMasiva = estadoVerificadaMasiva ? estadoVerificadaMasiva.orden : 5;
    const estadoMuestraRepetidaMasiva = estadosDisponibles.find(e => e.estado === 'Muestra Repetida');
    if (estadoMuestraRepetidaMasiva && estadoActualObjGenerico && 
        estadoActualObjGenerico.orden >= ordenVerificadaMasiva &&
        !['Anulada', 'Procesada', 'Muestra No conforme', 'Muestra No Conforme', 'Muestra Repetida', 'Análisis Reprocesado', 'Analisis Reprocesado'].includes(estadoActualObjGenerico.estado)) {
      opciones.push({
        id: estadoMuestraRepetidaMasiva.id,
        estado: estadoMuestraRepetidaMasiva.estado,
        isPrimary: false
      });
    }
    
    return {
      estadoActual: estadoComun,
      opcionesDisponibles: opciones,
      cantidadMuestras: muestrasSeleccionadasMasivas.length
    };
  };

  // Función para ejecutar acción masiva
  const handleAccionMasiva = async (nuevoEstadoId) => {
    const estadoRecibido = estadosDisponibles.find(e => e.id === nuevoEstadoId);
    
    const validacion = validarMuestrasSeleccionadasMismoEstado();
    
    if (!validacion.valido) {
      Swal.fire('Error', validacion.mensaje, 'error');
      return;
    }

    const estadoNuevo = estadosDisponibles.find(e => e.id === nuevoEstadoId);
    const estadoAnterior = estadosDisponibles.find(e => e.id === validacion.estadoComun);
    
    // Usar la fuente correcta según si estamos en modal de detalle o tabla principal
    const seleccionadas = modalDetalleOpen ? muestrasDetalleSeleccionadas : muestrasSeleccionadasMasivas;
    const fuenteMuestras = modalDetalleOpen ? muestrasDetalle : muestras;
    const muestrasAfectadas = seleccionadas.map(index => fuenteMuestras[index]);

    // Validación de secuencia para acciones masivas
    if (estadoAnterior && estadoNuevo) {
      const ordenAnterior = estadoAnterior.orden;
      const ordenNuevo = estadoNuevo.orden;
      
      // Detectar si es un cambio doble (TRASLADAR/VERIFICAR)
      const estadoTomaRealizadaOrden = estadosDisponibles.find(e => e.estado === 'Toma Realizada')?.orden;
      const estadoRecepcionVentanillaOrden = estadosDisponibles.find(e => e.estado === 'Recepción ventanilla')?.orden;
      const estadoVerificadaOrden = estadosDisponibles.find(e => e.estado === 'Verificada')?.orden;
      const esCambioDoble = (
        (estadoAnterior.orden === estadoTomaRealizadaOrden && estadoNuevo.orden === estadoVerificadaOrden) ||
        (estadoAnterior.orden === estadoRecepcionVentanillaOrden && estadoNuevo.orden === estadoVerificadaOrden)
      );
      
      // Permitir cambios secuenciales o a estados especiales
      const esSecuencial = ordenNuevo === ordenAnterior + 1;
      const esEstadoEspecial = ['Anulada', 'Paciente Ausente', 'Muestra No Conforme', 'Muestra No conforme', 'Recepción ventanilla', 'Muestra Repetida', 'Análisis Reprocesado', 'Analisis Reprocesado'].includes(estadoNuevo.estado);
      const esReactivacion = (
        estadoAnterior.estado === 'Anulada' || 
        estadoAnterior.estado === 'Muestra No Conforme' || 
        estadoAnterior.estado === 'Muestra No conforme'
      ) && estadoNuevo.estado === 'Pendiente';
      const esDesdePacienteAusente = estadoAnterior.estado === 'Paciente Ausente';
      const esDesdeRecepcionVentanilla = estadoAnterior.estado === 'Recepción ventanilla';
      const esDesdeMuestraRepetidaMasiva = estadoAnterior.estado === 'Muestra Repetida' && estadoNuevo.estado === 'Pendiente';
      const esAutoTransicionReprocesadoMasiva = (estadoAnterior.estado === 'Análisis Reprocesado' || estadoAnterior.estado === 'Analisis Reprocesado') && 
                                                  (estadoNuevo.estado === 'Análisis Reprocesado' || estadoNuevo.estado === 'Analisis Reprocesado');
      
      // Validación específica: desde "Muestra No conforme" solo se puede ir a "Pendiente"
      const desdeMuestraNoConforme = estadoAnterior.estado === 'Muestra No Conforme' || estadoAnterior.estado === 'Muestra No conforme';
      if (desdeMuestraNoConforme && estadoNuevo.estado !== 'Pendiente') {
        Swal.fire('Error', `Desde "Muestra No conforme" solo se puede reactivar a "Pendiente". No se puede cambiar directamente a "${estadoNuevo.estado}".`, 'error');
        return;
      }
      
      // Permitir cambio doble como caso especial
      if (!esSecuencial && !esEstadoEspecial && !esReactivacion && !esDesdePacienteAusente && !esDesdeRecepcionVentanilla && !esCambioDoble && !esDesdeMuestraRepetidaMasiva && !esAutoTransicionReprocesadoMasiva) {
        Swal.fire('Error', `No se puede cambiar de "${estadoAnterior.estado}" (orden ${ordenAnterior}) a "${estadoNuevo.estado}" (orden ${ordenNuevo}). Debe seguir la secuencia ordenada.`, 'error');
        return;
      }
    }

    // Detectar si es un cambio doble (TRASLADAR/VERIFICAR masivo)
    const estadoTomaRealizadaOrden = estadosDisponibles.find(e => e.estado === 'Toma Realizada')?.orden;
    const estadoRecepcionVentanillaOrden = estadosDisponibles.find(e => e.estado === 'Recepción ventanilla')?.orden;
    const estadoVerificadaOrden = estadosDisponibles.find(e => e.estado === 'Verificada')?.orden;
    const esCambioDoble = (
      (estadoAnterior?.orden === estadoTomaRealizadaOrden && estadoNuevo?.orden === estadoVerificadaOrden) ||
      (estadoAnterior?.orden === estadoRecepcionVentanillaOrden && estadoNuevo?.orden === estadoVerificadaOrden)
    );
    
    // Siempre permitir comentario personalizable cuando el destino sea "Muestra No Conforme"
    const esMuestraNoConforme = estadoNuevo.estado === 'Muestra No Conforme' || estadoNuevo.estado === 'Muestra No conforme';
    
    console.log('🔍 Debug acción masiva:', {
      estadoAnterior: estadoAnterior?.estado,
      estadoNuevo: estadoNuevo?.estado,
      esMuestraNoConforme,
      esCambioDoble,
      siemprePermiteComentario: esMuestraNoConforme || esCambioDoble
    });

    if (esCambioDoble) {
      // Para TRASLADAR/VERIFICAR masivo: solicitar información del traslado
      setPendingCambioEstado({
        esMasiva: true,
        esCambioDoble: true,
        nuevoEstadoId,
        estadoNuevo,
        estadoAnterior,
        muestrasAfectadas,
        muestrasIndices: seleccionadas,
        enModalDetalle: modalDetalleOpen,
        estadoIntermedio: estadosDisponibles.find(e => e.estado === 'Traslado')?.id
      });
      
      setComentarioText('');
      setUsuarioTraslado('');
      setFechaTraslado('');
      setMuestraParaComentario({ 
        descripcionMuestra: `${muestrasAfectadas.length} muestras seleccionadas`,
        analisis: muestrasAfectadas.map(m => m.descripcionMuestra).join(', ')
      });
      
      // Cargar usuarios disponibles
      await fetchUsuariosDisponibles();
      
      setOpenComentarioModal(true);
    } else if (esMuestraNoConforme) {
      // Para cualquier estado → Muestra No Conforme: campo vacío para escritura libre
      
      setPendingCambioEstado({
        esMasiva: true,
        nuevoEstadoId,
        estadoNuevo,
        estadoAnterior,
        muestrasAfectadas,
        muestrasIndices: seleccionadas,
        enModalDetalle: modalDetalleOpen
      });
      
      setComentarioText('');
      setMuestraParaComentario({ 
        descripcionMuestra: `${muestrasAfectadas.length} muestras seleccionadas`,
        analisis: muestrasAfectadas.map(m => m.descripcionMuestra).join(', ')
      });
      
      // Cargar condiciones de muestra para el combo box
      await fetchCondicionesMuestra();
      
      setOpenComentarioModal(true);
    } else if (estadoNuevo.estado === 'Muestra Repetida') {
      // Para Muestra Repetida masiva: motivo + responsable + comentario
      setPendingCambioEstado({
        esMasiva: true,
        esMuestraRepetida: true,
        nuevoEstadoId,
        estadoNuevo,
        estadoAnterior,
        muestrasAfectadas,
        muestrasIndices: seleccionadas,
        enModalDetalle: modalDetalleOpen
      });
      
      setComentarioText('');
      setMotivoRepetidaSeleccionado('');
      setResponsableRepetida('');
      setMuestraParaComentario({ 
        descripcionMuestra: `${muestrasAfectadas.length} muestras seleccionadas`,
        analisis: muestrasAfectadas.map(m => m.descripcionMuestra).join(', ')
      });
      
      // Cargar motivos de muestra repetida y usuarios disponibles
      await Promise.all([fetchMotivosRepetida(), fetchUsuariosDisponibles()]);
      
      setOpenComentarioModal(true);
    } else {
      // Para todos los demás casos: ejecutar directamente sin confirmación ni comentarios personalizables
      await procesarAccionMasivaSinConfirmacion(nuevoEstadoId, estadoNuevo, estadoAnterior, muestrasAfectadas, muestrasSeleccionadasMasivas);
    }
  };

  // Función para procesar la acción masiva con comentario (SIN mensaje de confirmación)
  const procesarAccionMasivaConComentario = async (comentario) => {
    const { nuevoEstadoId, estadoNuevo, estadoAnterior, muestrasAfectadas, muestrasIndices } = pendingCambioEstado;
    
    setProcesandoAccionMasiva(true);
    
    try {
      let exitosas = 0;
      let fallidas = 0;
      const errores = [];

      // Procesar cada muestra seleccionada
      for (let i = 0; i < muestrasAfectadas.length; i++) {
        const muestra = muestrasAfectadas[i];
        const muestraIndex = muestrasIndices[i];
        
        try {
          // Determinar si es un examen individual o un grupo
          const examenes = Array.isArray(muestra.examenes) ? muestra.examenes : [muestra];
          
          // Procesar cada análisis individual usando la nueva API
          for (const examen of examenes) {
            const usuarioFormateado = formatUsuario(user ? user.codigoUsuario : 'ADMIN');
            const idSucursalFinal = sucursalSeleccionada?.idSucursal || null;
            
            const requestData = {
              IdOrden: ordenSeleccionada.idOrdenMedica,
              IdMuestraXAnalisis: examen.idMuestraXAnalisis,
              EstadoAnterior: estadoAnterior?.id,
              NuevoEstado: nuevoEstadoId,
              UsuarioId: usuarioFormateado,
              Comentario: comentario.trim(),
              IdSucursal: idSucursalFinal,
              Cubiculo: cubiculoSeleccionado || null
            };

            console.log('🔄 [ACCIÓN MASIVA CON COMENTARIO] Enviando:', {
              ...requestData,
              muestraIndex: i + 1,
              totalMuestras: muestrasAfectadas.length,
              cubiculoActual: cubiculoSeleccionado,
              idSucursalDesdeSucursalSeleccionada: sucursalSeleccionada?.idSucursal,
              idSucursalFinal: idSucursalFinal
            });

            const response = await fetch(`${server}/mg_muestras_x_ordens/cambiarestado`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'x-api-key': key,
              },
              body: JSON.stringify(requestData),
            });

            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Error al cambiar estado: ${errorText}`);
            }
          }
          
          exitosas++;
        } catch (error) {
          fallidas++;
          errores.push(`${muestra.descripcionMuestra}: ${error.message}`);
          console.error(`Error en muestra ${muestra.descripcionMuestra}:`, error);
        }
      }

      // Recargar datos para reflejar cambios
      await fetchMuestrasOrden(ordenSeleccionada.idOrdenMedica);
      
      // Trackear los cambios de estado exitosos (acciones masivas)
      if (exitosas > 0) {
        const estadoAnteriorObj = estadosDisponibles.find(e => e.id === pendingCambioEstado.estadoAnteriorId) || { estado: 'Desconocido' };
        const estadoNuevoObj = estadosDisponibles.find(e => e.id === pendingCambioEstado.nuevoEstadoId) || { estado: 'Desconocido' };
        
        trackearAccion('muestra_modificada', { cantidad: exitosas });
        trackearAccion('cambio_estado', {
          descripcion: `Acción masiva: ${estadoAnteriorObj.estado} → ${estadoNuevoObj.estado} (${exitosas} muestras)`,
          cantidadMuestras: exitosas,
          comentario: comentario.trim(),
          estadoAnteriorId: pendingCambioEstado.estadoAnteriorId,
          estadoNuevoId: pendingCambioEstado.nuevoEstadoId
        });
      }
      
      // NO limpiar selecciones - mantener las muestras seleccionadas para continuar el flujo
      // setMuestrasSeleccionadasMasivas([]);

      // Mostrar resultado solo si hubo errores (sin mensaje de éxito para evitar retrasos)
      if (fallidas > 0) {
        Swal.fire({
          title: 'Acción completada con errores',
          html: `
            <strong>Exitosas:</strong> ${exitosas}<br>
            <strong>Fallidas:</strong> ${fallidas}<br><br>
            <strong>Errores:</strong><br>
            ${errores.join('<br>')}
          `,
          icon: 'warning'
        });
      }
      // Si todo fue exitoso, no mostrar mensaje para evitar retrasos

    } catch (error) {
      console.error('Error en acción masiva:', error);
      Swal.fire('Error', 'Error al procesar la acción masiva', 'error');
    } finally {
      setProcesandoAccionMasiva(false);
      setOpenComentarioModal(false);
      setPendingCambioEstado(null);
    }
  };

  // Función para procesar acción masiva sin confirmación (con comentarios predeterminados)
  // Función sin mensaje de confirmación para evitar retrasos en tablet
  //Funcion vieja 
  /*const procesarAccionMasivaSinConfirmacion = async (nuevoEstadoId, estadoNuevo, estadoAnterior, muestrasAfectadas, muestrasIndices) => {
    setProcesandoAccionMasiva(true);
    
    try {
      let exitosas = 0;
      let fallidas = 0;
      const errores = [];

      // Procesar cada muestra seleccionada
      for (let i = 0; i < muestrasAfectadas.length; i++) {
        const muestra = muestrasAfectadas[i];
        
        try {
          // Determinar si es un examen individual o un grupo
          const examenes = Array.isArray(muestra.examenes) ? muestra.examenes : [muestra];
          
          // Procesar cada análisis individual usando la nueva API
          for (const examen of examenes) {
            const usuarioFormateado = formatUsuario(user ? user.codigoUsuario : 'ADMIN');
            const idSucursalFinal = sucursalSeleccionada?.idSucursal || null;
            
            const requestData = {
              IdOrden: ordenSeleccionada.idOrdenMedica,
              IdMuestraXAnalisis: examen.idMuestraXAnalisis,
              EstadoAnterior: estadoAnterior?.id,
              NuevoEstado: nuevoEstadoId,
              UsuarioId: usuarioFormateado,
              Comentario: '',
              IdSucursal: idSucursalFinal,
              Cubiculo: cubiculoSeleccionado || null
            };

            console.log('🔄 [ACCIÓN MASIVA SIN CONFIRMACIÓN] Enviando:', {
              ...requestData,
              muestraIndex: i + 1,
              idSucursalDesdeSucursalSeleccionada: sucursalSeleccionada?.idSucursal,
              idSucursalFinal: idSucursalFinal,
              totalMuestras: muestrasAfectadas.length,
              cubiculoActual: cubiculoSeleccionado,
              idSucursalUsuario: user?.idSucursal
            });
           
            const response = await fetch(`${server}/mg_muestras_x_ordens/cambiarestado`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'x-api-key': key,
              },
              body: JSON.stringify(requestData),
            });
            
        
     
            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Error al cambiar estado: ${errorText}`);
            }
          }
          
          exitosas++;
        } catch (error) {
          fallidas++;
          errores.push(`${muestra.descripcionMuestra}: ${error.message}`);
          console.error(`Error en muestra ${muestra.descripcionMuestra}:`, error);
        }
      }

      // Recargar datos para reflejar cambios
      await fetchMuestrasOrden(ordenSeleccionada.idOrdenMedica);
      
      // Trackear los cambios de estado exitosos (acciones masivas sin confirmación)
      if (exitosas > 0) {
        trackearAccion('muestra_modificada', { cantidad: exitosas });
        trackearAccion('cambio_estado', {
          descripcion: `Acción masiva: ${estadoAnterior?.estado || 'Desconocido'} → ${estadoNuevo?.estado || 'Desconocido'} (${exitosas} muestras)`,
          cantidadMuestras: exitosas,
          estadoAnteriorId: estadoAnterior?.id,
          estadoNuevoId: estadoNuevo?.id
        });
      }
      
      // NO limpiar selecciones - mantener las muestras seleccionadas
      // setMuestrasSeleccionadasMasivas([]);

      // Mostrar resultado solo si hubo errores (sin mensaje de éxito)
      if (fallidas > 0) {
        Swal.fire({
          title: 'Acción completada con errores',
          html: `
            <strong>Exitosas:</strong> ${exitosas}<br>
            <strong>Fallidas:</strong> ${fallidas}<br><br>
            <strong>Errores:</strong><br>
            ${errores.join('<br>')}
          `,
          icon: 'warning'
        });
      }
      // Si todo fue exitoso, no mostrar mensaje para evitar retrasos en tablet

    } catch (error) {
      console.error('Error en acción masiva:', error);
      Swal.fire('Error', 'Error al procesar la acción masiva', 'error');
    } finally {
      setProcesandoAccionMasiva(false);
    }
  }; */
//Funcion nueva Optimizada--

// Función sin mensaje de confirmación para evitar retrasos en tablet (OPTIMIZADA)
const procesarAccionMasivaSinConfirmacion = async (
  nuevoEstadoId,
  estadoNuevo,
  estadoAnterior,
  muestrasAfectadas,
  muestrasIndices
) => {
  setProcesandoAccionMasiva(true);

  // Ajusta este número si querés más/menos paralelismo (4–8 suele ir bien)
  const CONCURRENCY_LIMIT = 6;

  // Helper: ejecutar tareas async con concurrencia limitada
  const runWithConcurrency = async (tasks, limit) => {
    const results = new Array(tasks.length);
    let nextIndex = 0;

    const workers = new Array(Math.min(limit, tasks.length)).fill(null).map(async () => {
      while (nextIndex < tasks.length) {
        const current = nextIndex++;
        try {
          results[current] = await tasks[current]();
        } catch (err) {
          results[current] = { ok: false, error: err };
        }
      }
    });

    await Promise.all(workers);
    return results;
  };

  try {
    let exitosas = 0;
    let fallidas = 0;
    const errores = [];

    // Precalcular valores comunes (menos trabajo por iteración)
    const idOrden = ordenSeleccionada?.idOrdenMedica;
    const usuarioFormateado = formatUsuario(user?.codigoUsuario || "ADMIN");
    const idSucursalFinal = sucursalSeleccionada?.idSucursal || null;
    const estadoAnteriorId = estadoAnterior?.id;
    const cubiculoFinal = cubiculoSeleccionado || null;

    const url = `${server}/mg_muestras_x_ordens/cambiarestado`;
    const headers = {
      "Content-Type": "application/json",
      "x-api-key": key,
    };

    // Si no hay orden o no hay muestras, salimos rápido
    if (!idOrden || !Array.isArray(muestrasAfectadas) || muestrasAfectadas.length === 0) {
      setProcesandoAccionMasiva(false);
      return;
    }

    // Vamos a crear tareas por EXAMEN (request por examen), pero contabilizar éxito por MUESTRA
    // Map para saber cuántos exámenes tiene cada muestra y cuántos fallaron
    const muestraStats = new Map(); // key: muestraKey => { desc, total, failed }

    const tasks = [];

    for (let i = 0; i < muestrasAfectadas.length; i++) {
      const muestra = muestrasAfectadas[i];
      const muestraDesc = muestra?.descripcionMuestra || `Muestra #${i + 1}`;

      // Normalizar examenes
      const examenes = Array.isArray(muestra?.examenes) ? muestra.examenes : [muestra];

      const muestraKey = i; // índice como key estable
      muestraStats.set(muestraKey, { desc: muestraDesc, total: examenes.length, failed: 0 });

      for (const examen of examenes) {
        tasks.push(async () => {
          const requestData = {
            IdOrden: idOrden,
            IdMuestraXAnalisis: examen?.idMuestraXAnalisis,
            EstadoAnterior: estadoAnteriorId,
            NuevoEstado: nuevoEstadoId,
            UsuarioId: usuarioFormateado,
            Comentario: "",
            IdSucursal: idSucursalFinal,
            Cubiculo: cubiculoFinal,
          };

          const response = await fetch(url, {
            method: "POST",
            headers,
            body: JSON.stringify(requestData),
          });

          if (!response.ok) {
            // leer texto SOLO si hay error (evita overhead innecesario en éxitos)
            const errorText = await response.text();
            const err = new Error(errorText || `HTTP ${response.status}`);
            err._muestraKey = muestraKey;
            err._muestraDesc = muestraDesc;
            throw err;
          }

          return { ok: true, muestraKey };
        });
      }
    }

    // Ejecutar en paralelo con límite
    const results = await runWithConcurrency(tasks, CONCURRENCY_LIMIT);

    // Procesar resultados y marcar fallos por muestra
    for (const r of results) {
      if (!r || r.ok === false) {
        const err = r?.error;
        const keyM = err?._muestraKey;
        const desc = err?._muestraDesc || "Muestra";

        if (muestraStats.has(keyM)) {
          muestraStats.get(keyM).failed += 1;
        }

        // Guardar mensaje de error (compacto)
        errores.push(`${desc}: ${err?.message || "Error desconocido"}`);
      }
    }

    // Contabilizar éxito/fallo por muestra (como tu lógica original)
    for (const [, st] of muestraStats) {
      if (st.failed === 0) exitosas += 1;
      else fallidas += 1;
    }

    // Recargar datos UNA sola vez al final
    await fetchMuestrasOrden(idOrden);

    // Trackear cambios (igual que tu flujo)
    if (exitosas > 0) {
      trackearAccion("muestra_modificada", { cantidad: exitosas });
      trackearAccion("cambio_estado", {
        descripcion: `Acción masiva: ${estadoAnterior?.estado || "Desconocido"} → ${estadoNuevo?.estado || "Desconocido"} (${exitosas} muestras)`,
        cantidadMuestras: exitosas,
        estadoAnteriorId: estadoAnterior?.id,
        estadoNuevoId: estadoNuevo?.id,
      });
    }

    // Mostrar solo si hubo errores (como querías)
    if (fallidas > 0) {
      Swal.fire({
        title: "Acción completada con errores",
        html: `
          <strong>Exitosas:</strong> ${exitosas}<br>
          <strong>Fallidas:</strong> ${fallidas}<br><br>
          <strong>Errores:</strong><br>
          ${errores.slice(0, 20).join("<br>")}
          ${errores.length > 20 ? `<br><br><em>...y ${errores.length - 20} más</em>` : ""}
        `,
        icon: "warning",
      });
    }
  } catch (error) {
    console.error("Error en acción masiva:", error);
    Swal.fire("Error", "Error al procesar la acción masiva", "error");
  } finally {
    setProcesandoAccionMasiva(false);
  }
};

  // Determinar si es modal o pantalla completa
  const isModal = open && onClose;
  
  // Estilos condicionales basados en si es modal o no
  const containerStyle = isModal 
    ? { margin: 0, padding: 0 }
    : {};
    
  const contentStyle = isModal 
    ? { margin: 0, padding: '5px' }
    : {};
    
  const titleStyle = isModal 
    ? { margin: '8px 0' }
    : {};
    
  const tableStyle = isModal 
    ? { marginTop: '10px' }
    : { marginTop: '20px' };
    
  const gridSpacing = isModal ? 1 : 2;
  const titleTag = isModal ? 'h3' : 'h2';

  return (
    <div className="right_col" role="main" style={containerStyle}>

      <div className="x_content" style={contentStyle}>

        {/* <div className="row"> */}
        {/* <div className="col-md-8 col-sm-7 offset-md-2"> */}
        {/* Header optimizado - Botón escanear en primera posición */}
        <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={2}>
            <Button 
              size="small" 
              variant="contained" 
              color="primary" 
              onClick={() => setScanning(!scanning)}
              fullWidth
              sx={{ 
                minHeight: '36px',
                fontSize: '0.8rem',
                fontWeight: 'bold'
              }}
            >
              {scanning ? 'Detener Escaneo' : 'Escanear Código'}
            </Button>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField 
              size="small" 
              label="Número de Orden" 
              value={ordenMedica} 
              fullWidth 
              onChange={(e) => setOrdenMedica(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  height: '36px'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button 
              size="small" 
              variant="contained" 
              color="primary" 
              onClick={handleSearch}
              fullWidth
              sx={{ 
                minHeight: '36px',
                fontSize: '0.8rem'
              }}
            >
              Buscar
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button 
              size="small" 
              variant="outlined" 
              color="secondary" 
              onClick={limpiarFormulario}
              fullWidth
              sx={{ 
                minHeight: '36px',
                fontSize: '0.8rem'
              }}
            >
              Limpiar
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button 
              size="small" 
              variant="contained" 
              color={cubiculoSeleccionado ? "success" : "warning"}
              onClick={handleAbrirGestionCubiculo}
              fullWidth
              sx={{ 
                minHeight: '36px',
                fontSize: '0.8rem',
                fontWeight: 'bold'
              }}
              startIcon={<Store />}
            >
              {cubiculoSeleccionado ? cubiculoSeleccionado : 'Gestión Cubículo'}
            </Button>
          </Grid>
        </Grid>

        {/* Componente de escaneo de código de barras */}
        {scanning && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => setFacingMode(prev => prev === 'environment' ? 'user' : 'environment')}
                  startIcon={<CameraAltIcon />}
                >
                  {facingMode === 'environment' ? 'Cámara Frontal' : 'Cámara Trasera'}
                </Button>
              </Box>
              <BarcodeScannerComponent
                width="100%"
                height="100%"
                facingMode={facingMode}
                onUpdate={(err, result) => {
                  if (result) {
                    handleCodigoBarraChange(result.text);
                    setScanning(false);
                  }
                }}
              />
            </Grid>

          </Grid>
        )}
        <Grid container spacing={gridSpacing}>
          <Grid item xs={12}>
            {React.createElement(titleTag, { style: titleStyle }, 'Datos de la Orden')}
          </Grid>
          {/* Primera fila - Información del cliente */}
          <Grid item xs={12} md={5}>
            <TextField 
              size="small" 
              label="Cliente" 
              value={ordenSeleccionada.cliente || ''} 
              fullWidth 
              inputProps={{ readOnly: true }}
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f9f9f9' } }}
            />
          </Grid>
          <Grid item xs={12} md={2.5}>
            <TextField 
              size="small" 
              label="Identidad" 
              value={ordenSeleccionada.identidad || ''} 
              fullWidth 
              inputProps={{ readOnly: true }}
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f9f9f9' } }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField 
              size="small" 
              label="Edad" 
              value={ordenSeleccionada.edad || ''} 
              fullWidth 
              inputProps={{ readOnly: true }}
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f9f9f9' } }}
            />
          </Grid>
          <Grid item xs={6} md={2.5}>
            <TextField 
              size="small" 
              label="Sexo" 
              value={ordenSeleccionada.sexo || ''} 
              fullWidth 
              inputProps={{ readOnly: true }}
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f9f9f9' } }}
            />
          </Grid>
          
          {/* Segunda fila - Dirección y contacto */}
          <Grid item xs={12} md={6}>
            <TextField 
              size="small" 
              label="Dirección" 
              value={ordenSeleccionada.direccion || ''} 
              fullWidth 
              inputProps={{ readOnly: true }}
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f9f9f9' } }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField 
              size="small" 
              label="Teléfono" 
              value={ordenSeleccionada.telefono || ''} 
              fullWidth 
              inputProps={{ readOnly: true }}
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f9f9f9' } }}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField 
              size="small" 
              label="Correo Electrónico" 
              value={ordenSeleccionada.correoElectronico || ''} 
              fullWidth 
              inputProps={{ readOnly: true }}
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f9f9f9' } }}
            />
          </Grid>

          {/* Tercera fila - Información de fechas y sucursal */}
          <Grid item xs={12} md={3}>
            <TextField 
              size="small" 
              label="Sucursal Entrega" 
              value={ordenSeleccionada.sucursalEntrega || ''} 
              fullWidth 
              inputProps={{ readOnly: true }}
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f9f9f9' } }}
            />
          </Grid>
          <Grid item xs={12} md={4.5}>
            <TextField 
              size="small" 
              label="Fecha Hora Recepción" 
              value={ordenSeleccionada.fechaHoraRecepcion || ''} 
              fullWidth 
              inputProps={{ readOnly: true }}
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f9f9f9' } }}
            />
          </Grid>
          <Grid item xs={12} md={4.5}>
            <TextField 
              size="small" 
              label="Fecha Hora Entrega" 
              value={ordenSeleccionada.fechaHoraEntrega || ''} 
              fullWidth 
              inputProps={{ readOnly: true }}
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f9f9f9' } }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={gridSpacing}>

          <Grid item xs={12}>
            {React.createElement(titleTag, { style: titleStyle }, 'Muestras')}
            
            {/* Sección de Acciones Masivas */}
            {muestras.length > 0 && (
              <Box sx={{ 
                mb: 2, 
                p: 2, 
                border: '1px solid #ddd', 
                borderRadius: 1, 
                backgroundColor: '#f9f9f9' 
              }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Acciones Masivas
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Checkbox para seleccionar todas */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={muestrasSeleccionadasMasivas.length === muestras.length && muestras.length > 0}
                        indeterminate={muestrasSeleccionadasMasivas.length > 0 && muestrasSeleccionadasMasivas.length < muestras.length}
                        onChange={(e) => handleSeleccionarTodasMasiva(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={`Seleccionar todas (${muestrasSeleccionadasMasivas.length}/${muestras.length})`}
                  />

                  {/* Información de selección */}
                  {muestrasSeleccionadasMasivas.length > 0 && (
                    <>
                      <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                        {muestrasSeleccionadasMasivas.length} muestra(s) seleccionada(s)
                      </Typography>

                      {/* Botón de acción masiva según el estado común */}
                      {(() => {
                        const infoEstado = obtenerEstadoSiguienteMasivo();
                        
                        if (!infoEstado) {
                          return (
                            <Typography variant="body2" color="error">
                              Las muestras seleccionadas deben tener el mismo estado
                            </Typography>
                          );
                        }

                        const estadoActualObj = estadosDisponibles.find(e => e.id === infoEstado.estadoActual);
                        const siguienteEstado = infoEstado.estadoSiguiente;

                        return (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                              Estado actual: <strong>{estadoActualObj?.estado || 'Pendiente'}</strong>
                            </Typography>
                            
                            {/* Si hay múltiples opciones disponibles (estado Pendiente) */}
                            {infoEstado.opcionesDisponibles && (
                              /*Aqui esta el ciclo para recorrer los opciones de estado*/
                              <>
                            
                                {infoEstado.opcionesDisponibles.map((opcion) => (
                                  <Button
                                    key={opcion.id}
                                    variant={opcion.isPrimary ? "contained" : "outlined"}
                                    color={getEstadoButtonColor(opcion.id)}
                                    startIcon={getEstadoIcon(opcion.id)}
                                    onClick={() => handleAccionMasiva(opcion.id)}
                                    disabled={procesandoAccionMasiva}
                                    sx={{
                                      fontWeight: 'bold',
                                      fontSize: '0.9rem',
                                      minWidth: '180px'
                                    }}
                                  >
                                    {procesandoAccionMasiva ? 'Procesando...' : `${opcion.estado} (${infoEstado.cantidadMuestras})`}
                                  </Button>
                                ))}
                              </>
                            )}
                            
                            {/* Si solo hay un siguiente estado (otros estados) */}
                            {infoEstado.estadoSiguiente && !infoEstado.opcionesDisponibles && (
                              <Button
                                variant="contained"
                                color={getEstadoButtonColor(infoEstado.estadoSiguiente.id)}
                                startIcon={getEstadoIcon(infoEstado.estadoSiguiente.id)}
                                onClick={() => handleAccionMasiva(infoEstado.estadoSiguiente.id)}
                                disabled={procesandoAccionMasiva}
                                sx={{
                                  fontWeight: 'bold',
                                  fontSize: '0.9rem',
                                  minWidth: '180px'
                                }}
                              >
                                {procesandoAccionMasiva ? 'Procesando...' : `${infoEstado.estadoSiguiente.estado} (${infoEstado.cantidadMuestras})`}
                              </Button>
                            )}

                            {/* Botón para anular masivamente si no están anuladas */}
                            {infoEstado.estadoActual !== 6 && (
                              <Button
                                variant="outlined"
                                color="error"
                                startIcon={<CancelIcon />}
                                onClick={() => handleAccionMasiva(6)}
                                disabled={procesandoAccionMasiva}
                                sx={{
                                  fontWeight: 'bold',
                                  fontSize: '0.8rem'
                                }}
                              >
                                Anular Todas
                              </Button>
                            )}

                            {/* Botón para marcar como "Muestra No Conforme" masivamente */}
                            {infoEstado.estadoActual !== 9 && estadosDisponibles.find(e => e.id === 9) && (
                              <Button
                                variant="outlined"
                                color="warning"
                                startIcon={<MuestraNoConformeIcon />}
                                onClick={() => handleAccionMasiva(9)}
                                disabled={procesandoAccionMasiva}
                                sx={{
                                  fontWeight: 'bold',
                                  fontSize: '0.8rem',
                                  color: '#d32f2f',
                                  borderColor: '#d32f2f',
                                  '&:hover': {
                                    backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                    borderColor: '#b71c1c'
                                  }
                                }}
                              >
                                Muestra No Conforme
                              </Button>
                            )}

                            {/* Botón para limpiar selección */}
                            <Button
                              variant="text"
                              color="secondary"
                              onClick={() => setMuestrasSeleccionadasMasivas([])}
                              sx={{ fontSize: '0.8rem' }}
                            >
                              Limpiar Selección
                            </Button>
                          </Box>
                        );
                      })()}
                    </>
                  )}
                </Box>
              </Box>
            )}

            <TableContainer component={Paper} style={tableStyle} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '40px', backgroundColor: '#f5f5f5' }}>
                      <Checkbox
                        checked={muestrasSeleccionadasMasivas.length === muestras.length && muestras.length > 0}
                        indeterminate={muestrasSeleccionadasMasivas.length > 0 && muestrasSeleccionadasMasivas.length < muestras.length}
                        onChange={(e) => handleSeleccionarTodasMasiva(e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: '80px', backgroundColor: '#f5f5f5' }}>Módulo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: '120px', backgroundColor: '#f5f5f5' }}>Tipo de Muestra</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '60px', backgroundColor: '#f5f5f5' }}>Cant.</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: '150px', backgroundColor: '#f5f5f5' }}>Exámenes</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '120px', backgroundColor: '#f5f5f5' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '80px', backgroundColor: '#f5f5f5' }}>Detalle</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 'bold', 
                      width: '60px', 
                      backgroundColor: '#f5f5f5', 
                      textAlign: 'center'
                    }}>
                      Obs.
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '100px', backgroundColor: '#f5f5f5' }}>Usuario</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '140px', backgroundColor: '#f5f5f5' }}>Fecha/Hora</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {muestras.map((muestra, index) => {
                    const estadoActual = muestra.idEstado || 1;
                    const estadoActualObj = estadosDisponibles.find(e => e.id === estadoActual);
                    
                    return (
                      <TableRow 
                        key={index}
                        sx={{
                          '&:nth-of-type(odd)': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          },
                        }}
                      >
                        <TableCell>
                          <Checkbox
                            checked={muestrasSeleccionadasMasivas.includes(index)}
                            onChange={(e) => handleSeleccionMasivaChange(index, e.target.checked)}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{muestra.modulo}</TableCell>
                        <TableCell>{muestra.descripcionMuestra}</TableCell>
                        <TableCell>{muestra.descripcionCantidad}</TableCell>
                        <TableCell>{muestra.ComparteCon}</TableCell>
                        <TableCell>
                          <Box 
                            sx={{
                              padding: '6px 12px',
                              borderRadius: '8px',
                              backgroundColor: getEstadoColor(estadoActual),
                              color: 'white',
                              fontWeight: 'bold',
                              display: 'inline-block',
                              minWidth: '100px',
                              textAlign: 'center',
                              fontSize: '0.75rem',
                              boxShadow: 1,
                              border: '1px solid rgba(255,255,255,0.3)'
                            }}
                          >
                            {estadoActualObj?.estado || 'Pendiente'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={`Ver detalle de ${muestra.cantidadMuestras} muestra(s) individual(es)`} arrow>
                            <Button 
                              size="small" 
                              variant="outlined" 
                              color="info"
                              onClick={() => handleAbrirDetalleModal(muestra)}
                              startIcon={<ViewIcon sx={{ fontSize: '0.8rem' }} />}
                              sx={{ fontSize: '0.7rem', minWidth: '80px' }}
                            >
                              Ver ({muestra.cantidadMuestras})
                            </Button>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ 
                          width: '60px',
                          padding: '4px',
                          textAlign: 'center',
                          verticalAlign: 'top'
                        }}>
                          <ObservacionesCompactas
                            index={index}
                            observacion={getObservacionMuestra(index)}
                            placeholder={getObservacionMuestra(index) ? "Editando comentario del histórico..." : "Observaciones..."}
                          />
                        </TableCell>
                        <TableCell>
                          {muestra.usuarioToma || '-'}
                        </TableCell>
                        <TableCell>
                          {getFechaUltimoComentario(index) || muestra.fechaHoraToma || '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>


          </Grid>
          <Grid item xs={12}>
            {React.createElement(titleTag, { style: titleStyle }, 'Condiciones')}
            
            {/* Sección de Acciones Masivas para Condiciones */}
            {condiciones.length > 0 && (
              <Box sx={{ 
                mb: 2, 
                p: 2, 
                border: '1px solid #ddd', 
                borderRadius: 1, 
                backgroundColor: '#f9f9f9' 
              }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Acciones Masivas - Condiciones
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Checkbox para seleccionar todas las condiciones */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={condicionesSeleccionadasMasivas.length === condiciones.length && condiciones.length > 0}
                        indeterminate={condicionesSeleccionadasMasivas.length > 0 && condicionesSeleccionadasMasivas.length < condiciones.length}
                        onChange={(e) => handleSeleccionarTodasCondiciones(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={`Seleccionar todas (${condicionesSeleccionadasMasivas.length}/${condiciones.length})`}
                  />

                  {/* Información de selección */}
                  {condicionesSeleccionadasMasivas.length > 0 && (
                    <>
                      <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                        {condicionesSeleccionadasMasivas.length} condición(es) seleccionada(s)
                      </Typography>

                      <Button
                        variant="contained"
                        color="success"
                        onClick={handleGuardarCondicionesMasivo}
                        disabled={procesandoGuardadoCondiciones}
                        sx={{
                          fontWeight: 'bold',
                          fontSize: '0.9rem',
                          minWidth: '180px'
                        }}
                      >
                        {procesandoGuardadoCondiciones ? 'Guardando...' : `Guardar Condiciones (${condicionesSeleccionadasMasivas.length})`}
                      </Button>

                      {/* Botón para limpiar selección */}
                      <Button
                        variant="text"
                        color="secondary"
                        onClick={() => setCondicionesSeleccionadasMasivas([])}
                        sx={{ fontSize: '0.8rem' }}
                      >
                        Limpiar Selección
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            )}

            <TableContainer component={Paper} style={tableStyle} sx={{ maxHeight: '50vh', overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '50px', backgroundColor: '#f5f5f5' }}>Sel.</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Condición</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: '200px', backgroundColor: '#f5f5f5' }}>Examenes</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '100px', backgroundColor: '#f5f5f5' }}>Cumple</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: '250px', backgroundColor: '#f5f5f5' }}>Observaciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {condiciones.map((condicion, index) => (
                    <TableRow 
                      key={condicion.keyUnico || `condicion-${condicion.idCondicion}-${index}`}
                      sx={{ 
                        backgroundColor: condicionesSeleccionadasMasivas.includes(condicion.idCondicion) ? '#e3f2fd' : 'inherit',
                        '&:hover': { backgroundColor: '#f5f5f5' }
                      }}
                    >
                      <TableCell>
                        <Checkbox
                          checked={condicionesSeleccionadasMasivas.includes(condicion.idCondicion)}
                          onChange={(e) => handleCondicionSeleccionChange(condicion.idCondicion, e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {condicion.descripcionCondicion}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {condicion.ComparteCon}
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={condicion.cumple || false}
                          onChange={(e) => handleCondicionCumpleChange(condicion.idCondicion, e.target.checked)}
                          color={condicion.cumple ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {/* Solo mostrar campo de observaciones si la condición NO cumple */}
                        {!condicion.cumple ? (
                          <TextField
                            size="small"
                            multiline
                            maxRows={2}
                            placeholder="Observación para condición no cumplida..."
                            value={getObservacionCondicion(condicion.idCondicion)}
                            onChange={(e) => handleObservacionCondicionChange(condicion.idCondicion, e.target.value)}
                            variant="outlined"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                fontSize: '0.75rem',
                                padding: '4px',
                                backgroundColor: '#fff3cd'
                              },
                              '& .MuiOutlinedInput-input': {
                                padding: '6px 8px'
                              }
                            }}
                            helperText="Requerido para condiciones no cumplidas"
                          />
                        ) : (
                          <Typography variant="body2" color="success.main" sx={{ fontStyle: 'italic' }}>
                            Condición cumplida ✓
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button 
              size="small" 
              variant="contained" 
              onClick={handleAbrirImprimirEtiquetas} 
              style={{ backgroundColor: '#3f51b5', color: 'white' }}
              disabled={muestras.length === 0}
            >
              Imprimir Etiquetas
            </Button>
            
            <Button 
              size="small" 
              variant="contained" 
              onClick={handleGuardarFormulario}
              disabled={guardandoFormulario}
              style={{ 
                backgroundColor: guardandoFormulario ? '#94a3b8' : '#28a745', 
                color: 'white',
                position: 'relative'
              }}
            >
              {guardandoFormulario ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: '8px', width: '16px', height: '16px' }}></span>
                  Guardando...
                </>
              ) : (
                <>
                  💾 Guardar Toma de Muestra
                </>
              )}
            </Button>
          </Grid>
        </Grid>
        {/* Dialog para seleccionar muestras e imprimir etiquetas */}
        <Dialog 
          open={imprimirEtiquetasOpen} 
          onClose={handleCerrarImprimirEtiquetas}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Seleccionar Muestras para Etiquetas
          </DialogTitle>
          <DialogContent>
            <FormControlLabel
              control={
                <Checkbox
                  checked={muestrasSeleccionadas.length === muestras.length && muestras.length > 0}
                  onChange={(e) => handleSeleccionarTodasMuestras(e.target.checked)}
                  color="primary"
                />
              }
              label="Seleccionar todas"
            />
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Seleccionar</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Módulo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Cantidad</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Examenes</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Cobas 303</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {muestras.map((muestra, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Checkbox
                          checked={muestrasSeleccionadas.includes(muestra.idMuestraXAnalisis)}
                          onChange={(e) => handleMuestraSeleccionChange(muestra.idMuestraXAnalisis, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>{muestra.modulo}</TableCell>
                      <TableCell>{muestra.descripcionMuestra}</TableCell>
                      <TableCell>{muestra.descripcionCantidad}</TableCell>
                      <TableCell>{muestra.ComparteCon}</TableCell>
                      <TableCell>
                        <Checkbox
                          checked={muestra.esCobas303 || false}
                          onChange={(e) => {
                            // Update the sample's Cobas303 status
                            const updatedSamples = [...muestras];
                            updatedSamples[index] = {
                              ...muestra,
                              esCobas303: e.target.checked
                            };
                            setMuestras(updatedSamples);
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCerrarImprimirEtiquetas} color="secondary" variant="outlined">
              Cancelar
            </Button>
            <Button 
              onClick={handleImprimirEtiquetas} 
              color="primary" 
              variant="contained"
              disabled={muestrasSeleccionadas.length === 0}
            >
              Imprimir Etiquetas
            </Button>
          </DialogActions>
        </Dialog>
        {open && onClose && (
          <Box display="flex" justifyContent="flex-end" p={isModal ? 0.5 : 1} mt={isModal ? 1 : 2}>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => {
                console.log('🔒 Validación cierre:', {
                  hayCondiciones: condiciones.length > 0,
                  totalCondiciones: condiciones.length,
                  condicionesGuardadas: condicionesGuardadas
                });
                
                if (condiciones.length > 0 && !condicionesGuardadas) {
                  Swal.fire({
                    title: 'Condiciones sin guardar',
                    html: 'Debe guardar las condiciones antes de cerrar.<br><br><small>Presione <kbd>ESC</kbd> para salir sin guardar</small>',
                    icon: 'warning',
                    confirmButtonText: 'Entendido'
                  });
                  return;
                }
                onClose();
              }}
            >
              Cerrar
            </Button>
          </Box>
        )}
      </div>

      {/* Modal de Detalle Individual */}
      <Dialog
        open={modalDetalleOpen}
        onClose={handleCerrarDetalleModal}
        // maxWidth="lg"
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          {grupoSeleccionado && (
            <>
              Detalle Individual: {grupoSeleccionado.descripcionMuestra}
              <Typography variant="subtitle2" color="textSecondary">
                Módulo: {grupoSeleccionado.modulo} | Cantidad: {grupoSeleccionado.cantidad}
              </Typography>
              
              {/* Barra de acciones masivas en modal de detalle */}
              {muestrasDetalleSeleccionadas.length > 0 && (() => {
                // Validar que todas las muestras seleccionadas tienen el mismo estado
                const estadosSeleccionados = muestrasDetalleSeleccionadas.map(index => muestrasDetalle[index]?.idEstado || 1);
                const estadoComun = estadosSeleccionados[0];
                const todosMismoEstado = estadosSeleccionados.every(estado => estado === estadoComun);
                
                if (!todosMismoEstado) {
                  return (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Selecciona muestras con el mismo estado para acciones masivas
                    </Alert>
                  );
                }
                
                // Obtener opciones disponibles
                const estadoActualObj = estadosDisponibles.find(e => e.id === estadoComun);
                const resultadoEstado = obtenerEstadoSiguienteMasivo();
                
                if (!resultadoEstado || !resultadoEstado.opcionesDisponibles) {
                  return null;
                }
                
                const opcionPrimaria = resultadoEstado.opcionesDisponibles.find(op => op.isPrimary);
                const opcionesSecundarias = resultadoEstado.opcionesDisponibles.filter(op => !op.isPrimary);
                
                return (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      {muestrasDetalleSeleccionadas.length} muestra(s) seleccionada(s) - Estado actual: {estadoActualObj?.estado}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {opcionPrimaria && (
                        <Button
                          size="small"
                          variant="contained"
                          color={getEstadoButtonColor(opcionPrimaria.id)}
                          onClick={() => handleAccionMasiva(opcionPrimaria.id)}
                          startIcon={getEstadoIcon(opcionPrimaria.id)}
                          disabled={procesandoAccionMasiva}
                        >
                          {opcionPrimaria.estado} ({muestrasDetalleSeleccionadas.length})
                        </Button>
                      )}
                      {opcionesSecundarias.map((opcion) => (
                        <Button
                          key={opcion.id}
                          size="small"
                          variant="outlined"
                          color={getEstadoButtonColor(opcion.id)}
                          onClick={() => handleAccionMasiva(opcion.id)}
                          startIcon={getEstadoIcon(opcion.id)}
                          disabled={procesandoAccionMasiva}
                        >
                          {opcion.estado} ({muestrasDetalleSeleccionadas.length})
                        </Button>
                      ))}
                      <Button
                        size="small"
                        variant="text"
                        color="secondary"
                        onClick={() => setMuestrasDetalleSeleccionadas([])}
                      >
                        Limpiar selección
                      </Button>
                    </Box>
                  </Box>
                );
              })()}
            </>
          )}
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={muestrasDetalleSeleccionadas.length === muestrasDetalle.length && muestrasDetalle.length > 0}
                      indeterminate={muestrasDetalleSeleccionadas.length > 0 && muestrasDetalleSeleccionadas.length < muestrasDetalle.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setMuestrasDetalleSeleccionadas(muestrasDetalle.map((_, idx) => idx));
                        } else {
                          setMuestrasDetalleSeleccionadas([]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Análisis</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Estado Actual</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Cambiar Estado</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Histórico</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Usuario</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {muestrasDetalle.map((examen, index) => {
                  const estadoActual = examen.idEstado || 1;
                  const estadoActualObj = estadosDisponibles.find(e => e.id === estadoActual);
                  
                  return (
                    <TableRow key={index}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={muestrasDetalleSeleccionadas.includes(index)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setMuestrasDetalleSeleccionadas(prev => [...prev, index]);
                            } else {
                              setMuestrasDetalleSeleccionadas(prev => prev.filter(i => i !== index));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>{examen.idMuestraXAnalisis}</TableCell>
                      <TableCell>{examen.analisis}</TableCell>
                      <TableCell>
                        <Box 
                          component="span" 
                          sx={{
                            backgroundColor: getEstadoColor(estadoActual),
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '0.75rem'
                          }}
                        >
                          {estadoActualObj?.estado || 'Pendiente'}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <AccionesDropdown
                          estadoActual={estadoActual}
                          estadosDisponibles={estadosDisponibles}
                          onCambiarEstado={(nuevoEstadoId) => handleCambiarEstadoIndividual(examen, nuevoEstadoId)}
                          getSiguienteEstado={getSiguienteEstado}
                          getEstadoIcon={getEstadoIcon}
                          getEstadoButtonColor={getEstadoButtonColor}
                          isLoading={isMuestraIndividualCargando(examen)}
                          enModalDetalle={true}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Ver histórico de cambios de estado" arrow>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => handleAbrirHistorico(examen)}
                            startIcon={<HistoryIcon sx={{ fontSize: '0.8rem' }} />}
                            sx={{ 
                              fontSize: '0.7rem',
                              minWidth: '60px'
                            }}
                          >
                            Ver
                          </Button>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{examen.ingresadoPor?.trim() || '-'}</TableCell>
                      <TableCell>
                        {examen.inicioTomaMuestra ? formatFecha(examen.inicioTomaMuestra) : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCerrarDetalleModal}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Histórico - MEJORADO */}
      <Dialog
        open={modalHistoricoOpen}
        onClose={() => setModalHistoricoOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '70vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          Histórico de Estados - {grupoSeleccionado?.ComparteCon || grupoSeleccionado?.analisis}
          <Typography variant="subtitle2" color="textSecondary">
            Muestra: {grupoSeleccionado?.descripcionMuestra} | Módulo: {grupoSeleccionado?.modulo}
          </Typography>
          <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
            Los comentarios automáticos del sistema se ocultan, solo se muestran comentarios del operador
          </Typography>
        </DialogTitle>
        <DialogContent>
          {grupoSeleccionado?.historico && grupoSeleccionado.historico.length > 0 ? (
            <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 140, backgroundColor: '#f5f5f5' }}>Fecha Cambio</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 130, backgroundColor: '#f5f5f5' }}>Estado Anterior</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 130, backgroundColor: '#f5f5f5' }}>Estado Nuevo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120, backgroundColor: '#f5f5f5' }}>Usuario</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 400, backgroundColor: '#f5f5f5' }}>Comentario del Operador</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grupoSeleccionado.historico.length > 0 ? grupoSeleccionado.historico.map((item, index) => (
                    <TableRow 
                      key={index}
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          {item.fecha_cambio_estado ? formatFecha(item.fecha_cambio_estado) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={item.estadoAnterior || 'Inicial'} 
                          size="small"
                          color="default"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={item.estadoNuevo || 'N/A'} 
                          size="small"
                          color={
                            item.id_estado_nuevo === 1 ? 'default' :
                            item.id_estado_nuevo === 2 ? 'success' :
                            item.id_estado_nuevo === 3 ? 'primary' :
                            item.id_estado_nuevo === 4 ? 'info' :
                            item.id_estado_nuevo === 5 ? 'warning' :
                            item.id_estado_nuevo === 9 ? 'error' : 'default'
                          }
                          sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                          {item.ingresadoPor ? item.ingresadoPor.trim() : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                            lineHeight: 1.6,
                            fontSize: '0.9rem',
                            padding: '8px 0',
                            fontStyle: filtrarComentarioAutomatico(item.comentario) ? 'italic' : 'normal',
                            color: filtrarComentarioAutomatico(item.comentario) ? '#333' : '#999'
                          }}
                        >
                          {filtrarComentarioAutomatico(item.comentario) || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                          No hay historial de cambios de estado para esta muestra.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                No hay histórico disponible para esta muestra.
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                El histórico se generará cuando se realicen cambios de estado.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalHistoricoOpen(false)} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Comentarios para Cambio de Estado */}
      <Dialog
        open={openComentarioModal}
        onClose={handleCloseComentarioModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {pendingCambioEstado && (
            <>
              {pendingCambioEstado.esMasiva ? 'Comentario para Acción Masiva' : 'Comentario para Cambio de Estado'}
              <Typography variant="subtitle2" color="textSecondary">
                {pendingCambioEstado.esMasiva ? 
                  `${pendingCambioEstado.muestrasAfectadas?.length || 0} muestras seleccionadas` :
                  pendingCambioEstado.isIndividual ? 
                    `Análisis: ${muestraParaComentario?.analisis}` :
                    `Muestra: ${muestraParaComentario?.descripcionMuestra}`
                }
              </Typography>
              <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
                {pendingCambioEstado.estadoAnterior?.estado} → {pendingCambioEstado.estadoNuevo?.estado}
                {pendingCambioEstado.esMasiva && ` (${pendingCambioEstado.muestrasAfectadas?.length || 0} muestras)`}
              </Typography>
            </>
          )}
        </DialogTitle>
        <DialogContent>
          {/* Campos para identificar quién traslada y quién verifica las muestras */}
          {pendingCambioEstado?.esCambioDoble && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Complete la información del traslado y verificación de muestras:
              </Typography>
              
              <Autocomplete
                fullWidth
                options={usuariosDisponibles}
                getOptionLabel={(option) => `${option.nombreCompleto || ''} (${option.codigoUsuario || ''})`}
                value={usuariosDisponibles.find(u => u.codigoUsuario === usuarioTraslado) || null}
                onChange={(event, newValue) => {
                  setUsuarioTraslado(newValue?.codigoUsuario || '');
                }}
                loading={cargandoUsuarios}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Usuario que traslada (requerido)"
                    placeholder="Seleccione quien trasladó la muestra"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {cargandoUsuarios ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                type="datetime-local"
                label="Fecha y hora del traslado (requerido)"
                value={fechaTraslado}
                onChange={(e) => setFechaTraslado(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                helperText="Registre cuándo se trasladó la muestra"
                sx={{ mb: 2 }}
              />
              
              <Autocomplete
                fullWidth
                options={usuariosDisponibles}
                getOptionLabel={(option) => `${option.nombreCompleto || ''} (${option.codigoUsuario || ''})`}
                value={usuariosDisponibles.find(u => u.codigoUsuario === usuarioVerifica) || null}
                onChange={(event, newValue) => {
                  setUsuarioVerifica(newValue?.codigoUsuario || '');
                }}
                loading={cargandoUsuarios}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Usuario que verifica (requerido)"
                    placeholder="Seleccione quien verifica la muestra"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {cargandoUsuarios ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          )}

          {/* Combo box de condiciones de muestra para cambio a "Muestra No Conforme" */}
          {pendingCambioEstado?.nuevoEstadoId === 9 && (pendingCambioEstado?.esMasiva || pendingCambioEstado?.isIndividual) && (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Condición de Muestra (requerido)</InputLabel>
                <Select
                  value={condicionMuestraSeleccionada}
                  onChange={(e) => setCondicionMuestraSeleccionada(e.target.value)}
                  label="Condición de Muestra (requerido)"
                  displayEmpty
                  disabled={cargandoCondicionesMuestra}
                >
                  {Array.isArray(condicionesMuestra) && condicionesMuestra.length > 0 ? (
                    condicionesMuestra.map((condicion) => (
                      <MenuItem key={condicion.id} value={condicion.id}>
                        {condicion.descripcion || `Condición ID: ${condicion.id}`}
                      </MenuItem>
                    ))
                  ) : (
                    !cargandoCondicionesMuestra && (
                      <MenuItem disabled>
                        <em>No hay condiciones disponibles</em>
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Campos para Muestra Repetida: motivo + responsable */}
          {pendingCambioEstado?.esMuestraRepetida && (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                <InputLabel>Motivo de Muestra Repetida (requerido)</InputLabel>
                <Select
                  value={motivoRepetidaSeleccionado}
                  onChange={(e) => setMotivoRepetidaSeleccionado(e.target.value)}
                  label="Motivo de Muestra Repetida (requerido)"
                  disabled={cargandoMotivosRepetida}
                >
                  {cargandoMotivosRepetida ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Cargando motivos...
                    </MenuItem>
                  ) : (
                    motivosRepetida.map((motivo) => (
                      <MenuItem key={motivo.id} value={motivo.id}>
                        {motivo.descripcion}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              
              <Autocomplete
                fullWidth
                options={usuariosDisponibles}
                getOptionLabel={(option) => `${option.nombreCompleto || ''} (${option.codigoUsuario || ''})`}
                value={usuariosDisponibles.find(u => u.codigoUsuario === responsableRepetida) || null}
                onChange={(event, newValue) => {
                  setResponsableRepetida(newValue?.codigoUsuario || '');
                }}
                loading={cargandoUsuarios}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Responsable (requerido)"
                    placeholder="Seleccione el responsable"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {cargandoUsuarios ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          )}

          {/* Solo mostrar campo de comentario si NO es cambio doble */}
          {!pendingCambioEstado?.esCambioDoble && (
            <>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder={
                  pendingCambioEstado?.esMasiva && pendingCambioEstado?.nuevoEstadoId === 9 ?
                    "Acciones correctivas para resolver no conformidad" :
                    "Escriba su comentario aquí... (mínimo 5 caracteres)"
                }
                value={comentarioText}
                onChange={(e) => setComentarioText(e.target.value)}
                variant="outlined"
                autoFocus
                sx={{ mb: 2 }}
                inputProps={{
                  style: { resize: 'vertical' }
                }}
              />
              
              <Typography variant="caption" color="textSecondary">
                Caracteres: {comentarioText.length} (mínimo 5 requeridos)
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseComentarioModal}
            color="secondary"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmComentario}
            color="primary"
            variant="contained"
            disabled={
              // Si es cambio doble, validar ambos usuarios y fecha de traslado
              pendingCambioEstado?.esCambioDoble ? 
                (!usuarioTraslado.trim() || !usuarioVerifica.trim() || !fechaTraslado.trim()) :
                // Si es Muestra Repetida, validar motivo + responsable + comentario
                pendingCambioEstado?.esMuestraRepetida ?
                  (!motivoRepetidaSeleccionado || !responsableRepetida.trim() || !comentarioText.trim() || comentarioText.trim().length < 5) :
                // Si no es cambio doble, validar comentario
                (!comentarioText.trim() || 
                 comentarioText.trim().length < 5 ||
                 (pendingCambioEstado?.nuevoEstadoId === 9 && 
                  (pendingCambioEstado?.esMasiva || pendingCambioEstado?.isIndividual) && 
                  !condicionMuestraSeleccionada))
            }
            sx={{ 
              backgroundColor: pendingCambioEstado?.esCambioDoble ? '#2196f3' :
                               pendingCambioEstado?.nuevoEstadoId === 3 ? '#1976d2' : 
                               pendingCambioEstado?.nuevoEstadoId === 9 ? '#d32f2f' : '#1976d2',
              '&:hover': { 
                backgroundColor: pendingCambioEstado?.esCambioDoble ? '#1976d2' :
                                 pendingCambioEstado?.nuevoEstadoId === 3 ? '#115293' : 
                                 pendingCambioEstado?.nuevoEstadoId === 9 ? '#b71c1c' : '#115293'
              }
            }}
          >
            {pendingCambioEstado?.esCambioDoble ? 'Confirmar Verificación' : 'Confirmar Cambio'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para editar condición de muestra (Muestra No Conforme) */}
      <Dialog
        open={modalCondicionMuestraOpen}
        onClose={cerrarModalCondicionMuestra}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
          Condición de Muestra No Conforme
          <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
            {condicionMuestraEditando !== null && muestras[condicionMuestraEditando] ? 
              `Muestra: ${muestras[condicionMuestraEditando].descripcionMuestra}` : 
              'Muestra seleccionada'
            }
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Seleccione la condición específica que no se cumple en esta muestra:
          </Typography>
          
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel>Condición de Muestra *</InputLabel>
            <Select
              value={condicionMuestraSeleccionada}
              onChange={(e) => setCondicionMuestraSeleccionada(e.target.value)}
              label="Condición de Muestra *"
              displayEmpty
              disabled={cargandoCondicionesMuestra}
            >
              {/* <MenuItem value="">
                <em>{cargandoCondicionesMuestra ? 'Cargando...' : 'Seleccione una condición'}</em>
              </MenuItem> */}
              {Array.isArray(condicionesMuestra) && condicionesMuestra.map((condicion) => (
                <MenuItem key={condicion.IdCondicion || condicion.id} value={condicion.IdCondicion || condicion.id}>
                  {condicion.DescripcionCondicion || condicion.descripcion}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Acciones correctivas para resolver no conformidad"
            value={comentarioCondicionMuestra}
            onChange={(e) => setComentarioCondicionMuestra(e.target.value)}
            variant="outlined"
            label="Acciones correctivas para resolver no conformidad"
            sx={{ mb: 2 }}
            inputProps={{
              style: { resize: 'vertical' }
            }}
          />
          
          <Typography variant="caption" color="textSecondary">
            Caracteres: {comentarioCondicionMuestra.length} (mínimo 10 requeridos)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={cerrarModalCondicionMuestra}
            color="secondary"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={async () => {
              if (!condicionMuestraSeleccionada) {
                Swal.fire('Error', 'Debe seleccionar una condición', 'error');
                return;
              }
              if (!comentarioCondicionMuestra.trim() || comentarioCondicionMuestra.trim().length < 10) {
                Swal.fire('Error', 'Las acciones correctivas deben tener al menos 10 caracteres', 'error');
                return;
              }
              
              try {
                // Actualizar comentario en el histórico de estados de muestra
                await actualizarComentarioHistorico(comentarioCondicionMuestra.trim());
                Swal.fire('Éxito', 'Acciones correctivas actualizadas correctamente', 'success');
                cerrarModalCondicionMuestra();
                
                // Recargar las muestras para reflejar los cambios
                await fetchMuestrasOrden(ordenSeleccionada.idOrdenMedica);
              } catch (error) {
                Swal.fire('Error', 'No se pudieron actualizar las acciones correctivas', 'error');
              }
            }}
            color="primary"
            variant="contained"
            disabled={!condicionMuestraSeleccionada || !comentarioCondicionMuestra.trim() || comentarioCondicionMuestra.trim().length < 10}
            sx={{ 
              backgroundColor: '#d32f2f',
              '&:hover': { backgroundColor: '#b71c1c' }
            }}
          >
            Guardar Condición
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Preview de Etiquetas */}
      <Dialog
        open={openEtiquetasPreview}
        onClose={() => {
          setOpenEtiquetasPreview(false);
          if (etiquetasPdfUrl) {
            window.URL.revokeObjectURL(etiquetasPdfUrl);
            setEtiquetasPdfUrl(null);
          }
          setMuestrasSeleccionadas([]);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="h6" component="div">
            Etiquetas de Muestras - Orden #{ordenSeleccionada.idOrdenMedicaMes || ordenSeleccionada.idOrdenMedica}
          </Typography>
          <Box>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                if (etiquetasPdfUrl) {
                  const a = document.createElement('a');
                  a.href = etiquetasPdfUrl;
                  a.download = `Etiquetas_Orden_${ordenSeleccionada.idOrdenMedica}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  
                  Swal.fire({
                    title: 'Descargado',
                    text: 'El PDF se ha descargado correctamente',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                  });
                }
              }}
              sx={{ mr: 1 }}
            >
              Descargar PDF
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {etiquetasPdfUrl && (
            <iframe
              src={etiquetasPdfUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                flexGrow: 1
              }}
              title="Preview Etiquetas"
            />
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
          <Button
            onClick={() => {
              setOpenEtiquetasPreview(false);
              if (etiquetasPdfUrl) {
                window.URL.revokeObjectURL(etiquetasPdfUrl);
                setEtiquetasPdfUrl(null);
              }
              setMuestrasSeleccionadas([]);
            }}
            variant="outlined"
            color="secondary"
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Gestión de Cubículo */}
      <Dialog 
        open={modalCubiculoOpen} 
        onClose={() => {
          if (!mostrarAlertaCubiculo) {
            setModalCubiculoOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          backgroundColor: cubiculoSeleccionado ? '#4caf50' : '#ff9800',
          color: 'white'
        }}>
          <CubiculoIcon />
          {cubiculoSeleccionado ? 'Cambiar Cubículo de Trabajo' : 'Seleccionar Cubículo de Trabajo'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {mostrarAlertaCubiculo && (
            <Box sx={{ 
              mb: 2, 
              p: 2, 
              backgroundColor: '#fff3cd', 
              borderRadius: 1,
              border: '1px solid #ffc107'
            }}>
              <Typography variant="body2" sx={{ color: '#856404', fontWeight: 'bold' }}>
                ⚠️ Debe seleccionar un cubículo antes de operar
              </Typography>
            </Box>
          )}
          
          {cubiculoSeleccionado && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#e8f5e9', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                <strong>Cubículo actual:</strong> {cubiculoSeleccionado}
              </Typography>
            </Box>
          )}
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            Seleccione el cubículo donde está trabajando:
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant={cubiculoSeleccionado === 'TOMA 1' ? 'contained' : 'outlined'}
                color="primary"
                size="large"
                onClick={() => handleGuardarCubiculo('TOMA 1')}
                startIcon={<CubiculoIcon />}
                sx={{ 
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold'
                }}
              >
                TOMA 1
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant={cubiculoSeleccionado === 'TOMA 2' ? 'contained' : 'outlined'}
                color="primary"
                size="large"
                onClick={() => handleGuardarCubiculo('TOMA 2')}
                startIcon={<CubiculoIcon />}
                sx={{ 
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold'
                }}
              >
                TOMA 2
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant={cubiculoSeleccionado === 'TOMA 3' ? 'contained' : 'outlined'}
                color="primary"
                size="large"
                onClick={() => handleGuardarCubiculo('TOMA 3')}
                startIcon={<CubiculoIcon />}
                sx={{ 
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold'
                }}
              >
                TOMA 3
              </Button>
            </Grid>
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <Grid item xs={12} key={`ventanilla-${num}`}>
                <Button
                  fullWidth
                  variant={cubiculoSeleccionado === `VENTANILLA ${num}` ? 'contained' : 'outlined'}
                  color="success"
                  size="large"
                  onClick={() => handleGuardarCubiculo(`VENTANILLA ${num}`)}
                  startIcon={<CubiculoIcon />}
                  sx={{ 
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }}
                >
                  VENTANILLA {num}
                </Button>
              </Grid>
            ))}
            {cubiculoSeleccionado && (
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  size="large"
                  onClick={() => handleGuardarCubiculo('')}
                  sx={{ 
                    py: 2,
                    fontSize: '1rem'
                  }}
                >
                  No seleccionar ninguno
                </Button>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        {!mostrarAlertaCubiculo && (
          <DialogActions>
            <Button 
              onClick={() => setModalCubiculoOpen(false)} 
              color="secondary"
            >
              Cancelar
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </div>
  );
};

export default TomaMuestra;
